/**
 * Centralised API client + auth-token store.
 *
 * Production-readiness (2026-05-08). Wraps fetch with:
 *   - bearer-token injection from localStorage
 *   - 401 → redirect-to-login behaviour
 *   - JSON request/response framing
 *   - typed error surface
 *
 * The token is set by the login page (calling POST /v1/auth/login -
 * Phase 4 customer-login; /v1/auth/demo-login for the demo personas) and
 * picked up by every subsequent API call via this client. The useSseStream
 * hook also reads it via getAuthToken() so SSE connections carry the
 * same credential as REST calls.
 *
 * In production this is replaced by SSO/SCIM cookie-based session; the
 * client interface stays identical.
 */

const STORAGE_TOKEN_KEY = 'foctta.auth.token';
const STORAGE_USER_KEY = 'foctta.auth.user';
const TOKEN_EXPIRY_KEY = 'foctta.auth.expiresAt';

// Exported so SSE hooks + direct-download helpers across the app route
// through the SAME base. Defaults to `/api`; the Pattern-A build sets
// VITE_API_BASE=/api/discovery so bundled-tenant calls reach Discovery's
// controller (Core rewrites /api/discovery/* → /api/*) instead of colliding
// with Core on the bare /api/v1/* prefix. Legacy standalone leaves it `/api`.
//
// Injected via Vite `define` (vite.config.ts → __API_BASE__) because this
// Vite setup does NOT expose process.env VITE_* to import.meta.env. The
// `typeof` guard keeps vitest (no define) safe - it falls back to '/api'.
declare const __API_BASE__: string;
export const API_BASE = ((typeof __API_BASE__ !== 'undefined' ? __API_BASE__ : '/api') || '/api').replace(
  /\/$/,
  '',
);

export interface AuthenticatedUser {
  email: string;
  displayName: string;
  role: string;
  tenantId: string;
  /** Set when the user logged in as a custom_role. */
  customRoleId?: string;
  /** Human-readable label for the custom role (e.g. "SOC Tier-2 Triage"). */
  customRoleName?: string;
}

export function getAuthToken(): string | null {
  const token = localStorage.getItem(STORAGE_TOKEN_KEY);
  if (!token) return null;
  const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (expiresAt && new Date(expiresAt).getTime() < Date.now()) {
    clearAuth();
    return null;
  }
  return token;
}

export function getAuthUser(): AuthenticatedUser | null {
  const raw = localStorage.getItem(STORAGE_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthenticatedUser;
  } catch {
    return null;
  }
}

export function setAuth(token: string, user: AuthenticatedUser, expiresAt: string): void {
  localStorage.setItem(STORAGE_TOKEN_KEY, token);
  localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(user));
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt);
  // Notify same-tab listeners (Zustand stores etc.).
  window.dispatchEvent(new Event('foctta:auth-changed'));
}

export function clearAuth(): void {
  localStorage.removeItem(STORAGE_TOKEN_KEY);
  localStorage.removeItem(STORAGE_USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  window.dispatchEvent(new Event('foctta:auth-changed'));
}

/**
 * SSO bridge exchange (Phase A - doc 21 §4.2).
 *
 * Called by the /auth/bridge SPA route when the user is redirected from
 * Core's tenant SPA carrying a bridge token in the URL fragment
 * (`#t=<jwt>`). Posts the token to /v1/auth/exchange; on success
 * persists the returned Discovery session via setAuth().
 *
 * The fragment is read client-side and never sent to the server in a
 * Referer header - keeps the bridge token off any HTTP intermediary's
 * access log.
 */
export interface BridgeExchangeResponse {
  token: string;
  expiresAt: string;
  user: {
    email: string;
    displayName: string;
    role: string;
    tenantId: string;
  };
}

// Public-signup helpers were removed 2026-05-22. Core (app.foctta.com)
// is the canonical signup. Customers arrive via /auth/bridge.

// ── Phase 4 (2026-05-22) - customer auth helpers ────────────────────

export interface MagicLinkVerification {
  ok: true;
  purpose: 'first_login' | 'forgot_password' | 'invitation_resend';
  tenant: { id: string; slug: string | null };
  user: {
    id: string;
    email: string;
    fullName: string;
    role: string;
    isFirstLogin: boolean;
  };
}

export async function verifyMagicLinkApi(token: string): Promise<MagicLinkVerification> {
  const res = await fetch(
    `${API_BASE}/v1/auth/verify-magic-link?t=${encodeURIComponent(token)}`,
  );
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return JSON.parse(text) as MagicLinkVerification;
}

export interface SetPasswordResponse {
  ok: true;
  /** Discriminator for the CustomerLoginResponse union. */
  mfaChallengeRequired?: false;
  session: { token: string; expiresAt: string };
  user: AuthenticatedUser;
}

export async function setPasswordApi(args: {
  magicLinkToken: string;
  password: string;
}): Promise<SetPasswordResponse> {
  const res = await fetch(`${API_BASE}/v1/auth/set-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return JSON.parse(text) as SetPasswordResponse;
}

/**
 * Wave 6 (2026-05-27) - login response now discriminates between a
 * fully-authenticated session and a partial-auth response requiring
 * MFA enrolment or challenge.
 */
export interface CustomerLoginMfaResponse {
  ok?: false;
  partialAuth: { token: string; expiresAt: string };
  mfaChallengeRequired: true;
  mfaReason: 'mfa_required' | 'mfa_enrolment_due';
  user: AuthenticatedUser;
}
export type CustomerLoginResponse = SetPasswordResponse | CustomerLoginMfaResponse;

export async function customerLoginApi(args: {
  tenantSlug: string;
  email: string;
  password: string;
}): Promise<CustomerLoginResponse> {
  const res = await fetch(`${API_BASE}/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return JSON.parse(text) as CustomerLoginResponse;
}

// ============================================================
// Wave 6 (2026-05-27) - MFA enrol + verify + challenge wrappers.
// Backend already complete at /v1/auth/mfa/* (Phase 4). All three
// endpoints take a partial-auth JWT in the Authorization header.
// ============================================================

export interface MfaEnrolResult {
  ok: true;
  secretBase32: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
  backupCodes: string[];
}
export interface MfaVerifyResult {
  ok: true;
  session: { token: string; expiresAt: string };
  user: AuthenticatedUser;
}
export interface MfaChallengeResult {
  ok: true;
  session: { token: string; expiresAt: string };
  user: AuthenticatedUser;
}

async function mfaFetch<T>(path: string, partialAuthToken: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${partialAuthToken}`,
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return JSON.parse(text) as T;
}

export async function mfaEnrol(partialAuthToken: string): Promise<MfaEnrolResult> {
  return mfaFetch<MfaEnrolResult>('/v1/auth/mfa/enroll', partialAuthToken, {});
}
export async function mfaVerifyEnrol(
  partialAuthToken: string,
  totpCode: string,
): Promise<MfaVerifyResult> {
  return mfaFetch<MfaVerifyResult>('/v1/auth/mfa/verify', partialAuthToken, { totpCode });
}
export async function mfaChallenge(
  partialAuthToken: string,
  args: { totpCode?: string; backupCode?: string },
): Promise<MfaChallengeResult> {
  return mfaFetch<MfaChallengeResult>('/v1/auth/mfa/challenge', partialAuthToken, args);
}

// ── Workstream B (2026-07-12) - login-page SSO discovery ────────────
//
// Tells the login page whether a tenant has an enabled IdP so it can show
// "Sign in with SSO". Pre-auth (no bearer). The endpoint returns the SAME
// shape for an unknown workspace and a workspace-without-SSO, so this never
// leaks which slugs are real tenants. Any failure degrades to ssoEnabled:false
// - SSO discovery must never block password login.

export interface SsoDiscovery {
  ssoEnabled: boolean;
  kind: 'saml' | 'oidc' | null;
}

export async function getSsoDiscovery(tenantSlug: string): Promise<SsoDiscovery> {
  try {
    const res = await fetch(
      `${API_BASE}/v1/auth/sso/discover?slug=${encodeURIComponent(tenantSlug)}`,
      { headers: { accept: 'application/json' } },
    );
    if (!res.ok) return { ssoEnabled: false, kind: null };
    const body = (await res.json()) as Partial<SsoDiscovery>;
    return {
      ssoEnabled: body.ssoEnabled === true,
      kind: body.kind === 'saml' || body.kind === 'oidc' ? body.kind : null,
    };
  } catch {
    return { ssoEnabled: false, kind: null };
  }
}

/**
 * Build the URL the "Sign in with SSO" button sends the browser to. The
 * server (GET /v1/auth/sso/login/:slug) resolves the slug, auto-picks
 * SAML vs OIDC, and 302-redirects to the IdP. Relative to API_BASE so it
 * stays same-origin (the IdP posts back to the same host's /sso/landing).
 */
export function ssoLoginUrl(tenantSlug: string, returnUrl = '/'): string {
  return `${API_BASE}/v1/auth/sso/login/${encodeURIComponent(tenantSlug)}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

export async function forgotPasswordApi(args: {
  tenantSlug: string;
  email: string;
}): Promise<{ ok: true; emailSent: boolean }> {
  const res = await fetch(`${API_BASE}/v1/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return res.json() as Promise<{ ok: true; emailSent: boolean }>;
}

/**
 * Resolve the apex domain from the current hostname. Returns the canonical
 * apex for which URL pattern the user is on so subsequent redirects /
 * displays stay on the same pattern during the migration window.
 *
 * Returns:
 *   - 'foctta.com'             (Pattern A canonical, per docs/architecture/24)
 *   - 'discovery.foctta.com'   (legacy, kept during 30-day migration window)
 *   - 'foctta.com'             (fallback for localhost / unknown - Pattern A canonical)
 */
export function tenantApexFromHost(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  // Legacy pattern wins if both end-with checks match (".discovery.foctta.com"
  // also ends-with ".foctta.com", so longest-first detection matters).
  if (host === 'discovery.foctta.com' || host.endsWith('.discovery.foctta.com')) {
    return 'discovery.foctta.com';
  }
  // Pattern A canonical default - covers <slug>.foctta.com, app.foctta.com,
  // foctta.com, and any future host under the apex.
  return 'foctta.com';
}

/** Resolve the tenantSlug from the current hostname. Returns null on apex / dev. */
export function tenantSlugFromHost(): string | null {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  // Reserved system hostnames - never treated as tenant slugs.
  const RESERVED = new Set([
    'foctta.com',
    'www.foctta.com',
    'app.foctta.com',
    'activate.foctta.com',
    'discovery.foctta.com',
    'www.discovery.foctta.com',
  ]);
  if (RESERVED.has(host)) return null;
  // <slug>.discovery.foctta.com   → "<slug>" (Pattern A migration: legacy)
  // <slug>.foctta.com             → "<slug>" (Pattern A canonical, per docs/architecture/24)
  // localhost / IP                → null
  const match = host.match(/^([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)\.(discovery\.foctta\.com|foctta\.com)$/);
  return match ? match[1]! : null;
}

// ── Phase 5 (2026-05-22) - per-tenant DAM retention policy ─────────

export interface RetentionPolicy {
  tenantId: string;
  tenantSlug: string;
  retentionDays: number;
  recommendedDays: number | null;
  minDays: number;
  maxDays: number;
}

export async function getRetentionPolicy(): Promise<RetentionPolicy> {
  return apiFetch<RetentionPolicy>('/v1/activity/retention');
}

export async function setRetentionPolicy(args: {
  retentionDays: number;
  reason: string;
}): Promise<RetentionPolicy> {
  return apiFetch<RetentionPolicy>('/v1/activity/retention', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export interface RetentionCleanupResult {
  tenantId: string;
  retentionDays: number;
  rowsMarkedForDelete: number | null;
  mutationsSubmitted: number;
  errors: string[];
  durationMs: number;
}

export async function triggerRetentionCleanup(): Promise<RetentionCleanupResult> {
  return apiFetch<RetentionCleanupResult>('/v1/activity/retention/cleanup', {
    method: 'POST',
  });
}

export async function exchangeBridgeToken(bridgeToken: string): Promise<BridgeExchangeResponse> {
  // Absolute URL surfaced in diagnostics so a "Failed to fetch" (network /
  // CORS / blocked) error shows exactly which endpoint the browser tried.
  const exchangeUrl = new URL(`${API_BASE}/v1/auth/exchange`, window.location.origin).href;
  let res: Response;
  try {
    res = await fetch(exchangeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bridgeToken }),
    });
  } catch (netErr) {
    // fetch() only rejects on a network-level failure (DNS, refused, CORS
    // preflight blocked, offline, blocked by an extension/proxy). Surface the
    // URL + underlying reason so the failure is diagnosable from the screen.
    const reason = netErr instanceof Error ? `${netErr.name}: ${netErr.message}` : String(netErr);
    throw new Error(`${reason} — could not reach ${exchangeUrl}`);
  }
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* leave parsed as null; ApiError carries the raw text */
  }
  if (!res.ok) {
    const detail =
      (parsed as { detail?: string } | null)?.detail ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, detail, parsed);
  }
  return parsed as BridgeExchangeResponse;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  /** When true, do NOT attach Authorization. Used by /v1/auth/login. */
  skipAuth?: boolean;
  /**
   * Optional Idempotency-Key. When provided, sent as the `Idempotency-Key`
   * header. Backend caches the response for 24h keyed on this value, so
   * retries with the same key return the same outcome - required for safe
   * POSTs that the user might double-click.
   */
  idempotencyKey?: string;
}

/**
 * Generate a fresh idempotency key. Useful when the caller doesn't have a
 * stable id of its own; bind once at form-mount time and re-use across
 * retries of the same submit. UUID v4 from the browser's WebCrypto.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for very old browsers: hex-shaped, NOT a real UUID but unique
  // enough for idempotency purposes (the backend treats it as opaque).
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...(opts.headers as Record<string, string> | undefined),
  };
  if (opts.body && !headers['content-type']) {
    headers['content-type'] = 'application/json';
  }
  if (opts.idempotencyKey) {
    headers['idempotency-key'] = opts.idempotencyKey;
  }
  if (!opts.skipAuth) {
    const token = getAuthToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, { ...opts, headers });
  if (res.status === 401 && !opts.skipAuth) {
    clearAuth();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.assign('/login');
    }
  }
  const contentType = res.headers.get('content-type') ?? '';
  let body: unknown = null;
  if (contentType.includes('application/json')) {
    body = await res.json().catch(() => null);
  } else {
    body = await res.text().catch(() => null);
  }
  if (!res.ok) {
    // Prefer the richest detail available. RFC 7807 problem-details
    // (`title` + `detail` + `code`) is the canonical shape across the
    // platform; fall back to legacy `error` field; finally fall back
    // to status code + reason phrase. The full body is preserved on
    // ApiError.body so callers can render structured detail.
    const b = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    const title = typeof b?.title === 'string' ? b.title : null;
    const detail = typeof b?.detail === 'string' ? b.detail : null;
    const code = typeof b?.code === 'string' ? (b.code as string) : null;
    const legacyError = typeof b?.error === 'string' ? b.error : null;
    const message =
      // "Title - detail (code: xxx)" when we have rich problem-details
      (title && detail ? `${title} - ${detail}${code ? ` (code: ${code})` : ''}` : null) ??
      title ??
      detail ??
      legacyError ??
      `${res.status} ${res.statusText}`;
    if (res.status === 403) {
      // 403 problem-details carry raw RBAM permission strings (e.g.
      // "role 'dpo' lacks permission 'foundational:integration:read'") that are
      // noise - and slightly alarming - to end users. Surface a clean,
      // role-aware message; the technical detail stays on ApiError.body
      // (+ console) for operators/debugging. Caught in a live UI audit
      // 2026-05-31 (DPO seeing raw Forbidden text on IT-Admin-only pages).
      if (typeof console !== 'undefined') console.warn('[api] 403', path, message);
      throw new ApiError(
        403,
        'You don’t have permission to view this section. It requires a role with elevated access (typically IT Admin) - ask your administrator if you need it.',
        body,
      );
    }
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

// ── High-level domain calls ─────────────────────────────────────────

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: AuthenticatedUser;
}

/**
 * Public auth policy probe - tells the LoginPage whether to render a
 * password field. Three modes:
 *   - 'disabled': demo login is turned off; the page should direct the
 *                  user to the SSO sign-in flow.
 *   - 'password': caller must send a shared demo password. The page
 *                  renders a password input.
 *   - 'open':     dev mode - email-only.
 */
export interface DemoLoginPolicy {
  mode: 'disabled' | 'password' | 'open';
}
export async function getDemoLoginPolicy(): Promise<DemoLoginPolicy> {
  return apiFetch<DemoLoginPolicy>('/v1/auth/policy', { skipAuth: true });
}

export async function loginRequest(
  email: string,
  options: { password?: string; customRoleId?: string } = {},
): Promise<LoginResponse> {
  const body: { email: string; password?: string; customRoleId?: string } = { email };
  if (options.password && options.password.length > 0) body.password = options.password;
  if (options.customRoleId && options.customRoleId.length > 0) body.customRoleId = options.customRoleId;
  // Demo-login moved /v1/auth/login → /v1/auth/demo-login on 2026-05-22.
  // The canonical /v1/auth/login is now Phase 4 customer-login.
  return apiFetch<LoginResponse>('/v1/auth/demo-login', {
    method: 'POST',
    skipAuth: true,
    body: JSON.stringify(body),
  });
}

export interface DevUserPreset {
  actorId: string;
  email: string;
  displayName: string;
  role: string;
}

export async function listDevUsers(): Promise<DevUserPreset[]> {
  const r = await apiFetch<{ users: DevUserPreset[] }>('/v1/auth/users', {
    skipAuth: true,
  });
  return r.users;
}

export interface ApprovedCustomRolePreset {
  id: string;
  name: string;
  description: string | null;
  permissionsCount: number;
}

/**
 * Fetch approved custom_roles for the demo tenant. Unauthenticated, dev
 * only - powers the LoginPage "Log in as Custom Role" picker. Production
 * SSO/SCIM via @foctta/auth replaces this entirely.
 */
export async function listApprovedCustomRoles(): Promise<ApprovedCustomRolePreset[]> {
  const r = await apiFetch<{ roles: ApprovedCustomRolePreset[] }>(
    '/v1/auth/approved-custom-roles',
    { skipAuth: true },
  );
  return r.roles;
}

// ── Discovery ──

export interface DiscoveryFinding {
  id: string;
  findingType: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string | null;
  recommendation: string | null;
  confidence: number;
  status: string;
  classifications: Array<{ label: string; confidence: number }>;
  relatedRegulations: string[];
  createdAt: string;
}

export async function listDiscoveryFindings(params: {
  severity?: string;
  status?: string;
  limit?: number;
  /**
   * P1 OBS-2.alt (2026-05-26) - cursor-based pagination. Pass the
   * `nextCursor` from the previous page's response to get the next
   * page. Backend filters on `createdAt < cursor` (descending).
   */
  cursor?: string;
} = {}): Promise<{ findings: DiscoveryFinding[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (params.severity) q.set('severity', params.severity);
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.cursor) q.set('cursor', params.cursor);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/discovery/findings${suffix}`);
}

/**
 * Discovery asset. Wire shape matches the server's `AssetSchema` at
 * apps/api-controller/src/modules/discovery/schemas.ts:166 - the
 * earlier minimal client interface (5 fields) silently dropped most of
 * the response; same drift class as the Section9 + triggerScan bugs
 * fixed in the Phase-2 hardening pass.
 */
export interface DiscoveryAsset {
  id: string;
  tenantId: string;
  businessUnitId: string | null;
  sourceType: string;
  externalRefs: Record<string, string | null>;
  displayName: string;
  networkZone: string | null;
  countryCode: string | null;
  discoveredVia: string[];
  confidenceScore: number;
  classifications: Array<{ label: string; confidence?: number }>;
  sensitivity: 'critical' | 'high' | 'medium' | 'low' | 'unknown';
  ownerEmail: string | null;
  rowCount: number | null;
  sizeBytes: number | null;
  lastScannedAt: string | null;
  lastAccessedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export async function listDiscoveryAssets(): Promise<{ assets: DiscoveryAsset[] }> {
  return apiFetch('/v1/discovery/assets');
}

/**
 * Get a single discovery asset. Server response is wrapped as
 * `{ asset: ... }`, so we unwrap here to keep the call-site simple
 * (the alternative - every consumer doing `(await getDiscoveryAsset(id)).asset` -
 * is noise that doesn't justify itself).
 */
export async function getDiscoveryAsset(id: string): Promise<DiscoveryAsset> {
  const wrapped = await apiFetch<{ asset: DiscoveryAsset }>(
    `/v1/discovery/assets/${encodeURIComponent(id)}`,
  );
  return wrapped.asset;
}

/** Item-1: assign a data custodian (owner) to an asset. Returns the updated asset. */
export async function assignAssetOwner(id: string, ownerEmail: string): Promise<DiscoveryAsset> {
  const wrapped = await apiFetch<{ asset: DiscoveryAsset }>(
    `/v1/discovery/assets/${encodeURIComponent(id)}/owner`,
    { method: 'PATCH', body: JSON.stringify({ ownerEmail }) },
  );
  return wrapped.asset;
}

export interface RemediationResult {
  action: string;
  executed: boolean;
  /** True when the finding no longer reproduces (re-checked by its own detector). */
  verified: boolean;
  /** Finding status after the loop ('resolved' when verified). */
  status: string;
  detail: string;
}

/**
 * Remediation loop: run a named action on a finding; the server executes the fix,
 * verifies, and auto-closes the finding on success. Phase 1: assign_owner.
 */
export async function remediateFinding(
  id: string,
  action: string,
  input: { ownerEmail?: string } = {},
): Promise<RemediationResult> {
  return apiFetch<RemediationResult>(
    `/v1/discovery/findings/${encodeURIComponent(id)}/remediate`,
    { method: 'POST', body: JSON.stringify({ action, ...input }) },
  );
}

export interface ReverseDsarResponse {
  principalIdentifierHash: string;
  assets: Array<{
    assetId: string;
    sourceType: string;
    displayName: string;
    classifications: string[];
  }>;
  cachedAt: string | null;
  ttlExpiresAt: string | null;
  /**
   * How this answer was produced (FCT-P0-007). `value_matched` means the
   * principal's identifier value was actually observed in these assets;
   * `label_scope` means these are assets that COULD hold an identifier of this
   * kind and would be the same list for any principal of that kind;
   * `no_assets_scanned` means nothing has been scanned, so an empty list is not
   * evidence of absence. The UI must not present the latter two as the first.
   */
  resolution: {
    mode: 'value_matched' | 'label_scope' | 'no_assets_scanned';
    valueIndexPopulated: boolean;
    assetsScanned: number;
    assetsTotal: number;
    detail: string;
  };
  labelLandscape: string[];
  toxicCombinations: Array<{ assetId: string; combination: string[] }>;
  /**
   * Present when FOCTTA core is connected - adds consent receipts +
   * datamap cross-references for the same principal so the operator sees
   * Discovery's view and FOCTTA core's view side-by-side.
   */
  focttaCoreEnrichment?: {
    coreIntegrated: true;
    /** Count of Core's authoritative DSPM-walk assets for the same principal. */
    coreAssetCount: number;
    /** Processing activities Core's RoPA links to this principal. */
    relatedActivities: Array<{
      processingActivityId: string;
      name: string;
      purpose: string;
    }>;
    /** Distinct source systems Core sees for the principal. */
    crossRefSystems: string[];
    /** Count of active consents (status === 'active'). */
    openConsents: number;
    /** Total count of consents (all statuses). */
    consentCount: number;
    /**
     * Full list of consents - active + expired + withdrawn. T1.5.A.2
     * extension lets the UI render a withdrawal-history timeline.
     */
    activeConsents: Array<{
      consentId: string;
      purpose: string;
      lawfulBasis: string;
      status: string;
      grantedAt: string;
      expiresAt?: string;
      withdrawnAt?: string;
      isMinor: boolean;
      receiptHash?: string;
    }>;
    integratedAt: string;
  };
}

export async function reverseDsar(
  principalIdentifier: string,
  identifierKind: 'email' | 'phone' | 'aadhaar' | 'pan' | 'customer_number' | 'custom',
): Promise<ReverseDsarResponse> {
  return apiFetch('/v1/discovery/reverse-dsar', {
    method: 'POST',
    body: JSON.stringify({ principalIdentifier, identifierKind }),
  });
}

// ── Threat signatures (malware YARA / hash catalogue) ──

/** Built-in catalogue entry - METADATA ONLY (rule bodies never leave the pod). */
export interface BuiltinThreatSignature {
  name: string;
  family: string;
  category: string;
  description: string;
}

/** A tenant-authored YARA rule or known-bad hash. */
export interface CustomThreatSignature {
  id: string;
  kind: 'yara' | 'hash';
  name: string;
  family: string | null;
  value: string;
  enabled: boolean;
  createdByUserId: string | null;
  createdAt: string | null;
}

export interface ThreatSignaturesResponse {
  builtin: BuiltinThreatSignature[];
  custom: CustomThreatSignature[];
}

export async function listThreatSignatures(): Promise<ThreatSignaturesResponse> {
  return apiFetch('/v1/discovery/threat-signatures');
}

export async function createThreatSignature(input: {
  kind: 'yara' | 'hash';
  name: string;
  family?: string;
  value: string;
  enabled?: boolean;
}): Promise<{ signature: CustomThreatSignature }> {
  return apiFetch('/v1/discovery/threat-signatures', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateThreatSignature(
  id: string,
  updates: { name?: string; family?: string | null; value?: string; enabled?: boolean },
): Promise<{ signature: CustomThreatSignature }> {
  return apiFetch(`/v1/discovery/threat-signatures/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteThreatSignature(
  id: string,
): Promise<{ signature: CustomThreatSignature }> {
  return apiFetch(`/v1/discovery/threat-signatures/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ── Exact Data Match (salted-hash EDM) ──
// The browser salts + SHA-256-hashes each value locally; only the salt + hex
// digests cross the wire. The server stores hashes and looks them up - it
// never sees plaintext.

export interface EdmDatasetSummary {
  id: string;
  name: string;
  fieldLabel: string;
  rowCount: number;
  createdAt: string;
}

/** Single-dataset shape - carries the salt so the browser can re-hash a test
 *  value before calling /match. */
export interface EdmDatasetDetail extends EdmDatasetSummary {
  salt: string;
}

export interface CreateEdmDatasetResult {
  datasetId: string;
  rowCount: number;
}

export interface EdmMatchResult {
  matched: boolean;
  datasetName: string;
}

export async function listEdmDatasets(): Promise<EdmDatasetSummary[]> {
  return apiFetch('/v1/discovery/edm/datasets');
}

export async function getEdmDataset(id: string): Promise<EdmDatasetDetail> {
  return apiFetch(`/v1/discovery/edm/datasets/${encodeURIComponent(id)}`);
}

export async function createEdmDataset(input: {
  name: string;
  fieldLabel: string;
  salt: string;
  hashes: string[];
}): Promise<CreateEdmDatasetResult> {
  return apiFetch('/v1/discovery/edm/datasets', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function deleteEdmDataset(id: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/v1/discovery/edm/datasets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function matchEdmValue(
  datasetId: string,
  valueHash: string,
): Promise<EdmMatchResult> {
  return apiFetch('/v1/discovery/edm/match', {
    method: 'POST',
    body: JSON.stringify({ datasetId, valueHash }),
  });
}

// ── DPDPA Section 9 - verifiable parental consent ──
// Discovery detects children's data (minor_dob classifier + section_9_minor_data
// findings); these calls RECORD + track the verifiable parental consent Section 9
// mandates before that data may be processed.

export type ParentalConsentVerificationMethod =
  | 'digilocker'
  | 'id_document'
  | 'existing_kyc'
  | 'manual';

export type ParentalConsentStatus = 'verified' | 'pending' | 'revoked';

export interface ParentalConsentRecord {
  id: string;
  tenantId: string;
  /** Opaque/hashed reference to the minor (e.g. a KYC id). Never plaintext PII. */
  minorRef: string;
  guardianName: string | null;
  verificationMethod: ParentalConsentVerificationMethod;
  status: ParentalConsentStatus;
  verifiedAt: string | null;
  verifiedByUserId: string | null;
  expiresAt: string | null;
  /** sha256 (hex) of the canonical consent fields - tamper-evident receipt. */
  receiptHash: string | null;
  notes: string | null;
  createdAt: string | null;
}

export interface ParentalConsentSummary {
  verified: number;
  pending: number;
  revoked: number;
}

export interface ListParentalConsentsResponse {
  records: ParentalConsentRecord[];
  summary: ParentalConsentSummary;
}

export interface RecordParentalConsentInput {
  minorRef: string;
  guardianName?: string;
  verificationMethod: ParentalConsentVerificationMethod;
  status?: ParentalConsentStatus;
  /** ISO-8601 datetime. */
  expiresAt?: string;
  notes?: string;
}

export async function listParentalConsents(): Promise<ListParentalConsentsResponse> {
  return apiFetch('/v1/discovery/section-9/consents');
}

export async function recordParentalConsent(
  input: RecordParentalConsentInput,
): Promise<ParentalConsentRecord> {
  const wrapped = await apiFetch<{ record: ParentalConsentRecord }>(
    '/v1/discovery/section-9/consents',
    { method: 'POST', body: JSON.stringify(input) },
  );
  return wrapped.record;
}

export async function updateParentalConsent(
  id: string,
  updates: {
    status?: ParentalConsentStatus;
    guardianName?: string | null;
    expiresAt?: string | null;
    notes?: string | null;
  },
): Promise<ParentalConsentRecord> {
  const wrapped = await apiFetch<{ record: ParentalConsentRecord }>(
    `/v1/discovery/section-9/consents/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(updates) },
  );
  return wrapped.record;
}

export async function deleteParentalConsent(id: string): Promise<ParentalConsentRecord> {
  const wrapped = await apiFetch<{ record: ParentalConsentRecord }>(
    `/v1/discovery/section-9/consents/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  return wrapped.record;
}

// ── Activity ──

export interface UebaFinding {
  id: string;
  actorUserId: string;
  metric: string;
  rule: string;
  observed: string;
  expectedMean: string;
  expectedStddev: string;
  zScore: string;
  baselineSampleSize: number;
  windowSince: string;
  windowUntil: string;
  detectedAt: string;
  status: string;
}

export async function listUebaFindings(): Promise<{ findings: UebaFinding[] }> {
  return apiFetch('/v1/activity/ueba/findings');
}

// (Duplicate SonarFinding + listSonarFindings declarations previously
// lived here. Canonical definitions now live in the Activity block
// further down, with the wire shape aligned to the server's response
// from listSonarFindings.)

// ── Detection ──

/**
 * DDR decision row. Wire shape matches Drizzle's `$inferSelect` on
 * `ddr_decisions` (apps/api-controller/src/shared/database/schema/
 * detection.ts:57) - server returns camelCase keys via Drizzle's
 * column-name-to-TS-key mapping. Previous client type used snake_case
 * which never matched the wire - caught during Phase 4 wiring.
 */
export interface Decision {
  id: string;
  tenantId: string;
  ruleId: string | null;
  triggerType: 'discovery_finding' | 'activity_event' | 'combined' | string;
  triggerId: string | null;
  decision: 'allow' | 'mask' | 'alert' | 'step_up' | 'break_glass' | 'quarantine' | 'block' | string;
  decisionExplanation: string | null;
  tripleRedundancyVotes: Array<{ engine: string; vote: string; confidence: number }>;
  playbookId: string | null;
  playbookStatus: string | null;
  relatedFindingId: string | null;
  relatedEventId: string | null;
  relatedBreachId: string | null;
  requestId: string | null;
  decidedAt: string;
}

export async function listDecisions(params: { decision?: string; limit?: number } = {}): Promise<{ decisions: Decision[] }> {
  const q = new URLSearchParams();
  if (params.decision) q.set('decision', params.decision);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/detection/decisions${suffix}`);
}

// ── Frontier (Phase 5) ──
//
// Sovereign-tier opt-in. Backed by /v1/frontier/* and gated by
// product='foctta_platform' + feature.frontier_tier=true.

export interface FrontierHealth {
  status: 'ok' | 'degraded';
  surfaces: Record<string, string>;
}

export async function getFrontierHealth(): Promise<FrontierHealth> {
  return apiFetch('/v1/frontier/health');
}

export interface SmpcBenchmark {
  key: string;
  description: string;
  aggregationFn: 'sum' | 'avg' | 'p50' | 'p95' | 'count';
  targetIndustry: string;
}

export async function listSmpcBenchmarks(): Promise<{ benchmarks: SmpcBenchmark[] }> {
  return apiFetch('/v1/frontier/smpc/benchmarks');
}

export interface SmpcRoundtripResult {
  tenantId: string;
  benchmarkKey: string;
  revealed: {
    aggregate: number;
    aggregationFn: string;
    participantCount: number;
  };
  payloadHash: string;
}

export async function runSmpcDemoRoundtrip(
  benchmarkKey: string,
  secrets: number[],
): Promise<SmpcRoundtripResult> {
  return apiFetch('/v1/frontier/smpc/demo-roundtrip', {
    method: 'POST',
    body: JSON.stringify({ benchmarkKey, secrets }),
  });
}

export interface ByoLlmProvider {
  id: string;
  displayName: string;
  authMethod: string;
  defaultBaseUrl?: string;
  maxContextWindowTokens: number;
  supportsPromptCaching: boolean;
  supportsToolUse: boolean;
  supportsStreaming: boolean;
}

export async function listByoLlmProviders(): Promise<{ providers: ByoLlmProvider[] }> {
  return apiFetch('/v1/frontier/byo-llm/providers');
}

export interface EbpfTracingPolicy {
  name: string;
  description: string;
  syscalls: string[];
  risk: 'low' | 'medium' | 'high' | 'critical';
}

export async function listEbpfTracingPolicies(): Promise<{ policies: EbpfTracingPolicy[] }> {
  return apiFetch('/v1/frontier/sensors/tracing-policies');
}

// P2 OBS-16 (2026-05-26) - tenant-scoped Frontier list endpoints.
// These return real rows from the per-tenant tables (federated_rounds,
// smpc_sessions, pq_key_pairs, ebpf_sensors, byo_llm_keys) so Frontier
// pages can render real demo data instead of hardcoded mocks.

export interface FederatedRoundRow {
  id: string;
  tenantId: string;
  modelKind: string;
  roundNumber: number;
  coordinatorOrigin: string;
  secureAggregationProtocol: string;
  differentialPrivacyEpsilon: string | null;
  differentialPrivacyDelta: string | null;
  targetParticipants: number;
  actualParticipants: number;
  minParticipantsForRelease: number;
  status: string;
  aggregatedUpdateRef: string | null;
  metadata: Record<string, unknown>;
  startedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface FederatedParticipantRow {
  id: string;
  tenantId: string;
  roundId: string;
  encryptedUpdateRef: string;
  sampleCount: number;
  noiseSeedHash: string | null;
  submittedAt: string;
  accepted: boolean;
  rejectionReason: string | null;
  createdAt: string;
}
export async function listFederatedRounds(
  limit = 20,
): Promise<{ rounds: FederatedRoundRow[]; participants: FederatedParticipantRow[] }> {
  return apiFetch(`/v1/frontier/federated/rounds?limit=${limit}`);
}

export interface SmpcSession {
  id: string;
  tenantId: string;
  benchmarkKey: string;
  description: string | null;
  targetIndustry: string | null;
  minParticipants: number;
  actualParticipants: number;
  aggregationFn: string;
  status: string;
  revealedResultJson: Record<string, unknown> | null;
  coordinatorOrigin: string;
  metadata: Record<string, unknown>;
  openedAt: string;
  closedAt: string | null;
}
export async function listSmpcSessions(
  limit = 20,
): Promise<{ sessions: SmpcSession[] }> {
  return apiFetch(`/v1/frontier/smpc/sessions?limit=${limit}`);
}

export interface PqKeyPairRow {
  id: string;
  tenantId: string;
  purpose: string;
  algorithm: string;
  publicKeyB64: string;
  privateKeyKmsRef: string;
  libraryProvider: string;
  rotatedFromKeyId: string | null;
  activeFrom: string;
  rotatedAt: string | null;
  revokedAt: string | null;
}
export async function listPqKeyPairs(
  limit = 20,
): Promise<{ keyPairs: PqKeyPairRow[] }> {
  return apiFetch(`/v1/frontier/pq/key-pairs?limit=${limit}`);
}

export interface EbpfSensorRow {
  id: string;
  tenantId: string;
  sensorName: string;
  nodeOrigin: string;
  kernelVersion: string;
  tetragonVersion: string;
  captureMode: string;
  tracingPolicy: string;
  spiffeId: string;
  status: string;
  eventsLast24h: number;
  metadata: Record<string, unknown>;
  registeredAt: string;
  lastSeenAt: string | null;
}
export async function listEbpfSensors(
  limit = 50,
): Promise<{ sensors: EbpfSensorRow[] }> {
  return apiFetch(`/v1/frontier/sensors/ebpf?limit=${limit}`);
}

export interface ByoLlmKey {
  id: string;
  tenantId: string;
  provider: string;
  modelId: string;
  endpointUrl: string | null;
  apiKeyKmsRef: string;
  contextWindowTokens: number;
  monthlyTokenBudget: number | null;
  purposeTag: string;
  status: string;
}
export async function listByoLlmKeys(
  limit = 20,
): Promise<{ keys: ByoLlmKey[] }> {
  return apiFetch(`/v1/frontier/byo-llm/keys?limit=${limit}`);
}

// P2 OBS-15 (2026-05-27) - tenant-data-grounded chat surface.
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}
export interface ChatResponse {
  content: string;
  liveAi: boolean;
  modelHint: string | null;
  groundedOn: string[];
}
export async function frontierChat(
  messages: ReadonlyArray<ChatMessage>,
): Promise<ChatResponse> {
  return apiFetch('/v1/frontier/chat', {
    method: 'POST',
    body: JSON.stringify({ messages }),
  });
}

// Federated learning
export interface RecruitRoundInput {
  modelKind: 'classifier_tier1' | 'classifier_tier2_distilbert_indic' | 'ueba_baseline' | 'sonar_signature';
  coordinatorOrigin: string;
  secureAggregationProtocol?: 'flwr_v1_secagg_plus' | 'flwr_v1_secagg' | 'fedavg_dp';
  differentialPrivacyEpsilon?: number;
  differentialPrivacyDelta?: number;
  targetParticipants: number;
  minParticipantsForRelease: number;
}

export interface FederatedRound {
  roundId: string;
  modelKind: string;
  protocol: string;
  targetParticipants: number;
  status: string;
  createdAt: string;
}

export async function recruitFederatedRound(input: RecruitRoundInput): Promise<FederatedRound> {
  return apiFetch('/v1/frontier/federated/rounds', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// PQ crypto
export type PqAlgorithm =
  | 'crystals_kyber_768'
  | 'crystals_kyber_1024'
  | 'crystals_dilithium_2'
  | 'crystals_dilithium_3'
  | 'crystals_dilithium_5';
export type PqPurpose = 'audit_chain' | 'breach_notification' | 'tokenisation_kem' | 'workload_identity';
export type PqProvider = 'liboqs' | 'cloudflare_circl' | 'aws_kms_pq_pre_release' | 'surrogate_test_only';

export interface GeneratePqKeyPairInput {
  algorithm: PqAlgorithm;
  purpose: PqPurpose;
  libraryProvider?: PqProvider;
}

export interface PqKeyPair {
  keyId: string;
  algorithm: PqAlgorithm;
  purpose: PqPurpose;
  publicKeyB64: string;
  privateKeyKmsRef: string;
  libraryProvider: string;
  createdAt: string;
}

export async function generatePqKeyPair(input: GeneratePqKeyPairInput): Promise<PqKeyPair> {
  return apiFetch('/v1/frontier/pq/key-pairs', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Compliance replay
export type Regulator =
  | 'dpdpa' | 'gdpr' | 'ccpa' | 'hipaa' | 'pci_dss' | 'rbi_dpf' | 'sebi_csscb'
  | 'uidai_2025_circular' | 'nbfc_dpf' | 'sec_5b' | 'glba' | 'pipeda'
  | 'pdpa_sg' | 'lgpd_brazil' | 'pipl';

export interface ReplayEvent {
  id: string;
  eventType: 'finding' | 'activity_event' | 'decision' | 'breach_incident';
  occurredAt: string;
  piiCategories: string[];
  crossBorder: boolean;
  involvesMinor: boolean;
  originalDecision?: string;
  originalNotificationClockHours?: number;
  metadata?: Record<string, unknown>;
}

export interface RunReplayInput {
  replayName: string;
  fromTimestamp: string;
  toTimestamp: string;
  targetRegulators: Regulator[];
  events: ReplayEvent[];
}

export interface ReplayDelta {
  eventId: string;
  regulator: Regulator;
  originalDecision?: string;
  counterFactualDecision: string;
  originalClockHours?: number;
  counterFactualClockHours: number;
  clockChangedBy: number;
  section9EquivalentTriggered: boolean;
  newHardBlock: boolean;
}

export interface RunReplayResult {
  replayId: string;
  replayName: string;
  perRegulator: Array<{
    regulator: Regulator;
    deltas: ReplayDelta[];
    summary: {
      clockTightenings: number;
      clockLoosens: number;
      newHardBlocks: number;
    };
  }>;
}

export async function runComplianceReplay(input: RunReplayInput): Promise<RunReplayResult> {
  return apiFetch('/v1/frontier/replay/run', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// T1.5 (2026-05-29) - historical replay: pulls real activity_events +
// ddr_decisions + breach_incidents over the window from the tenant's
// own stores rather than supplying synthetic events. Same result shape.
export interface RunHistoricalReplayInput {
  replayName: string;
  fromTimestamp: string;
  toTimestamp: string;
  targetRegulators: Regulator[];
  hypothesis?: string;
  maxEventsPerSource?: number;
}

export async function runHistoricalComplianceReplay(
  input: RunHistoricalReplayInput,
): Promise<RunReplayResult> {
  return apiFetch('/v1/frontier/replay/historical', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ── Super Admin (Platform-ops only) ──

export interface TenantHealth {
  tenantId: string;
  tenantName: string;
  industryAnchor: string;
  decisions24h: number;
  blocks24h: number;
  stepUps24h: number;
  masks24h: number;
  findingsOpen: number;
  findingsCritical: number;
  uebaOpenAlerts: number;
  breachIncidentsOpen: number;
  topRuleFiring: string;
  licenseTier: string;
  lastSeenAt: string;
}

export interface CrossTenantHealth {
  generatedAt: string;
  tenantCount: number;
  totals: { decisions24h: number; blocks24h: number; findingsOpen: number; breachesOpen: number };
  tenants: TenantHealth[];
  fleetAlerts: Array<{
    severity: 'critical' | 'high' | 'medium';
    title: string;
    affectedTenantCount: number;
    rationale: string;
  }>;
}

export async function getSuperAdminHealth(): Promise<CrossTenantHealth> {
  return apiFetch('/v1/super-admin/ddr/health');
}

export interface CuratedRule {
  ruleId: string;
  name: string;
  category: 'discovery' | 'activity' | 'detection' | 'frontier';
  industryAnchors: string[];
  severity: 'critical' | 'high' | 'medium' | 'low';
  rationale: string;
  regoBodyExcerpt: string;
  curatedBy: string;
  curatedAt: string;
  distributionCount: number;
}

export async function listMasterRules(): Promise<{ rules: CuratedRule[] }> {
  return apiFetch('/v1/super-admin/ddr/rule-library');
}

export async function promoteMasterRule(ruleId: string, tenantId: string): Promise<{
  ruleId: string; tenantId: string; promotedAt: string; distributionCount: number;
}> {
  return apiFetch(`/v1/super-admin/ddr/rule-library/${encodeURIComponent(ruleId)}/promote`, {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  });
}

// ── FOCTTA core integration (Connect wizard) ──

export interface FocttaCoreIntegrationStatus {
  connected: boolean;
  integrationId?: string;
  displayName?: string;
  baseUrl?: string;
  remoteTenantId?: string;
  status?: 'pending' | 'active' | 'paused' | 'failed' | 'revoked';
  lastSuccessAt?: string;
  lastFailureAt?: string;
  consecutiveFailures?: number;
  subscribedEventTypes?: string;
  featureFlags?: Record<string, boolean>;
}

export interface ConnectFocttaCoreInput {
  displayName?: string;
  baseUrl: string;
  remoteTenantId: string;
  apiKey: string;
  hmacEgressSecret: string;
  scimToken?: string;
  inboundWebhookSecret: string;
  subscribedEventTypes?: string;
  featureFlags?: Record<string, boolean>;
}

export interface ConnectFocttaCoreResult {
  integrationId: string;
  status: string;
  probe: Array<{ check: string; ok: boolean; detail?: string }>;
  message: string;
}

export async function getFocttaCoreStatus(): Promise<FocttaCoreIntegrationStatus> {
  return apiFetch('/v1/admin/integrations/foctta-core');
}

export async function connectFocttaCore(input: ConnectFocttaCoreInput): Promise<ConnectFocttaCoreResult> {
  return apiFetch('/v1/admin/integrations/foctta-core/connect', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ── BFSI-grade single-paste Connect (v1) ─────────────────────────
// See docs/integration-spec/FOCTTA-ENROLLMENT-TOKEN-V1.md
export interface ConnectByTokenInput {
  enrollmentToken: string;
  discoveryBaseUrl?: string;
}

export interface ConnectByTokenResult extends ConnectFocttaCoreResult {
  displayName: string;
  baseUrl: string;
  remoteTenantId: string;
  connectorInstanceId: string;
  subscribedEventTypes: string;
  scopes: string[];
  degradedChecks?: string[];
}

export async function connectFocttaCoreByToken(input: ConnectByTokenInput): Promise<ConnectByTokenResult> {
  return apiFetch('/v1/admin/integrations/foctta-core/connect-by-token', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function testFocttaCore(): Promise<{
  checks: Array<{ check: string; ok: boolean; detail?: string }>;
  allOk: boolean;
}> {
  return apiFetch('/v1/admin/integrations/foctta-core/test', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function disconnectFocttaCore(reason: string): Promise<{
  message: string;
  revokedIntegrationId: string;
}> {
  return apiFetch('/v1/admin/integrations/foctta-core/disconnect', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// =====================================================================
// Phase 1 (2026-05-14): client coverage expansion for Phases 2-5 wiring
//
// Adds wrappers for the ~35 backend routes that were uncovered per the
// Phase 1 audit (`docs/poc-roadmap/01-AUDIT-SUMMARY.md §2`). Each function
// matches the route's request/response shape; types reflect the Zod
// schemas from the api-controller side (cross-referenced at function
// definition time, not auto-generated - that's Phase 9).
// =====================================================================

// ── Foundational (10 routes - all previously uncovered) ────────────

/**
 * Wire shape returned by GET /v1/foundational/licenses. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (LicenseSchema).
 *
 * NOTE 2026-05-16 - prior product enum ('foctta_complete' /
 * 'foctta_enterprise' / 'foctta_discovery_only') did not match the server
 * enum ('foctta_platform' / 'discovery' / 'activity' / 'ddr'). Prior shape
 * was also missing tenantId / status / uatToProdPromotedAt /
 * revocationReason / createdAt / updatedAt. Corrected.
 */
export type FoundationalLicenseProduct = 'foctta_platform' | 'discovery' | 'activity' | 'ddr';
export type FoundationalLicenseTier = 'lite' | 'standard' | 'sovereign';
export type FoundationalLicenseStatus = 'active' | 'trial' | 'expired' | 'revoked';

export interface FoundationalLicense {
  id: string;
  tenantId: string;
  product: FoundationalLicenseProduct;
  tier: FoundationalLicenseTier;
  status: FoundationalLicenseStatus;
  activatedAt: string;
  expiresAt: string | null;
  limits: Record<string, number | null>;
  features: Record<string, boolean>;
  procurementRef: string | null;
  uatToProdPromotedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function getFoundationalHealth(): Promise<{ status: 'ok'; service: string }> {
  return apiFetch('/v1/foundational/health');
}

export async function listLicenses(): Promise<{ licenses: FoundationalLicense[] }> {
  return apiFetch('/v1/foundational/licenses');
}

/**
 * Wire shape returned by GET /v1/foundational/egress-log. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (EgressLogEntrySchema). Each row records one metadata payload that
 * left the customer's network, with a SHA-256 hash chain so the CISO
 * can verify tamper-evidence via /v1/foundational/egress-log/verify.
 *
 * NOTE 2026-05-16 - the prior shape declared here was a copy/paste of
 * the audit-chain entry shape (eventType/entityType/actorUserId), not
 * the egress-log shape. Corrected as part of Phase 5b item #8 wire-up.
 */
export type EgressPayloadKind =
  | 'discovery_finding'
  | 'activity_event_summary'
  | 'ddr_decision'
  | 'health_check'
  | 'attestation_doc';

export interface EgressLogEntry {
  id: string;
  tenantId: string;
  payloadHash: string;
  payloadSizeBytes: number;
  destinationUrl: string;
  payloadKind: EgressPayloadKind;
  schemaVersion: string;
  bannedFieldsDropped: Record<string, unknown>;
  previousHash: string;
  chainHash: string;
  createdAt: string;
}

// ── Edge fleet (Phase 7 batch A) ──────────────────────────────────────

/**
 * Wire shape returned by GET /v1/edge/fleet. Server-side source:
 * apps/api-controller/src/modules/edge/schemas.ts (EdgePodSummarySchema).
 */
export type EdgeComponent = 'discovery' | 'activity' | 'detection';
export type EdgeHealthStatus =
  | 'pending'
  | 'healthy'
  | 'degraded'
  | 'stale'
  | 'down'
  | 'decommissioned';

export interface EdgePodSummary {
  id: string;
  podId: string;
  component: EdgeComponent;
  displayName: string;
  businessUnitId: string | null;
  version: string;
  helmChartVersion: string | null;
  clusterName: string | null;
  namespace: string | null;
  lastHeartbeatAt: string | null;
  stalenessThresholdSeconds: number;
  /**
   * Cadence the AGENT declares for itself. The effective staleness window is
   * GREATEST(stalenessThresholdSeconds, this * 2) - which is why a laptop that
   * reports every 4 hours is no longer permanently `down`.
   */
  heartbeatIntervalSeconds: number | null;
  lagWatermarkSeconds: number | null;
  eventsProcessed24h: number;
  eventsDropped24h: number;
  /**
   * Agent self-measurement (Command Plane Phase 1). NULL means NOT REPORTED,
   * which is not the same as zero - every agent class except the scanner
   * reports nothing today, and rendering those as 0% would show an
   * un-instrumented agent as idle.
   */
  cpuSecondsTotal: number | null;
  cpuPercent: number | null;
  rssMb: number | null;
  measuredAt: string | null;
  healthStatus: EdgeHealthStatus;
  statusDetail: string | null;
  decommissionedAt: string | null;
  createdAt: string;
}

export interface EdgeFleetSummary {
  total: number;
  healthy: number;
  degraded: number;
  stale: number;
  down: number;
  pending: number;
}

export interface EdgePodDetail extends EdgePodSummary {
  decisionRpcP50Ms: number | null;
  decisionRpcP95Ms: number | null;
  decisionRpcP99Ms: number | null;
  metadata: Record<string, unknown>;
  activeOverride: {
    overrideEtag: string;
    patch: Record<string, unknown>;
    pushedAt: string;
    appliedAt: string | null;
  } | null;
  /**
   * What the agent says it is ACTUALLY running (Command Plane Phase 5), as
   * opposed to activeOverride, which is what we ASKED for.
   *
   * null means the agent has never reported one - a pre-Phase-5 build. That is
   * NOT the same as "running defaults", and the UI must say so, because an
   * operator who reads defaults for a silent agent concludes a scan is paused
   * when it is still running.
   */
  effectiveConfig: Record<string, unknown> | null;
  effectiveConfigAt: string | null;
}

export async function listEdgeFleet(
  params: {
    component?: EdgeComponent;
    healthStatus?: EdgeHealthStatus;
    businessUnitId?: string;
    includeDecommissioned?: boolean;
    limit?: number;
  } = {},
): Promise<{ pods: EdgePodSummary[]; summary: EdgeFleetSummary }> {
  const q = new URLSearchParams();
  if (params.component) q.set('component', params.component);
  if (params.healthStatus) q.set('healthStatus', params.healthStatus);
  if (params.businessUnitId) q.set('businessUnitId', params.businessUnitId);
  if (params.includeDecommissioned) q.set('includeDecommissioned', 'true');
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/edge/fleet${suffix}`);
}

export async function getEdgePod(id: string): Promise<{ pod: EdgePodDetail }> {
  return apiFetch(`/v1/edge/fleet/${id}`);
}

/**
 * Decommission an edge pod (operator-driven). Marks `decommissioned_at`
 * + flips `health_status` to `decommissioned`. Idempotent.
 *
 * POST /v1/edge/fleet/:id/decommission
 */
export async function decommissionEdgePod(
  id: string,
  reason: string,
): Promise<{ pod: EdgePodDetail }> {
  return apiFetch(`/v1/edge/fleet/${id}/decommission`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/** Deployment platform the provisioning wizard targets (2026-07-11). */
export type EdgeInstallPlatform = 'kubernetes' | 'docker' | 'linux' | 'windows' | 'macos';

/** One generated install file (download/copy) + its one-line run hint. */
export interface EdgeInstallArtifact {
  filename: string;
  contents: string;
  language: 'yaml' | 'bash' | 'powershell';
  runNote: string;
}

export interface MintInstallTokenInput {
  component: EdgeComponent;
  platform?: EdgeInstallPlatform;
  displayName: string;
  businessUnitId?: string;
  ttlSeconds?: number;
  allowedIpCidr?: string;
  scope?: Record<string, unknown>;
  /** 'fleet' + maxDevices: one installer for a whole MDM rollout. */
  kind?: 'single' | 'fleet';
  maxDevices?: number;
}

export async function mintEdgeInstallToken(
  input: MintInstallTokenInput,
  idempotencyKey?: string,
): Promise<{
  tokenId: string;
  bootstrapToken: string;
  expiresAt: string;
  component: EdgeComponent;
  platform: EdgeInstallPlatform;
  artifacts: EdgeInstallArtifact[];
  helmValues: string;
  installScript: string;
}> {
  return apiFetch('/v1/edge/install-tokens', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export interface EdgeInstallTokenRow {
  id: string;
  component: EdgeComponent;
  kind: 'single' | 'fleet';
  /** Fleet only. The licence ceiling; null on a single token. */
  maxDevices: number | null;
  /**
   * How much of this token has been SPENT. A ceiling ledger - it only ever goes
   * up, because recycling slots on decommission would let a fleet quietly
   * exceed its licence.
   */
  enrolledCount: number;
  /** How many of those devices are running RIGHT NOW. Diverges from
   * enrolledCount as soon as any device is retired, and reading one as the
   * other will mis-size the next rollout. */
  livePods: number;
  boundPodId: string | null;
  expiresAt: string;
  usedAt: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
}

export async function listEdgeInstallTokens(): Promise<{ tokens: EdgeInstallTokenRow[] }> {
  return apiFetch('/v1/edge/install-tokens');
}

export type AgentEntitlementState = 'unlimited' | 'within' | 'grace' | 'over';

export interface AgentEntitlementVerdict {
  state: AgentEntitlementState;
  live: number;
  limit: number | null;
  over: number;
  graceCeiling: number | null;
  enrolmentAllowed: boolean;
  tokenMintingAllowed: boolean;
  summary: string;
}

/**
 * Agent entitlement per class.
 *
 * Read-only by design. A tenant sees exactly where they stand and can ask for
 * more; the ceiling itself is set by FOCTTA, which is why the response also
 * says who manages it rather than rendering an editable field the operator
 * cannot actually change.
 */
export async function getAgentEntitlement(): Promise<{
  classes: Record<'endpoint' | 'vpcPod' | 'gateway', AgentEntitlementVerdict>;
  ceilingManagedBy: string;
}> {
  return apiFetch('/v1/edge/entitlement');
}

/**
 * STOPS every device on the token at its next heartbeat. The response names
 * them, because on a fleet token that can be a hundred machines.
 */
export async function revokeEdgeInstallToken(
  id: string,
  reason: string,
): Promise<{ revoked: boolean; livePodsStopped: string[] }> {
  return apiFetch(`/v1/edge/install-tokens/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: newIdempotencyKey(),
  });
}

/** No new devices; everything already running keeps running. */
export async function sealEdgeInstallToken(
  id: string,
): Promise<{ sealed: boolean; atDevices: number }> {
  return apiFetch(`/v1/edge/install-tokens/${id}/seal`, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function pushEdgeConfigOverride(
  podId: string,
  patch: Record<string, unknown>,
  idempotencyKey?: string,
): Promise<{ overrideEtag: string; patch: Record<string, unknown>; pushedAt: string }> {
  return apiFetch(`/v1/edge/fleet/${podId}/config-override`, {
    method: 'POST',
    body: JSON.stringify({ patch }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Edge fleet - resource budgets + watchdog (Phase 6) ────────────────

export type EdgeEnforcement = 'observe' | 'throttle' | 'pause';

export interface EdgeResourceBudget {
  component: EdgeComponent;
  cpuPercentCeiling: number | null;
  rssMbCeiling: number | null;
  sustainedSamples: number;
  enforcement: EdgeEnforcement;
  /** True when no tenant row exists and the platform default is in force. */
  isDefault: boolean;
}

export interface EdgeBudgetBreach {
  id: string;
  podId: string;
  component: EdgeComponent;
  observedCpuPercent: number | null;
  observedRssMb: number | null;
  ceilingCpuPercent: number | null;
  ceilingRssMb: number | null;
  consecutiveSamples: number;
  /** What the watchdog DID. `none` means observe-only - it recorded, nothing more. */
  actionTaken: 'none' | 'throttle' | 'pause';
  actionDetail: string | null;
  firstObservedAt: string;
  resolvedAt: string | null;
}

export async function listEdgeBudgets(): Promise<{ budgets: EdgeResourceBudget[] }> {
  return apiFetch('/v1/edge/budgets');
}

export async function upsertEdgeBudget(
  component: EdgeComponent,
  input: Partial<Pick<EdgeResourceBudget,
    'cpuPercentCeiling' | 'rssMbCeiling' | 'sustainedSamples' | 'enforcement'>>,
  idempotencyKey?: string,
): Promise<{ budget: EdgeResourceBudget }> {
  return apiFetch(`/v1/edge/budgets/${component}`, {
    method: 'PUT',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function listEdgeBreaches(): Promise<{ breaches: EdgeBudgetBreach[] }> {
  return apiFetch('/v1/edge/breaches');
}

// ── Edge fleet - diagnose + update (Phase 7 batch B) ──────────────────

export type DiagnoseCheckStatus = 'ok' | 'warn' | 'fail' | 'unknown';

export interface DiagnoseCheck {
  name: string;
  status: DiagnoseCheckStatus;
  detail: string;
}

export interface DiagnoseReport {
  podId: string;
  generatedAt: string;
  mode: 'synthesised_from_telemetry' | 'live_pod_rpc';
  overall: DiagnoseCheckStatus;
  checks: DiagnoseCheck[];
  summary: string;
}

export async function diagnoseEdgePod(
  id: string,
  idempotencyKey?: string,
): Promise<{ report: DiagnoseReport }> {
  return apiFetch(`/v1/edge/fleet/${id}/diagnose`, {
    method: 'POST',
    body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export type EdgeRolloutStrategy = 'canary' | 'blue_green' | 'immediate';

export async function triggerEdgeUpdate(
  podId: string,
  input: { targetVersion: string; rolloutStrategy?: EdgeRolloutStrategy },
  idempotencyKey?: string,
): Promise<{
  overrideEtag: string;
  targetVersion: string;
  rolloutStrategy: EdgeRolloutStrategy;
  pushedAt: string;
}> {
  return apiFetch(`/v1/edge/fleet/${podId}/update`, {
    method: 'POST',
    body: JSON.stringify({
      targetVersion: input.targetVersion,
      rolloutStrategy: input.rolloutStrategy ?? 'canary',
    }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Edge command queue (Command Plane Phase 5) ───────────────────

export type EdgeCommandName = 'pause' | 'resume' | 'collect_diagnostics' | 'rescan' | 'upload_logs';
export type EdgeCommandState = 'queued' | 'sent' | 'acked' | 'expired';

export interface EdgeCommand {
  id: string;
  command: string;
  payload: Record<string, unknown>;
  state: EdgeCommandState;
  priority: number;
  /** Required at enqueue time - "who told this laptop to stop, and why". */
  reason: string | null;
  result: 'ok' | 'failed' | null;
  resultDetail: string | null;
  sentAt: string | null;
  ackedAt: string | null;
  expiresAt: string;
  createdAt: string;
  /**
   * How many times this was handed to the agent. >1 with no ack means the
   * device is taking commands and not answering - which is NOT the same as
   * never receiving one, and needs a different response from the operator.
   */
  deliveryAttempts: number;
}

export async function listEdgeCommands(podId: string): Promise<{ commands: EdgeCommand[] }> {
  return apiFetch(`/v1/edge/fleet/${podId}/commands`);
}

export async function enqueueEdgeCommand(
  podId: string,
  input: { command: EdgeCommandName; reason: string; payload?: Record<string, unknown>; ttlSeconds?: number },
  idempotencyKey?: string,
): Promise<{ id: string; command: string; state: string; expiresAt: string }> {
  return apiFetch(`/v1/edge/fleet/${podId}/commands`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance (Phase 8 batch A) ──────────────────────────────────────

export type RegulationId =
  | 'DPDPA_2023' | 'DPDPA_S9' | 'DPDPA_S8' | 'DPDPA_S10'
  | 'GDPR' | 'GDPR_Art8' | 'GDPR_Art9' | 'GDPR_Art13' | 'GDPR_Art16'
  | 'GDPR_Art17' | 'GDPR_Art18' | 'GDPR_Art20' | 'GDPR_Art21' | 'GDPR_Art22'
  | 'GDPR_Art33' | 'GDPR_Art35'
  | 'CCPA' | 'COPPA_1998' | 'UK_Children_Code'
  | 'RBI_MD_Cyber' | 'IRDAI_2023' | 'SEBI_CSCRF';

export type ComplianceStatus = 'green' | 'amber' | 'red' | 'not_started' | 'unknown';

export interface ComplianceControl {
  id: string;
  name: string;
  status: ComplianceStatus;
  detail: string;
  sourceRef?: string;
}

export interface RegulationCoverage {
  regulationId: RegulationId;
  displayName: string;
  jurisdiction: string;
  coverageScore: number;
  status: ComplianceStatus;
  controlsTotal: number;
  controlsGreen: number;
  controlsAmber: number;
  controlsRed: number;
  controls: ComplianceControl[];
}

export interface ComplianceCoverageResponse {
  generatedAt: string;
  regulations: RegulationCoverage[];
  overall: {
    score: number;
    status: ComplianceStatus;
    greenRegulations: number;
    amberRegulations: number;
    redRegulations: number;
  };
}

export interface ComplianceGap {
  id: string;
  regulationId: RegulationId;
  controlId: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  recommendedAction: string;
  ownerRoleHint: string;
  sourceRoute?: string;
}

export interface ComplianceGapsResponse {
  generatedAt: string;
  gaps: ComplianceGap[];
  byRegulation: Array<{ regulationId: RegulationId; count: number }>;
  bySeverity: { critical: number; high: number; medium: number; low: number };
}

export type ComplianceExportFormat = 'json' | 'csv' | 'pdf';

export interface ComplianceExportResponse {
  format: ComplianceExportFormat;
  regulationId: RegulationId | null;
  fromDate: string | null;
  toDate: string | null;
  generatedAt: string;
  signedDigest: string;
  rowCount: number;
  body: string;
  contentType: string;
}

export async function getComplianceCoverage(): Promise<ComplianceCoverageResponse> {
  return apiFetch('/v1/compliance/coverage');
}

export async function getComplianceGaps(): Promise<ComplianceGapsResponse> {
  return apiFetch('/v1/compliance/gaps');
}

export async function buildComplianceExport(input: {
  format: ComplianceExportFormat;
  regulationId?: RegulationId;
  fromDate?: string;
  toDate?: string;
}): Promise<ComplianceExportResponse> {
  return apiFetch('/v1/compliance/export', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ── Compliance / Notice generator (Phase 8 batch B item 1) ────────────

export type NoticeJurisdiction = 'India' | 'EU' | 'US-CA' | 'UK' | 'global';
export type NoticeStatus = 'draft' | 'published' | 'expired' | 'revoked';

export interface ComplianceNotice {
  id: string;
  tenantId: string;
  regulationId: RegulationId;
  jurisdiction: NoticeJurisdiction;
  templateVersion: string;
  processingActivity: string;
  dataClasses: string[];
  legalBases: string[];
  retentionPeriodDays: number | null;
  crossBorderDestinations: string[];
  contactDpoEmail: string;
  bodyHtml: string;
  signedDigest: string;
  status: NoticeStatus;
  publicSlug: string | null;
  publishedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNoticeInput {
  regulationId: RegulationId;
  jurisdiction: NoticeJurisdiction;
  processingActivity: string;
  dataClasses: string[];
  legalBases: string[];
  retentionPeriodDays?: number;
  crossBorderDestinations?: string[];
  contactDpoEmail: string;
  publish?: boolean;
  publicSlug?: string;
  expiresInDays?: number;
}

export async function listComplianceNotices(params: {
  status?: NoticeStatus;
  regulationId?: RegulationId;
  limit?: number;
} = {}): Promise<{
  notices: ComplianceNotice[];
  byStatus: { draft: number; published: number; expired: number; revoked: number };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.regulationId) q.set('regulationId', params.regulationId);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/notices${suffix}`);
}

export async function getComplianceNotice(id: string): Promise<{ notice: ComplianceNotice }> {
  return apiFetch(`/v1/compliance/notices/${id}`);
}

export async function createComplianceNotice(
  input: CreateNoticeInput,
  idempotencyKey?: string,
): Promise<{ notice: ComplianceNotice }> {
  return apiFetch('/v1/compliance/notices', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function publishComplianceNotice(
  id: string,
  input: { publicSlug: string; expiresInDays?: number },
  idempotencyKey?: string,
): Promise<{ notice: ComplianceNotice }> {
  return apiFetch(`/v1/compliance/notices/${id}/publish`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function revokeComplianceNotice(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ notice: ComplianceNotice }> {
  return apiFetch(`/v1/compliance/notices/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Erasure executor (Phase 8 batch B item 7) ─────────────

export type ErasureIdentifierKind =
  | 'email' | 'phone' | 'aadhaar' | 'pan' | 'customer_number' | 'custom';
export type ErasureJobStatus =
  | 'pending' | 'approved' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface ErasureLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
}

export interface ErasureJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: ErasureIdentifierKind;
  status: ErasureJobStatus;
  assetsScope: string[];
  executionLog: ErasureLogEntry[];
  requestedByActorId: string;
  requestReason: string | null;
  approverActorId: string | null;
  approverDecidedAt: string | null;
  approverReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listErasureJobs(params: {
  status?: ErasureJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: ErasureJob[];
  byStatus: { pending: number; approved: number; running: number; completed: number; failed: number; cancelled: number };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/erasure-jobs${suffix}`);
}

export async function createErasureRequest(
  input: { principalIdentifier: string; identifierKind: ErasureIdentifierKind; requestReason?: string },
  idempotencyKey?: string,
): Promise<{ job: ErasureJob }> {
  return apiFetch('/v1/compliance/erasure-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveErasureJob(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ job: ErasureJob }> {
  return apiFetch(`/v1/compliance/erasure-jobs/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function executeErasureJob(
  id: string,
  idempotencyKey?: string,
): Promise<{ job: ErasureJob }> {
  return apiFetch(`/v1/compliance/erasure-jobs/${id}/execute`, {
    method: 'POST',
    body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Rectification executor (Phase 8 batch C item 1) ──────

export type RectificationIdentifierKind = ErasureIdentifierKind;
export type RectificationJobStatus = ErasureJobStatus;

export interface RectificationLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
  fieldsUpdated: string[];
}

export interface RectificationJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: RectificationIdentifierKind;
  status: RectificationJobStatus;
  /** Field path → new value the subject asked us to apply. */
  corrections: Record<string, unknown>;
  assetsScope: string[];
  executionLog: RectificationLogEntry[];
  requestedByActorId: string;
  requestReason: string | null;
  approverActorId: string | null;
  approverDecidedAt: string | null;
  approverReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listRectificationJobs(params: {
  status?: RectificationJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: RectificationJob[];
  byStatus: { pending: number; approved: number; running: number; completed: number; failed: number; cancelled: number };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/rectification-jobs${suffix}`);
}

export async function createRectificationRequest(
  input: {
    principalIdentifier: string;
    identifierKind: RectificationIdentifierKind;
    corrections: Record<string, unknown>;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: RectificationJob }> {
  return apiFetch('/v1/compliance/rectification-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveRectificationJob(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ job: RectificationJob }> {
  return apiFetch(`/v1/compliance/rectification-jobs/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function executeRectificationJob(
  id: string,
  idempotencyKey?: string,
): Promise<{ job: RectificationJob }> {
  return apiFetch(`/v1/compliance/rectification-jobs/${id}/execute`, {
    method: 'POST',
    body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Restriction executor (Phase 8 batch D item 1) ────────

export type RestrictionIdentifierKind = ErasureIdentifierKind;
export type RestrictionJobStatus =
  | 'pending' | 'approved' | 'running' | 'restricted' | 'lifted' | 'failed' | 'cancelled';
export type RestrictionBasis =
  | 'accuracy_challenged'
  | 'unlawful_subject_opposes'
  | 'controller_no_longer_needs'
  | 'objection_pending';

export interface RestrictionLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
}

export interface RestrictionJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: RestrictionIdentifierKind;
  status: RestrictionJobStatus;
  restrictionBasis: RestrictionBasis;
  /** Optional sunset for the restriction. NULL = indefinite. */
  untilDate: string | null;
  assetsScope: string[];
  executionLog: RestrictionLogEntry[];
  requestedByActorId: string;
  requestReason: string | null;
  approverActorId: string | null;
  approverDecidedAt: string | null;
  approverReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  liftedAt: string | null;
  liftedByActorId: string | null;
  liftReason: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listRestrictionJobs(params: {
  status?: RestrictionJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: RestrictionJob[];
  byStatus: {
    pending: number; approved: number; running: number;
    restricted: number; lifted: number; failed: number; cancelled: number;
  };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/restriction-jobs${suffix}`);
}

export async function createRestrictionRequest(
  input: {
    principalIdentifier: string;
    identifierKind: RestrictionIdentifierKind;
    restrictionBasis: RestrictionBasis;
    untilDate?: string;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: RestrictionJob }> {
  return apiFetch('/v1/compliance/restriction-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveRestrictionJob(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ job: RestrictionJob }> {
  return apiFetch(`/v1/compliance/restriction-jobs/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function executeRestrictionJob(
  id: string, idempotencyKey?: string,
): Promise<{ job: RestrictionJob }> {
  return apiFetch(`/v1/compliance/restriction-jobs/${id}/execute`, {
    method: 'POST', body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function liftRestrictionJob(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ job: RestrictionJob }> {
  return apiFetch(`/v1/compliance/restriction-jobs/${id}/lift`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Portability executor (Phase 8 batch E item 1) ────────

export type PortabilityIdentifierKind = ErasureIdentifierKind;
export type PortabilityJobStatus =
  | 'pending' | 'approved' | 'running' | 'completed' | 'expired' | 'failed' | 'cancelled';
export type BundleFormat = 'json' | 'csv' | 'xml';

export interface PortabilityLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
  recordsExtracted: number;
}

export interface PortabilityJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: PortabilityIdentifierKind;
  status: PortabilityJobStatus;
  bundleFormat: BundleFormat;
  assetsScope: string[];
  executionLog: PortabilityLogEntry[];
  bundleUrl: string | null;
  bundleSha256: string | null;
  bundleSizeBytes: number | null;
  /** When the signed download URL stops working. NULL until completed. */
  expiresAt: string | null;
  requestedByActorId: string;
  requestReason: string | null;
  approverActorId: string | null;
  approverDecidedAt: string | null;
  approverReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listPortabilityJobs(params: {
  status?: PortabilityJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: PortabilityJob[];
  byStatus: {
    pending: number; approved: number; running: number;
    completed: number; expired: number; failed: number; cancelled: number;
  };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/portability-jobs${suffix}`);
}

export async function createPortabilityRequest(
  input: {
    principalIdentifier: string;
    identifierKind: PortabilityIdentifierKind;
    bundleFormat?: BundleFormat;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: PortabilityJob }> {
  return apiFetch('/v1/compliance/portability-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approvePortabilityJob(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ job: PortabilityJob }> {
  return apiFetch(`/v1/compliance/portability-jobs/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function executePortabilityJob(
  id: string, idempotencyKey?: string,
): Promise<{ job: PortabilityJob }> {
  return apiFetch(`/v1/compliance/portability-jobs/${id}/execute`, {
    method: 'POST', body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Objection executor (Phase 8 batch F item 1) ──────────

export type ObjectionIdentifierKind = ErasureIdentifierKind;
export type ObjectionJobStatus =
  | 'pending' | 'assessing' | 'upheld' | 'overridden' | 'failed' | 'cancelled';
export type ObjectionGround =
  | 'direct_marketing'
  | 'legitimate_interests'
  | 'public_task'
  | 'scientific_research';

export interface ObjectionLogEntry {
  ts: string;
  assetId: string;
  action: string;
  status: 'ok' | 'skipped' | 'failed';
  detail: string;
}

export interface ObjectionJob {
  id: string;
  tenantId: string;
  principalIdentifierHash: string;
  identifierKind: ObjectionIdentifierKind;
  status: ObjectionJobStatus;
  objectionGround: ObjectionGround;
  assetsScope: string[];
  executionLog: ObjectionLogEntry[];
  requestedByActorId: string;
  requestReason: string | null;
  assessedByActorId: string | null;
  assessedAt: string | null;
  assessmentOutcome: 'upheld' | 'overridden' | null;
  assessmentReason: string | null;
  compellingGroundsText: string | null;
  startedAt: string | null;
  completedAt: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listObjectionJobs(params: {
  status?: ObjectionJobStatus;
  limit?: number;
} = {}): Promise<{
  jobs: ObjectionJob[];
  byStatus: {
    pending: number; assessing: number; upheld: number;
    overridden: number; failed: number; cancelled: number;
  };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/objection-jobs${suffix}`);
}

export async function createObjectionRequest(
  input: {
    principalIdentifier: string;
    identifierKind: ObjectionIdentifierKind;
    objectionGround: ObjectionGround;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: ObjectionJob }> {
  return apiFetch('/v1/compliance/objection-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function startObjectionAssessment(
  id: string, idempotencyKey?: string,
): Promise<{ job: ObjectionJob }> {
  return apiFetch(`/v1/compliance/objection-jobs/${id}/start-assessment`, {
    method: 'POST', body: '{}',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function assessObjectionJob(
  id: string,
  input: {
    outcome: 'upheld' | 'overridden';
    assessmentReason: string;
    compellingGroundsText?: string;
  },
  idempotencyKey?: string,
): Promise<{ job: ObjectionJob }> {
  return apiFetch(`/v1/compliance/objection-jobs/${id}/assess`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Compliance / Phase 8 batch G registers ────────────────────────────

// ---- DPIA assessments ----
export type DpiaStatus = 'draft' | 'reviewed' | 'approved' | 'rejected' | 'archived';
export type DpiaLegalBasis =
  | 'consent' | 'contract' | 'legal_obligation'
  | 'vital_interests' | 'public_task' | 'legitimate_interests';
export interface DpiaRiskItem {
  id: string; description: string;
  likelihood: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
  residualRisk: 'low' | 'medium' | 'high';
  mitigation?: string;
}
export interface DpiaAssessment {
  id: string; tenantId: string;
  title: string; processingPurpose: string; legalBasis: DpiaLegalBasis;
  dataCategories: string[]; affectedSubjectsEstimate: number | null;
  riskRegister: DpiaRiskItem[]; mitigationPlan: string | null;
  consultationDpo: boolean; consultationLegal: boolean; consultationItAdmin: boolean;
  status: DpiaStatus;
  reviewedByActorId: string | null; reviewedAt: string | null;
  approverActorId: string | null; approvedAt: string | null;
  rejectionReason: string | null; archivedAt: string | null;
  createdByActorId: string; createdAt: string; updatedAt: string;
}
export async function listDpias(params: { status?: DpiaStatus; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch<{ dpias: DpiaAssessment[]; byStatus: Record<DpiaStatus, number> }>(
    `/v1/compliance/dpia${q.toString() ? `?${q}` : ''}`,
  );
}
export async function createDpia(
  input: {
    title: string; processingPurpose: string; legalBasis: DpiaLegalBasis;
    dataCategories: string[]; affectedSubjectsEstimate?: number;
    riskRegister: DpiaRiskItem[]; mitigationPlan?: string;
    consultationDpo?: boolean; consultationLegal?: boolean; consultationItAdmin?: boolean;
  },
  idempotencyKey?: string,
): Promise<{ dpia: DpiaAssessment }> {
  return apiFetch('/v1/compliance/dpia', {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}
export async function transitionDpia(
  id: string,
  input: { action: 'review' | 'approve' | 'reject' | 'archive'; reason?: string },
  idempotencyKey?: string,
): Promise<{ dpia: DpiaAssessment }> {
  return apiFetch(`/v1/compliance/dpia/${id}/transition`, {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ---- CCPA opt-outs ----
export type CcpaOptoutStatus = 'active' | 'revoked';
export type CcpaJurisdiction =
  | 'CA' | 'CO' | 'VA' | 'CT' | 'UT' | 'OR' | 'TX' | 'FL' | 'DE' | 'MT';
export type CcpaRequestChannel =
  | 'webform' | 'authorized_agent' | 'gpc_signal' | 'phone' | 'paper';
export interface CcpaOptout {
  id: string; tenantId: string;
  principalIdentifierHash: string; identifierKind: ErasureIdentifierKind;
  status: CcpaOptoutStatus;
  optOutSale: boolean; optOutSharing: boolean;
  jurisdiction: CcpaJurisdiction;
  requestChannel: CcpaRequestChannel | null; requestReason: string | null;
  requestedByActorId: string;
  revokedByActorId: string | null; revokedAt: string | null; revokeReason: string | null;
  createdAt: string; updatedAt: string;
}
export async function listCcpaOptouts(params: { status?: CcpaOptoutStatus; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch<{ optouts: CcpaOptout[]; byStatus: Record<CcpaOptoutStatus, number> }>(
    `/v1/compliance/ccpa-optouts${q.toString() ? `?${q}` : ''}`,
  );
}
export async function createCcpaOptout(
  input: {
    principalIdentifier: string; identifierKind: ErasureIdentifierKind;
    optOutSale?: boolean; optOutSharing?: boolean;
    jurisdiction?: CcpaJurisdiction; requestChannel?: CcpaRequestChannel;
    requestReason?: string;
  },
  idempotencyKey?: string,
): Promise<{ optout: CcpaOptout }> {
  return apiFetch('/v1/compliance/ccpa-optouts', {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}
export async function revokeCcpaOptout(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ optout: CcpaOptout }> {
  return apiFetch(`/v1/compliance/ccpa-optouts/${id}/revoke`, {
    method: 'POST', body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ---- Service providers ----
export type ProviderStatus = 'active' | 'paused' | 'offboarded';
export type ProviderRole = 'processor' | 'sub_processor' | 'joint_controller' | 'controller';
export type TransferMechanism =
  | 'adequacy' | 'sccs_2021' | 'sccs_2010' | 'bcrs' | 'derogation' | 'none';
export interface ServiceProvider {
  id: string; tenantId: string;
  vendorName: string; vendorRole: ProviderRole;
  jurisdiction: string; primaryContactEmail: string | null;
  dataCategories: string[]; processingPurposes: string[];
  dpaOnFile: boolean; dpaSignedAt: string | null; dpaReviewDueAt: string | null;
  transferMechanism: TransferMechanism | null;
  lastAuditAt: string | null; nextAuditDueAt: string | null;
  status: ProviderStatus;
  pausedReason: string | null;
  offboardedAt: string | null; offboardedReason: string | null;
  createdByActorId: string; createdAt: string; updatedAt: string;
}
export async function listProviders(params: { status?: ProviderStatus; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch<{ providers: ServiceProvider[]; byStatus: Record<ProviderStatus, number> }>(
    `/v1/compliance/service-providers${q.toString() ? `?${q}` : ''}`,
  );
}
export async function createProvider(
  input: Partial<Omit<ServiceProvider, 'id' | 'tenantId' | 'createdByActorId' | 'createdAt' | 'updatedAt'>> & {
    vendorName: string; vendorRole: ProviderRole; jurisdiction: string;
  },
  idempotencyKey?: string,
): Promise<{ provider: ServiceProvider }> {
  return apiFetch('/v1/compliance/service-providers', {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}
export async function patchProvider(
  id: string,
  input: Partial<Omit<ServiceProvider, 'id' | 'tenantId' | 'createdByActorId' | 'createdAt' | 'updatedAt'>>,
): Promise<{ provider: ServiceProvider }> {
  return apiFetch(`/v1/compliance/service-providers/${id}`, {
    method: 'PATCH', body: JSON.stringify(input),
  });
}
export async function offboardProvider(
  id: string, reason: string, idempotencyKey?: string,
): Promise<{ provider: ServiceProvider }> {
  return apiFetch(`/v1/compliance/service-providers/${id}/offboard`, {
    method: 'POST', body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ---- SDF obligations (singleton per tenant) ----
export interface SdfObligation {
  id: string; tenantId: string;
  isDesignatedSdf: boolean;
  designationBasis: string | null; designatedAt: string | null;
  dpoAppointed: boolean;
  dpoName: string | null; dpoEmail: string | null; dpoAppointedAt: string | null;
  annualDpiaCompletedAt: string | null; annualDataAuditCompletedAt: string | null;
  otherMeasures: Array<{ id: string; label: string; completedAt?: string; evidenceRef?: string }>;
  lastReviewedByActorId: string | null; lastReviewedAt: string | null; lastReviewerNote: string | null;
  createdByActorId: string; createdAt: string; updatedAt: string;
}
export async function getSdf(): Promise<{ sdf: SdfObligation | null }> {
  return apiFetch('/v1/compliance/sdf-obligations');
}
export async function upsertSdf(
  input: Partial<Omit<SdfObligation, 'id' | 'tenantId' | 'createdByActorId' | 'createdAt' | 'updatedAt' | 'lastReviewedByActorId' | 'lastReviewedAt'>>,
): Promise<{ sdf: SdfObligation }> {
  return apiFetch('/v1/compliance/sdf-obligations', {
    method: 'PUT', body: JSON.stringify(input),
  });
}

// ---- Sensitive-PI tier register ----
export type SensitivePiLawfulBasis =
  | 'art9_2_b_employment' | 'art9_2_c_vital' | 'art9_2_g_substantial_public'
  | 'art9_2_h_health' | 'art9_2_i_public_health' | 'art9_2_j_research';
export interface SensitivePiTier {
  id: string; tenantId: string;
  category: string; inScope: boolean;
  retentionDays: number | null;
  consentRequired: boolean; lawfulBasisOverride: SensitivePiLawfulBasis | null;
  processingRestrictions: Array<{ id: string; label: string; enforcedBy?: string }>;
  geoRestrictions: string[];
  lastReviewedByActorId: string | null; lastReviewedAt: string | null; lastReviewerNote: string | null;
  createdByActorId: string; createdAt: string; updatedAt: string;
}
export async function listSensitivePiTiers(params: { inScope?: boolean; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (typeof params.inScope === 'boolean') q.set('inScope', String(params.inScope));
  if (params.limit) q.set('limit', String(params.limit));
  return apiFetch<{ tiers: SensitivePiTier[]; counts: { inScope: number; outOfScope: number } }>(
    `/v1/compliance/sensitive-pi-tiers${q.toString() ? `?${q}` : ''}`,
  );
}
export async function createSensitivePiTier(
  input: {
    category: string; inScope?: boolean; retentionDays?: number;
    consentRequired?: boolean; lawfulBasisOverride?: SensitivePiLawfulBasis;
    processingRestrictions?: Array<{ id: string; label: string; enforcedBy?: string }>;
    geoRestrictions?: string[];
  },
  idempotencyKey?: string,
): Promise<{ tier: SensitivePiTier }> {
  return apiFetch('/v1/compliance/sensitive-pi-tiers', {
    method: 'POST', body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}
export async function updateSensitivePiTier(
  id: string,
  input: Partial<Omit<SensitivePiTier, 'id' | 'tenantId' | 'category' | 'createdByActorId' | 'createdAt' | 'updatedAt'>>,
): Promise<{ tier: SensitivePiTier }> {
  return apiFetch(`/v1/compliance/sensitive-pi-tiers/${id}`, {
    method: 'PATCH', body: JSON.stringify(input),
  });
}

// ── Compliance / Breach dispatch (Phase 8 batch B item 13) ────────────

export type BreachStatus =
  | 'draft' | 'acknowledged' | 'notified_dpb' | 'notified_data_principals'
  | 'closed' | 'false_positive';

export type BreachRegulator =
  | 'dpdpa' | 'gdpr' | 'rbi' | 'sec_5b' | 'hipaa' | 'pipl' | 'lgpd' | 'pdpa_sg' | 'pipeda';

export interface BreachIncident {
  id: string;
  tenantId: string;
  decisionId: string;
  requestId: string;
  breachWindowStart: string | null;
  breachWindowEnd: string | null;
  affectedRecordCount: number;
  affectedPrincipalsCount: number;
  dataCategories: string[];
  industryAnchor: string;
  recommendedSeverity: string;
  severityNumeric: number;
  status: BreachStatus;
  draftPayload: Record<string, unknown>;
  notificationsRendered: Record<string, string>;
  acknowledgedByUserId: string | null;
  acknowledgedAt: string | null;
  notifiedDpbAt: string | null;
  notifiedDataPrincipalsAt: string | null;
  closedAt: string | null;
  remediationSummary: string | null;
  postIncidentReviewSummary: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listBreachIncidents(params: {
  status?: BreachStatus;
  limit?: number;
} = {}): Promise<{
  incidents: BreachIncident[];
  byStatus: {
    draft: number; acknowledged: number; notified_dpb: number;
    notified_data_principals: number; closed: number; false_positive: number;
  };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/breach-incidents${suffix}`);
}

export async function getBreachIncident(id: string): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}`);
}

export async function acknowledgeBreach(
  id: string,
  reason?: string,
  idempotencyKey?: string,
): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function notifyBreachDpb(
  id: string,
  input: { regulators?: BreachRegulator[]; channelEvidence?: string } = {},
  idempotencyKey?: string,
): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}/notify-dpb`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function notifyBreachDataPrincipals(
  id: string,
  input: { channelEvidence?: string } = {},
  idempotencyKey?: string,
): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}/notify-data-principals`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function closeBreach(
  id: string,
  input: { remediationSummary: string; postIncidentReviewSummary: string },
  idempotencyKey?: string,
): Promise<{ incident: BreachIncident }> {
  return apiFetch(`/v1/compliance/breach-incidents/${id}/close`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Egress log (Phase 5b item 8) ──────────────────────────────────────

export async function listEgressLog(
  params: { cursor?: string; limit?: number; payloadKind?: EgressPayloadKind } = {},
): Promise<{
  entries: EgressLogEntry[];
  nextCursor: string | null;
}> {
  const q = new URLSearchParams();
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  if (params.payloadKind) q.set('payloadKind', params.payloadKind);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/foundational/egress-log${suffix}`);
}

export async function verifyEgressLog(): Promise<{
  valid: boolean;
  rowsVerified: number;
  firstBreakAt: string | null;
  message: string;
}> {
  return apiFetch(`/v1/foundational/egress-log/verify`);
}

/**
 * Wire shape returned by GET /v1/foundational/rbam. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (RbamMappingSchema). A mapping pins ONE role (either a tenant-authored
 * custom roleId OR a built-in role label like 'dpo' / 'compliance_analyst')
 * to a scope: purposes, allowedSystems, allowedDataClasses, businessUnits.
 *
 * NOTE 2026-05-16 - prior shape declared here was a guess that did not
 * match the server response (it had .roleName / .permissions / .scope.bu
 * / .custom / .approverIds, none of which the server returns). Corrected.
 */
export interface RbamMapping {
  id: string;
  tenantId: string;
  roleId: string | null;
  roleBuiltIn: string | null;
  purposes: string[];
  allowedSystems: string[];
  allowedDataClasses: string[];
  businessUnitScope: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UpsertRbamMappingInput {
  roleId?: string | null;
  roleBuiltIn?: string | null;
  purposes?: string[];
  allowedSystems?: string[];
  allowedDataClasses?: string[];
  businessUnitScope?: string[];
}

export async function listRbamMappings(): Promise<{ rbamMappings: RbamMapping[] }> {
  return apiFetch('/v1/foundational/rbam');
}

export async function createRbamMapping(
  input: UpsertRbamMappingInput,
  idempotencyKey?: string,
): Promise<{ rbamMapping: RbamMapping }> {
  return apiFetch('/v1/foundational/rbam', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Custom roles (Phase 5b item #5) ────────────────────────────────────

export type CustomRoleStatus = 'draft' | 'approved' | 'revoked';

export interface RbamScopeEnvelope {
  businessUnit?: 'all' | string[];
  system?: 'all' | string[];
  dataClass?: 'all' | string[];
  purpose?: 'all' | string[];
}

export interface TenantCustomRole {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  permissions: string[];
  scopeEnvelope: RbamScopeEnvelope;
  status: CustomRoleStatus;
  createdByActorId: string;
  approvedByActorId: string | null;
  approvedAt: string | null;
  approverReason: string | null;
  revokedByActorId: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomRoleInput {
  name: string;
  description?: string;
  permissions: string[];
  scopeEnvelope?: RbamScopeEnvelope;
}

export async function listCustomRoles(
  params: { status?: CustomRoleStatus; limit?: number } = {},
): Promise<{
  customRoles: TenantCustomRole[];
  byStatus: { draft: number; approved: number; revoked: number };
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/foundational/custom-roles${suffix}`);
}

export async function getCustomRole(id: string): Promise<{ customRole: TenantCustomRole }> {
  return apiFetch(`/v1/foundational/custom-roles/${id}`);
}

export async function createCustomRole(
  input: CreateCustomRoleInput,
  idempotencyKey?: string,
): Promise<{ customRole: TenantCustomRole }> {
  return apiFetch('/v1/foundational/custom-roles', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveCustomRole(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ customRole: TenantCustomRole }> {
  return apiFetch(`/v1/foundational/custom-roles/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function revokeCustomRole(
  id: string,
  reason: string,
  idempotencyKey?: string,
): Promise<{ customRole: TenantCustomRole }> {
  return apiFetch(`/v1/foundational/custom-roles/${id}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * Wire shape returned by GET /v1/foundational/purposes. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (PurposeMappingSchema). Each row binds an observed source
 * (application / endpoint / service_account / role / sql_tag /
 * bi_report / ropa_id / pia_id) to a declared processing purpose +
 * legal basis + confidence. This is how Discovery resolves "what is
 * this query for?" at scan-time.
 *
 * NOTE 2026-05-16 - prior shape declared here was a guess (key /
 * displayName / dataClasses / legalBases / retentionDays /
 * crossBorderAllowed) that did not match the server response. Corrected
 * to match PurposeMappingSchema.
 */
export type PurposeSourceType =
  | 'application'
  | 'endpoint'
  | 'service_account'
  | 'role'
  | 'sql_tag'
  | 'bi_report'
  | 'ropa_id'
  | 'pia_id';

export type PurposeConfidence = 'high' | 'medium' | 'low';

export interface PurposeMapping {
  id: string;
  tenantId: string;
  sourceType: PurposeSourceType;
  sourceIdentifier: string;
  purposeName: string;
  legalBasis: string | null;
  confidence: PurposeConfidence;
  evidence: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertPurposeMappingInput {
  sourceType: PurposeSourceType;
  sourceIdentifier: string;
  purposeName: string;
  legalBasis?: string | null;
  confidence?: PurposeConfidence;
  evidence?: string | null;
}

export async function listPurposes(): Promise<{ purposeMappings: PurposeMapping[] }> {
  return apiFetch('/v1/foundational/purposes');
}

export async function createPurpose(
  input: UpsertPurposeMappingInput,
  idempotencyKey?: string,
): Promise<{ purposeMapping: PurposeMapping }> {
  return apiFetch('/v1/foundational/purposes', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function resolvePurpose(input: {
  sourceType: PurposeSourceType;
  sourceIdentifier: string;
}): Promise<{ resolved: PurposeMapping | null; candidates: PurposeMapping[] }> {
  return apiFetch('/v1/foundational/purposes/resolve', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Wire shape returned by GET /v1/foundational/adapters/:axis. Server-side
 * source: apps/api-controller/src/modules/foundational/schemas.ts
 * (AdapterSchema). The four axes are first-class per CLAUDE.md
 * Architecture Rule 13 (symmetric-adapter architecture):
 *   • cmp  - alternative CMP that Discovery routes consent into
 *   • dspm - alternative DSPM that FOCTTA core routes data-discovery into
 *   • dam  - alternative DAM
 *   • ddr  - alternative DDR
 *
 * NOTE 2026-05-16 - prior axis values ('alt_dspm' / 'alt_dam' / 'alt_ddr')
 * did not match the server enum ('dspm' / 'dam' / 'ddr'). Prior row shape
 * (vendorKey / displayName / authMethod / configKmsRef) also did not match.
 * Corrected to match AdapterSchema.
 */
export type AdapterAxis = 'cmp' | 'dspm' | 'dam' | 'ddr';
export type AdapterStatus = 'active' | 'paused' | 'error';

export interface Adapter {
  id: string;
  tenantId: string;
  axis: AdapterAxis;
  vendor: string;
  configuration: Record<string, unknown>;
  webhookSecretCredentialId: string | null;
  status: AdapterStatus;
  cacheTtlSeconds: number;
  failOpenOnUnavailable: boolean;
  pullDirection: Record<string, boolean>;
  pushDirection: Record<string, boolean>;
  lastSyncAt: string | null;
  lastConformanceTestRunId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertAdapterInput {
  vendor: string;
  configuration?: Record<string, unknown>;
  webhookSecretCredentialId?: string | null;
  status?: AdapterStatus;
  cacheTtlSeconds?: number;
  failOpenOnUnavailable?: boolean;
  pullDirection?: Record<string, boolean>;
  pushDirection?: Record<string, boolean>;
}

export async function listAdapters(axis: AdapterAxis): Promise<{ adapters: Adapter[] }> {
  return apiFetch(`/v1/foundational/adapters/${axis}`);
}

export async function createAdapter(
  axis: AdapterAxis,
  input: UpsertAdapterInput,
  idempotencyKey?: string,
): Promise<{ adapter: Adapter }> {
  return apiFetch(`/v1/foundational/adapters/${axis}`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Foundational / Business Units (Phase 5b items 1-3) ─────────────────

export type BusinessUnitSource = 'local' | 'foctta_core' | 'system';
export type BusinessUnitStatus = 'active' | 'archived' | 'system';

export interface BusinessUnit {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  parentId: string | null;
  description: string | null;
  primaryOwnerUserId: string | null;
  status: BusinessUnitStatus;
  source: BusinessUnitSource;
  externalRef: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBusinessUnitInput {
  name: string;
  slug?: string;
  parentId?: string | null;
  description?: string | null;
  primaryOwnerUserId?: string | null;
}

export interface UpdateBusinessUnitInput {
  name?: string;
  slug?: string;
  parentId?: string | null;
  description?: string | null;
  primaryOwnerUserId?: string | null;
  status?: 'active' | 'archived';
}

export interface ListBusinessUnitsFilter {
  source?: BusinessUnitSource;
  status?: BusinessUnitStatus;
  /** 'root' = top-level BUs (parentId IS NULL); uuid = specific parent. */
  parentId?: string | 'root';
}

export interface ResyncFromCoreResponse {
  added: number;
  skippedExisting: number;
  localUntouched: number;
  sourceEventId: string | null;
  message: string;
}

export async function listBusinessUnits(
  filter: ListBusinessUnitsFilter = {},
): Promise<{ businessUnits: BusinessUnit[] }> {
  const params = new URLSearchParams();
  if (filter.source) params.set('source', filter.source);
  if (filter.status) params.set('status', filter.status);
  if (filter.parentId) params.set('parentId', filter.parentId);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`/v1/foundational/business-units${suffix}`);
}

export async function getBusinessUnit(
  id: string,
): Promise<{ businessUnit: BusinessUnit; children: BusinessUnit[] }> {
  return apiFetch(`/v1/foundational/business-units/${encodeURIComponent(id)}`);
}

export async function createBusinessUnit(
  input: CreateBusinessUnitInput,
  idempotencyKey?: string,
): Promise<{ businessUnit: BusinessUnit }> {
  return apiFetch('/v1/foundational/business-units', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function updateBusinessUnit(
  id: string,
  patch: UpdateBusinessUnitInput,
): Promise<{ businessUnit: BusinessUnit }> {
  return apiFetch(`/v1/foundational/business-units/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function archiveBusinessUnit(
  id: string,
): Promise<{ businessUnit: BusinessUnit }> {
  // Empty {} body is required because apiFetch sets Content-Type:
  // application/json by default, and Fastify's strict parser rejects an
  // empty body with that header. DELETE has no body semantics here, but
  // the wire shape needs to satisfy the content-type parser.
  return apiFetch(`/v1/foundational/business-units/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: JSON.stringify({}),
  });
}

export async function resyncBusinessUnitsFromFocttaCore(
  idempotencyKey?: string,
): Promise<ResyncFromCoreResponse> {
  return apiFetch('/v1/foundational/business-units/resync-from-foctta-core', {
    method: 'POST',
    body: JSON.stringify({}),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Discovery (additional routes - finding detail, scans CRUD, etc.) ───

export interface DiscoveryFindingDetail extends DiscoveryFinding {
  assetId: string | null;
  toxicCombinations: string[];
  evidenceSources: string[];
  resolutionNote: string | null;
  updatedAt: string;
}

export async function getDiscoveryFinding(id: string): Promise<DiscoveryFindingDetail> {
  return apiFetch(`/v1/discovery/findings/${encodeURIComponent(id)}`);
}

// F-2.8 - per-finding drill-down lineage (T1.3 surfaced 2026-05-29).
// GET /v1/discovery/findings/:id/lineage → finding-lineage.ts:FindingLineage.
// Each section degrades to an empty list when its source table/column
// isn't present, so the drawer renders rather than 500ing.
export interface FindingLineage {
  findingId: string;
  assetChain: Array<{ kind: string; id: string; label: string }>;
  linkedDecisions: Array<{
    id: string;
    requestId: string | null;
    decision: string;
    decidedAt: string;
    explanation: string | null;
  }>;
  linkedBreaches: Array<{
    id: string;
    severity: string;
    status: string;
    openedAt: string;
    affectedPrincipalsCount: number | null;
  }>;
  activityHistory: Array<{
    ts: string;
    eventType: string;
    actorId: string | null;
    summary: string;
  }>;
}

export async function getFindingLineage(id: string): Promise<FindingLineage> {
  return apiFetch<FindingLineage>(`/v1/discovery/findings/${encodeURIComponent(id)}/lineage`);
}

export async function patchDiscoveryFindingStatus(
  id: string,
  status: DiscoveryFinding['status'],
  resolutionNote?: string,
): Promise<DiscoveryFindingDetail> {
  return apiFetch(`/v1/discovery/findings/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, resolutionNote }),
  });
}

/**
 * Discovery scan. Wire shape matches server's ScanSchema in
 * apps/api-controller/src/modules/discovery/schemas.ts:209.
 * Previous client interface had completely different field names
 * (`connectorInstanceId / startedAt / finishedAt / itemsScanned`)
 * which would have silently failed at runtime - same drift class as
 * Section9 + asset + scan-trigger bugs fixed in the hardening pass.
 */
export interface DiscoveryScan {
  id: string;
  tenantId: string;
  scanMode: 'sketch' | 'full' | 'forensic';
  targetAssetIds: string[];
  targetSourceTypes: string[];
  classifierTiers: number[];
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string | null;
  completedAt: string | null;
  rowsScanned: number;
  findingsCreated: number;
  confidentialVmAttestationId: string | null;
  triggeredByUserId: string | null;
  errorMessage: string | null;
  triggeredByReason: string | null;
  breachId: string | null;
  incidentRef: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listDiscoveryScans(): Promise<{ scans: DiscoveryScan[] }> {
  return apiFetch('/v1/discovery/scans');
}

export async function getDiscoveryScan(id: string): Promise<DiscoveryScan> {
  return apiFetch(`/v1/discovery/scans/${encodeURIComponent(id)}`);
}

/**
 * Trigger a Discovery scan. Wire shape matches the server's
 * `TriggerScanSchema` in
 * apps/api-controller/src/modules/discovery/schemas.ts:202 - NOT the
 * earlier guess that used `connectorInstanceId / scanProfile`. The
 * shape mismatch was found during the Phase-2 hardening pass (it's
 * the same class of bug as the Section9 type drift; cf. tracker §9
 * OQ-2026-05-14-A).
 */
export interface TriggerScanInput {
  /** Server-side ScanModeSchema enum at schemas.ts:33 - one of 'sketch' / 'full' / 'forensic'. */
  scanMode: 'sketch' | 'full' | 'forensic';
  targetAssetIds?: string[];
  targetSourceTypes?: string[];
  classifierTiers?: number[];
}

export async function triggerDiscoveryScan(
  input: TriggerScanInput,
  idempotencyKey?: string,
): Promise<DiscoveryScan> {
  return apiFetch('/v1/discovery/scans', {
    method: 'POST',
    body: JSON.stringify({
      scanMode: input.scanMode,
      targetAssetIds: input.targetAssetIds ?? [],
      targetSourceTypes: input.targetSourceTypes ?? [],
      classifierTiers: input.classifierTiers ?? [1, 2, 3, 4],
    }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * Finalize a scan (completed / failed / cancelled). Wire shape matches the
 * server's CompleteScanSchema. Maps to POST /v1/discovery/scans/:id/complete
 * - the terminal transition that stamps completedAt + rowsScanned. The
 * resolver advances queued→running on ingest; this closes the run.
 */
export async function completeDiscoveryScan(
  id: string,
  patch: { status?: 'completed' | 'failed' | 'cancelled'; rowsScanned?: number; errorMessage?: string | null } = {},
): Promise<{ scan: DiscoveryScan }> {
  return apiFetch(`/v1/discovery/scans/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    body: JSON.stringify({ status: patch.status ?? 'completed', ...patch }),
  });
}

/**
 * Live catalog crawl - auto-discover the FULL schema/table/column catalog from
 * one DB connection. Maps to POST /v1/discovery/catalog-crawl. The "give creds,
 * we find everything inside" engine: registers every table as an asset +
 * classifies + flags PII, inside a tracked scan.
 */
export interface CatalogCrawlInput {
  connection: {
    vendor: 'postgres' | 'mssql' | 'oracle';
    host: string;
    port: number;
    database: string;
    user: string;
    password: string;
    ssl?: boolean;
    includeSchemas?: string[];
  };
  scanMode?: 'sketch' | 'full' | 'forensic';
  /** T2 - opt-in value-level checksum sampling ("second discovery layer"). */
  sampleValues?: boolean;
  /** Rows sampled per column when sampleValues=true (default 50). */
  sampleSize?: number;
}

export interface CatalogCrawlSummary {
  scanId: string;
  vendor: string;
  authority: string;
  schemas: string[];
  tableCount: number;
  columnCount: number;
  assetsCreated: number;
  assetsUpdated: number;
  columnsRegistered: number;
  piiFindings: number;
  /** T2 - columns classified by VALUE sampling. */
  valueFindings: number;
  /** T2 - value hits whose column name did NOT match (caught only by sampling). */
  valueOnlyFindings: number;
  status: string;
}

export async function catalogCrawl(input: CatalogCrawlInput): Promise<{ crawl: CatalogCrawlSummary }> {
  return apiFetch('/v1/discovery/catalog-crawl', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

// ── T2 control-plane→edge dispatch loop (2026-05-29) ─────────────────
// Operators enqueue scan jobs; edge pods (inside the customer VPC) claim
// the next queued job, scan locally, ingest findings, and mark complete.
export interface ScanJob {
  id: string;
  tenantId: string;
  status: 'queued' | 'claimed' | 'running' | 'completed' | 'failed';
  target: Record<string, unknown>;
  scanMode: string;
  sampleValues: boolean;
  claimedBy: string | null;
  claimedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  findingsCount: number | null;
  error: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function enqueueScanJob(input: {
  target: Record<string, unknown>;
  scanMode?: 'sketch' | 'full' | 'forensic';
  sampleValues?: boolean;
}): Promise<{ job: ScanJob }> {
  return apiFetch('/v1/discovery/scan-jobs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function listScanJobs(): Promise<{ jobs: ScanJob[] }> {
  return apiFetch('/v1/discovery/scan-jobs');
}

// ── T3 DSAR erasure execution (2026-05-29) ───────────────────────────
// Dry-run by default: preview the rows that WOULD be anonymised; pass
// dryRun:false to SET NULL the principal's PII columns. Hash-chained.
export interface DsarEraseTarget {
  schema: string;
  table: string;
  matchColumn: string;
  eraseColumns: string[];
}
export interface DsarEraseSummary {
  dryRun: boolean;
  principalIdentifierHash: string;
  previews?: Array<{ schema: string; table: string; matchColumn: string; eraseColumns: string[]; matchedRows: number }>;
  results?: Array<{ schema: string; table: string; matchColumn: string; eraseColumns: string[]; rowsAnonymized: number }>;
  totalRowsAffected: number;
}
export async function runDsarErasure(input: {
  connection: {
    vendor: 'postgres' | 'mssql' | 'oracle';
    host: string; port: number; database: string; user: string; password: string; ssl?: boolean;
  };
  principalValue: string;
  targets: DsarEraseTarget[];
  dryRun: boolean;
}): Promise<{ erasure: DsarEraseSummary }> {
  return apiFetch('/v1/discovery/dsar/erase', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

// ── Time-Travel Discovery (#2) - immutable posture snapshots + diff ───────

export interface DiscoverySnapshot {
  id: string;
  snapshotDate: string;
  assetCount: number;
  findingCountBySeverity: Record<string, number>;
  classificationByLabel: Record<string, number>;
  classificationBySourceType: Record<string, number>;
  createdAt: string;
}

export interface SnapshotDiff {
  from: { date: string; assetCount: number } | null;
  to: { date: string; assetCount: number };
  assetCountDelta: number;
  findingSeverityDelta: Record<string, number>;
  classificationLabelDelta: Record<string, { from: number; to: number; delta: number }>;
  addedLabels: string[];
  removedLabels: string[];
}

export async function takeDiscoverySnapshot(snapshotDate?: string): Promise<{ snapshot: DiscoverySnapshot }> {
  return apiFetch('/v1/discovery/snapshots', {
    method: 'POST',
    body: JSON.stringify(snapshotDate ? { snapshotDate } : {}),
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function listDiscoverySnapshots(): Promise<{ snapshots: DiscoverySnapshot[] }> {
  return apiFetch('/v1/discovery/snapshots');
}

export async function diffDiscoverySnapshots(from: string, to: string): Promise<{ diff: SnapshotDiff }> {
  return apiFetch(`/v1/discovery/snapshots/diff?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
}

// T2 (2026-05-29) - continuous rediscovery: snapshot now + diff vs prior,
// alerting on drift. Maps to POST /v1/discovery/rediscovery/run.
export interface RediscoveryResult {
  snapshotId: string;
  snapshotDate: string;
  assetCount: number;
  firstSnapshotToday: boolean;
  driftDetected: boolean;
  assetCountDelta: number;
  criticalDelta: number;
  addedLabels: string[];
  removedLabels: string[];
  alerted: boolean;
}

export async function runRediscovery(): Promise<{ rediscovery: RediscoveryResult }> {
  return apiFetch('/v1/discovery/rediscovery/run', {
    method: 'POST',
    body: JSON.stringify({}),
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Wire shape returned by GET /v1/discovery/section-9-report. Server-side
 * source: apps/api-controller/src/modules/discovery/service.ts:generateSection9Report
 * which delegates to repository.ts:aggregateSection9Counts.
 *
 * Note: the prior shape declared here (`bu_breakdown` / `totalMinorRecords`
 * / `generatedAt`) did NOT match the server - that mismatch crashed the
 * Section 9 page with "Cannot read properties of undefined (reading
 * 'length')" until corrected in the Phase-2-part-2 hardening pass.
 */
export interface Section9Report {
  totalMinorRecordsDetected: number;
  bySystem: Array<{ sourceType: string; minorRecordCount: number }>;
  byBusinessUnit: Array<{ businessUnitId: string | null; minorRecordCount: number }>;
  /**
   * Phase 6 close-out (2026-05-16) - per-pack §9 coverage view.
   * For each tenant-activated pack, surfaces how many minor-flagged
   * assets fall inside the pack's classifier vocab + a coverage score
   * + a green/amber/red tri-state.
   */
  byPack?: Array<{
    packId: string;
    packLabel: string;
    assetCount: number;
    coverageScore: number;
    // 'unknown' = the coverage ratio is undefined (0/0, no minor records
    // detected), which is not the same as zero coverage.
    coverageState: 'green' | 'amber' | 'red' | 'unknown';
  }>;
  openFindings: number;
  remediatedFindings: number;
  reportGeneratedAt: string;
  asOfDate: string;
  regulations: string[];
}

export async function getSection9Report(): Promise<Section9Report> {
  return apiFetch('/v1/discovery/section-9-report');
}

/**
 * Coverage = the real shape returned by GET /v1/discovery/coverage
 * (service.getCoverage). It returns scan/classification counts +
 * per-framework coverage. NOTE: the endpoint does NOT return `byCategory`,
 * `bySeverity`, or a `posture{dpdpa/rbi/sebi}` object - those were declared
 * here historically but never sent by the backend, so the dashboard derives
 * PII-by-type from /assets and severity from /findings instead. Kept optional
 * below only so any forward-compat consumer compiles.
 */
export interface DiscoveryCoverage {
  totalAssets: number;
  scannedAssets: number;
  classifiedAssets: number;
  byFramework: Array<{ framework: string; coverageScore: number; openFindings: number }>;
  byIndustryPack: Array<{ industryPackId: string; activated: boolean; contributingFindings: number }>;
  generatedAt: string;
  // Not currently returned by the endpoint - optional, do not rely on them.
  byCategory?: Record<string, number>;
  bySeverity?: Record<DiscoveryFinding['severity'], number>;
  posture?: { score: number; dpdpaScore: number; rbiScore: number; sebiScore: number };
}

export async function getDiscoveryCoverage(): Promise<DiscoveryCoverage> {
  return apiFetch('/v1/discovery/coverage');
}

/** Unified data-risk ranking - shape LOCKED by DSPM-RISK-SIGNALS-CONTRACT v1.0. */
export interface RiskRankedAsset {
  assetRef: string;
  name: string;
  score: number;
  band: string;
  topFactors: string[];
  findingCounts: { critical: number; high: number; medium: number; low: number };
  sourceType: string;
  orphan: boolean;
}
export interface DiscoveryRiskRanking {
  items: RiskRankedAsset[];
  bandSummary: Record<string, number>;
  total: number;
  limit: number;
  offset: number;
  generatedAt: string;
}
export async function getDiscoveryRiskRanking(): Promise<DiscoveryRiskRanking> {
  return apiFetch('/v1/discovery/risk-ranking');
}

/** Board-report summary - the canonical DSPM roll-up (also retrieved by Core
 *  to embed in its board report). Mirrors the api-controller BoardSummary. */
export interface DiscoveryBoardSummary {
  generatedAt: string;
  posture: { score: number; band: string };
  exposures: {
    criticalOpen: number;
    highOpen: number;
    topRisks: Array<{ name: string; assetRef: string; score: number; band: string; sourceType: string; orphan: boolean; critical: number; high: number }>;
  };
  classification: { totalAssets: number; classifiedAssets: number; classifiedPct: number; scannedPct: number };
  childrensData: { minorRecordsDetected: number; status: 'none' | 'detected' };
  compliance: Array<{ framework: string; coverageScore: number; openFindings: number }>;
}

export async function getDiscoveryBoardSummary(): Promise<DiscoveryBoardSummary> {
  return apiFetch('/v1/discovery/board-summary');
}

/** A connector config field as described by the registry. */
export interface DiscoveryConnectorField {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  enum?: string[];
}

/**
 * Real shape returned by GET /v1/discovery/connectors/registry. (The prior
 * type declared vendorKey/description/supportsScan which the endpoint never
 * sends - `kind`/`discoveryCapability`/`configFields` are the real fields, so
 * those UI bits were silently blank.)
 */
export interface DiscoveryConnector {
  kind: string;
  displayName: string;
  category: 'relational' | 'warehouse' | 'nosql' | 'object_storage' | 'm365' | 'workspace' | 'cache' | 'streaming' | 'saas';
  tier: number;
  authMethod: string;
  configFields: DiscoveryConnectorField[];
  discoveryCapability: { directScan: boolean; noDbAccessMode?: boolean; attestationSupported?: boolean };
  activityCapability?: Record<string, boolean>;
  industryHints?: string[];
  canonicalAssetPrefix?: string;
}

export async function listDiscoveryConnectorRegistry(): Promise<{ connectors: DiscoveryConnector[] }> {
  return apiFetch('/v1/discovery/connectors/registry');
}

// ── Phase 2 part 2 additions (Classifier / Lineage / Bootstrap /
//    HoneyPii / NoDbAccess / ReverseDsar export) ─────────────────

export interface ClassifierDetectorRow {
  /** How this built-in relates to the resolved industry pack. Advisory only. */
  relevance?: 'core' | 'not_typical' | 'general';
  name: string;
  label: string;
  tier: number;
  indiaSpecific: boolean;
  checksum: string | null;
  regulations: string[];
  /** Modular Rule Engine: per-tenant enabled state (absence = enabled). */
  enabled?: boolean;
}

export interface CustomDetectorRow {
  id: string;
  label: string;
  patternKind: string;
  pattern: string;
  checksum: string;
  enabled: boolean;
  confidenceBoost: number | string;
  packId?: string;
}

export interface ClassifierCatalogue {
  industryPacks?: ClassifierPackSection;
  detectors: ClassifierDetectorRow[];
  tier2Available: boolean;
  tier3Available: boolean;
  tier4Available: boolean;
  tier5Available: boolean;
  tenantOverlay: { customDetectors: ReadonlyArray<CustomDetectorRow> };
}

export interface PackDetectorRow {
  /** false when switched off for this unit. */
  enabled?: boolean;
  scope?: DetectorScope;
  label: string;
  pack: string;
  /** column-name vocabulary vs a value shape - they match different things. */
  match: 'column-name' | 'value-pattern';
  pattern: string;
  confidence: number;
}
export type DetectorScope = 'business_unit' | 'tenant';

export interface ClassifierPackSection {
  packIds: string[];
  /** Attached but shipping no content, so contributing nothing. */
  inertPackIds: string[];
  resolution: 'override' | 'inherited' | 'tenant_default';
  inheritedFromBuId: string | null;
  detectors: PackDetectorRow[];
}

/**
 * Scoped to a business unit. Tier 1 is identical everywhere; the industry packs
 * are what differ between a hospital unit and a banking one, so the catalogue
 * has to be asked per BU or the page cannot show the difference.
 */
export async function listClassifiers(buId?: string | null): Promise<ClassifierCatalogue> {
  return apiFetch(`/v1/discovery/classifiers${buId ? `?buId=${encodeURIComponent(buId)}` : ''}`);
}

// ── Modular Rule Engine - per-tenant detector enable/disable ──
export async function getDetectorConfig(): Promise<{ config: Record<string, boolean> }> {
  return apiFetch('/v1/discovery/detector-config');
}
export async function setDetectorEnabled(
  label: string,
  enabled: boolean,
  buId?: string | null,
): Promise<{ detectorLabel: string; enabled: boolean; businessUnitId: string | null }> {
  return apiFetch(
    `/v1/discovery/detector-config/${encodeURIComponent(label)}${buId ? `?buId=${encodeURIComponent(buId)}` : ''}`,
    { method: 'PUT', body: JSON.stringify({ enabled }) },
  );
}

/** Drop this unit's own decision so the label follows the tenant default. */
export async function clearDetectorOverride(
  label: string,
  buId: string,
): Promise<{ cleared: boolean }> {
  return apiFetch(
    `/v1/discovery/detector-config/${encodeURIComponent(label)}?buId=${encodeURIComponent(buId)}`,
    { method: 'DELETE' },
  );
}

export interface AccessGraphNode {
  id: string;
  kind: 'asset' | 'finding_category';
  label: string;
  sourceType?: string;
  countryCode?: string | null;
  count?: number;
}
export interface AccessGraphEdge {
  from: string;
  to: string;
  severity: string;
  findingId: string;
}
export interface AccessGraph {
  nodes: AccessGraphNode[];
  edges: AccessGraphEdge[];
  stats: {
    assetCount: number;
    categoryCount: number;
    edgeCount: number;
    rootHint: string | null;
    depthRequested: number;
  };
}

export async function getAccessGraph(params: { root?: string; depth?: number } = {}): Promise<AccessGraph> {
  const q = new URLSearchParams();
  if (params.root) q.set('root', params.root);
  if (params.depth) q.set('depth', String(params.depth));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/discovery/access-graph${suffix}`);
}

/**
 * Egress lineage: which jurisdictions sensitive data actually left towards.
 *
 * Separate from the access graph on purpose. The access graph answers "who can
 * REACH this data"; this answers "where did it GO". Neither substitutes for the
 * other, and merging them into one endpoint would make an empty result
 * ambiguous between the two questions.
 */
export interface EgressLineageNode {
  id: string;
  label: string;
  cat: 'app' | 'pipeline' | 'storage' | 'analytics' | 'cross-border' | 'export' | 'db';
  kind: 'asset' | 'destination';
  sev?: 'critical' | 'high' | 'medium' | 'low';
  pii?: string[];
  sourceType?: string;
  countryCode?: string | null;
  decisions?: number;
}
export interface EgressLineageEdge {
  source: string;
  target: string;
  decisions: number;
  verdicts: string[];
}
export interface EgressLineage {
  nodes: EgressLineageNode[];
  edges: EgressLineageEdge[];
  /** Enforced decisions whose source country could not be resolved. */
  unresolvedDecisions: number;
  /** Enforced decisions with a country but no finding to draw from. */
  unattributedDecisions: number;
  homeCountry: string;
  windowDays: number;
  coverage: {
    decisionsConsidered: number;
    decisionsPlaced: number;
    /** Always true: only non-allow decisions are persisted. */
    enforcedOnly: boolean;
  };
}

export async function getEgressLineage(
  params: { days?: number; homeCountry?: string } = {},
): Promise<EgressLineage> {
  const q = new URLSearchParams();
  if (params.days) q.set('days', String(params.days));
  if (params.homeCountry) q.set('homeCountry', params.homeCountry);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/discovery/egress-lineage${suffix}`);
}

export interface BootstrapLevel {
  level: number;
  title: string;
  complete: boolean;
  signal: string;
}
export interface BootstrapProgress {
  levels: BootstrapLevel[];
  summary: {
    completeCount: number;
    totalLevels: number;
    completionPct: number;
    assets: number;
    classifiedAssets: number;
    findings: number;
    criticalFindings: number;
    activeCanaries: number;
  };
}

export async function getBootstrapProgress(): Promise<BootstrapProgress> {
  return apiFetch('/v1/discovery/bootstrap-progress');
}

/**
 * Re-run a single bootstrap level. Wire shape matches
 * `apps/api-controller/src/modules/discovery/part2-handlers.ts:runBootstrapLevel`.
 * Levels 1 and 7 are actionable today (cloud-API discovery + scan);
 * levels 2-6 + 8 return `pending_connector` until the corresponding
 * connector is configured.
 */
export interface BootstrapRunResponse {
  level: number;
  accepted: boolean;
  status: 'queued' | 'use_connector_endpoint' | 'pending_connector';
  message: string;
  queuedAt: string;
}

export async function runBootstrapLevel(
  level: number,
  idempotencyKey?: string,
): Promise<BootstrapRunResponse> {
  return apiFetch(`/v1/discovery/bootstrap/run/${level}`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Saved bootstrap connectors (Phase 6 UI) ──────────────────────────
// Persist a connector config once (secret encrypted server-side), then
// list / delete / run it. Backend: modules/discovery/bootstrap/connector-store.ts

export interface StoredBootstrapConnector {
  id: string;
  level: number;
  kind: 'servicenow_cmdb' | 'scim' | 'generic_rest';
  displayName: string;
  config: Record<string, unknown>;
  hasSecret: boolean;
  status: string;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunSummary: Record<string, unknown> | null;
  createdAt: string;
}

export async function listBootstrapConnectors(): Promise<{ connectors: StoredBootstrapConnector[] }> {
  return apiFetch('/v1/discovery/bootstrap/connectors');
}

export interface CreateBootstrapConnectorInput {
  level: number;
  kind: 'servicenow_cmdb' | 'scim' | 'generic_rest';
  displayName: string;
  config: Record<string, unknown>;
  secret?: Record<string, unknown>;
}

export async function createBootstrapConnector(
  input: CreateBootstrapConnectorInput,
): Promise<{ connector: StoredBootstrapConnector }> {
  return apiFetch('/v1/discovery/bootstrap/connectors', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function deleteBootstrapConnector(id: string): Promise<{ deleted: boolean }> {
  return apiFetch(`/v1/discovery/bootstrap/connectors/${id}`, { method: 'DELETE' });
}

export interface RunBootstrapConnectorResponse {
  status: 'ok' | 'error';
  result: { level: number; levelTag: string; received: number; created: number; updated: number; skipped: number } | null;
  errors: string[];
}

export async function runBootstrapConnector(id: string): Promise<RunBootstrapConnectorResponse> {
  return apiFetch(`/v1/discovery/bootstrap/connectors/${id}/run`, {
    method: 'POST',
    idempotencyKey: newIdempotencyKey(),
  });
}

export interface HoneyCanary {
  id: string;
  canaryType: 'aadhaar' | 'pan' | 'email' | 'phone' | 'identity_bundle';
  plantedInAssetId: string;
  plantedInColumnId: string | null;
  plantedValueHash: string;
  plantedAt: string;
  triggeredAt: string | null;
  triggeredByActor: string | null;
  triggeredViaQueryHash: string | null;
  status: 'active' | 'retired' | 'triggered';
}

export async function listHoneyCanaries(): Promise<{ canaries: HoneyCanary[] }> {
  return apiFetch('/v1/discovery/honey-pii/canaries');
}

export async function plantHoneyCanary(
  input: {
    canaryType: HoneyCanary['canaryType'];
    plantedInAssetId: string;
    plantedInColumnId?: string;
    plantedValueHash: string;
  },
  idempotencyKey?: string,
): Promise<{ canary: HoneyCanary }> {
  return apiFetch('/v1/discovery/honey-pii/canaries', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function retireHoneyCanary(id: string): Promise<{ canary: HoneyCanary }> {
  return apiFetch(`/v1/discovery/honey-pii/canaries/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Trigger a canary - simulates an unauthorised access to the planted
 * row. Emits a `honey_pii_read` finding (severity = critical) and
 * marks the canary as `triggered`. Wire shape matches
 * `apps/api-controller/src/modules/discovery/part2-handlers.ts:triggerHoneyCanary`.
 */
export interface CanaryTriggerResponse {
  canary: HoneyCanary;
  finding: {
    id: string;
    findingType: 'honey_pii_read';
    severity: 'critical';
    status: string;
    title: string;
    createdAt: string;
  };
}

export async function triggerHoneyCanary(
  id: string,
  idempotencyKey?: string,
): Promise<CanaryTriggerResponse> {
  return apiFetch(`/v1/discovery/honey-pii/canaries/${encodeURIComponent(id)}/trigger`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export interface EvidenceSourceRow {
  sourceType: string;
  displayName: string;
  category: string;
  assetCount: number;
  findingCount: number;
  evidenceMode: 'direct_query' | 'graph_api';
}

export async function listEvidenceSources(): Promise<{ sources: EvidenceSourceRow[] }> {
  return apiFetch('/v1/discovery/evidence-sources');
}

/**
 * No-Direct-DB-Access - schema-export ingestion (Day 3, 2026-05-22).
 *
 * Customer DBA pastes/uploads schema-only DDL; backend parses CREATE
 * TABLE statements, classifies column NAMES against the Indian-context
 * PII vocabulary, and persists findings - never touches the live DB.
 * Unblocks BFSI deals where DBAs share schemas but never credentials.
 *
 * POST /v1/discovery/evidence/schema-export
 */
export interface SchemaExportRequest {
  assetExternalRef: string;
  ddl: string;
  uploadLabel?: string;
}

export interface SchemaExportResponse {
  assetExternalRef: string;
  tablesParsed: number;
  columnsParsed: number;
  findings: number;
  accepted: number;
  rejected?: number;
  rejections?: ReadonlyArray<{ findingType?: string; reason: string }>;
  message?: string;
}

export async function ingestSchemaExport(body: SchemaExportRequest): Promise<SchemaExportResponse> {
  return apiFetch('/v1/discovery/evidence/schema-export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface DsarSignature {
  algorithm: 'HMAC-SHA256';
  keyId: string;
  signedAt: string;
  payloadSha256: string;
  signature: string;
}

export interface ReverseDsarExport {
  exportedAt: string;
  tenantId: string;
  principalIdentifier: string;
  format: 'json' | 'pdf';
  body: ReverseDsarResponse;
  integrityNote: string;
  /** Detached HMAC-SHA256 signature over the canonical JSON of `body`. */
  signature?: DsarSignature;
  deferred?: string;
}

// 'json' (default) -> signed JSON envelope; 'pdf' -> raw application/pdf bytes.
export async function exportReverseDsar(
  principalIdentifier: string,
  format?: 'json',
): Promise<ReverseDsarExport>;
export async function exportReverseDsar(
  principalIdentifier: string,
  format: 'pdf',
): Promise<Blob>;
export async function exportReverseDsar(
  principalIdentifier: string,
  format: 'json' | 'pdf' = 'json',
): Promise<ReverseDsarExport | Blob> {
  const path = `/v1/discovery/reverse-dsar/${encodeURIComponent(principalIdentifier)}/export`;
  if (format === 'pdf') {
    const token = getAuthToken();
    const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/pdf',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ format }),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new ApiError(res.status, txt || `${res.status} ${res.statusText}`, txt);
    }
    return res.blob();
  }
  return apiFetch<ReverseDsarExport>(path, {
    method: 'POST',
    body: JSON.stringify({ format }),
  });
}

// ── Activity (additional routes) ───────────────────────────────────────

export interface ActivityEvent {
  id: string;
  occurredAt: string;
  actorUserId: string;
  actorRole: string | null;
  application: string | null;
  source: string;
  statementHash: string;
  tablesTouched: string[];
  columnsReturned: string[];
  rowCount: number | null;
  bytesReturned: number | null;
  decision: 'allow' | 'mask' | 'step_up' | 'block' | null;
  requestId: string | null;
}

/**
 * Server returns ClickHouse rows in snake_case (the canonical
 * activity_events column names). Wrapper transforms to the camelCase
 * shape this codebase uses everywhere. Same pattern as the
 * `getDiscoveryAsset` envelope unwrap.
 */
interface ActivityEventRowSnake {
  tenant_id: string;
  asset_id: string;
  occurred_at: string;
  actor_user_id: string;
  actor_role: string;
  application: string;
  statement_hash: string;
  statement_type: string;
  tables_touched: string[];
  columns_returned: string[];
  rows_returned: number;
  duration_ms: number;
  pii_categories_touched: string[];
  decision: string;
  decision_explanation: string;
  severity: string;
}

function mapActivityEvent(row: ActivityEventRowSnake, idx: number): ActivityEvent {
  // The ClickHouse row has no `id` column - use statement_hash + index
  // as a stable React-key surrogate. Decision is mapped to the client's
  // discriminated union; unknown values pass through to `null`.
  const decision = (['allow', 'mask', 'step_up', 'block'] as const).includes(row.decision as never)
    ? (row.decision as ActivityEvent['decision'])
    : null;
  return {
    id: `${row.statement_hash}-${idx}`,
    occurredAt: row.occurred_at,
    actorUserId: row.actor_user_id,
    actorRole: row.actor_role ?? null,
    application: row.application ?? null,
    source: 'clickhouse',
    statementHash: row.statement_hash,
    tablesTouched: row.tables_touched ?? [],
    columnsReturned: row.columns_returned ?? [],
    rowCount: row.rows_returned ?? null,
    bytesReturned: null,
    decision,
    requestId: null,
  };
}

export async function listActivityEvents(params: {
  since?: string;
  actorUserId?: string;
  limit?: number;
} = {}): Promise<{ events: ActivityEvent[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (params.since) q.set('since', params.since);
  if (params.actorUserId) q.set('actorUserId', params.actorUserId);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  const raw = await apiFetch<{ events: ActivityEventRowSnake[]; nextCursor?: string | null }>(
    `/v1/activity/events${suffix}`,
  );
  return {
    events: (raw.events ?? []).map(mapActivityEvent),
    nextCursor: raw.nextCursor ?? null,
  };
}

/**
 * Activity baseline row. Wire shape matches the server's
 * `actor_baselines` table at
 * apps/api-controller/src/shared/database/schema/activity.ts:60 - NOT
 * the earlier guessed shape. Same drift class as Section9 + DiscoveryAsset
 * + scanMode bugs caught in Phase-2 hardening.
 */
export interface ActivityBaseline {
  id: string;
  tenantId: string;
  actorId: string;
  baselineMetric: string;
  baselineValue: string; // numeric → string over the wire
  baselineStddev: string | null;
  sampleSize: number;
  extra: Record<string, unknown>;
  computedAt: string;
}

export async function listActivityBaselines(params: { actorId?: string } = {}): Promise<{
  baselines: ActivityBaseline[];
}> {
  const q = new URLSearchParams();
  if (params.actorId) q.set('actorId', params.actorId);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/activity/baselines${suffix}`);
}

/**
 * Application sonar signature row. Mirrors
 * `application_sonar_signatures` at schema/activity.ts:89.
 */
export interface SonarSignature {
  id: string;
  tenantId: string;
  applicationName: string;
  signatureDistribution: Record<string, Record<string, number>>;
  sampleSize: number;
  lastKlDivergence: string | null;
  computedAt: string;
}

export async function listSonarSignatures(): Promise<{ signatures: SonarSignature[] }> {
  return apiFetch('/v1/activity/sonar');
}

/**
 * Sonar persisted finding (low-and-slow exfil, etc.). Subset of
 * fields needed by the page; backend may include more.
 */
export interface SonarFinding {
  id: string;
  tenantId: string;
  applicationName: string;
  pattern: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'acknowledged' | 'resolved' | 'false_positive';
  detectedAt: string;
  rationale?: string;
  metadata?: Record<string, unknown>;
}

export async function listSonarFindings(params: { status?: string; limit?: number } = {}): Promise<{
  findings: SonarFinding[];
}> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/activity/sonar/findings${suffix}`);
}

/**
 * Breach readiness payload. The server returns ClickHouse-derived
 * counters merged with an `rbi6HourClock` block; the response is
 * intentionally permissive because the engine emits richer metadata
 * on real breaches. We surface only the fields the page renders.
 */
export interface BreachReadiness {
  generatedAt: string;
  // ClickHouse-derived fields (engine emits more; client renders these).
  recentBreaches?: number;
  averageDetectionMinutes?: number | null;
  averageContainmentMinutes?: number | null;
  rbi6HourClock: {
    minutesSinceLastDetection: number | null;
    hoursRemaining: number | null;
  };
  // Optional richer fields the engine may populate. The page should
  // render whatever is present without exploding on absence.
  openIncidents?: Array<{
    id: string;
    severity: string;
    detectedAt: string;
    dpdpa72HoursRemaining?: number | null;
    gdpr72HoursRemaining?: number | null;
    rbi6HoursRemaining?: number | null;
  }>;
  recentClosed?: Array<{ id: string; closedAt: string; outcome: string }>;
}

export async function getBreachReadiness(): Promise<BreachReadiness> {
  return apiFetch('/v1/activity/breach-readiness');
}

/**
 * Early-warning indicator row. Wire shape matches the server's
 * `early_warning_indicators` table at schema/activity.ts:116. Server
 * wraps the list as `{ indicators: [...] }` (not `{ events: [...] }`).
 */
export interface EarlyWarningIndicator {
  id: string;
  tenantId: string;
  indicatorType:
    | 'reconnaissance_burst'
    | 'off_pattern_query'
    | 'credential_creation'
    | 'audit_log_disable_attempt'
    | 'schema_mapping'
    | 'cold_table_burst';
  actorId: string | null;
  assetId: string | null;
  firedAt: string;
  resolvedAt: string | null;
  severity: 'critical' | 'high' | 'medium' | 'low';
  metadata: Record<string, unknown>;
  relatedEventCount: number;
  createdAt: string;
}

export async function listEarlyWarning(): Promise<{ indicators: EarlyWarningIndicator[] }> {
  return apiFetch('/v1/activity/early-warning');
}

// Deprecated alias for any code still referencing the old name. Remove
// after grep returns zero callers.
export const listEarlyWarnings = listEarlyWarning;

/**
 * Phase 3 Part 2 - unified anomaly stream. Aggregates open UEBA + Sonar
 * findings and live EarlyWarning indicators into one severity-sorted
 * list. Wire shape: `apps/api-controller/src/modules/activity/service.ts:listAnomalies`.
 */
export interface AnomalyRow {
  id: string;
  engine: 'ueba' | 'sonar' | 'early_warning';
  severity: 'critical' | 'high' | 'medium' | 'low';
  actor: string | null;
  detectedAt: string;
  title: string;
  summary: string;
}

export async function listAnomalies(): Promise<{ anomalies: AnomalyRow[] }> {
  return apiFetch('/v1/activity/anomalies');
}

// ── Detection (additional routes - rules CRUD, playbook run, break-glass) ─

/**
 * DDR rule row. Wire shape matches `$inferSelect` on `ddr_rules`
 * (schema/detection.ts:28). Previous client guess used `category`,
 * `body`, `regoBody`, `source`, `createdBy` - none of which exist
 * on the server table. Aligned for Phase 4 wiring.
 *
 * Derived UI fields:
 *   `category` is recomputed in the page (one-of 'discovery' /
 *   'activity' / 'detection' / 'frontier') from `ruleType` or
 *   industry-pack metadata; the server doesn't carry a literal
 *   category column.
 *   `source` is `built_in === true ? 'built_in' : 'tenant'`.
 */
export interface DetectionRule {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  ruleType: 'discovery_finding' | 'activity_event' | 'combined' | string;
  severity: 'critical' | 'high' | 'medium' | 'low' | string;
  regoSource: string;
  status: 'draft' | 'dry_run' | 'published' | 'active' | 'paused' | 'pending_approval' | 'deprecated' | string;
  builtIn: boolean;
  industryPackId: string | null;
  createdByUserId: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export async function listDetectionRules(): Promise<{ rules: DetectionRule[] }> {
  return apiFetch('/v1/detection/rules');
}

export async function getDetectionRule(id: string): Promise<DetectionRule> {
  return apiFetch(`/v1/detection/rules/${encodeURIComponent(id)}`);
}

/**
 * New-rule input. Matches `CreateRuleSchema` server-side. The server
 * stamps tenant_id from RLS context and defaults status='draft'.
 */
export interface CreateDetectionRuleInput {
  name: string;
  description?: string | null;
  ruleType: 'discovery_finding' | 'activity_event' | 'combined';
  severity: 'critical' | 'high' | 'medium' | 'low';
  regoSource: string;
}

export async function createDetectionRule(
  input: CreateDetectionRuleInput,
  idempotencyKey?: string,
): Promise<DetectionRule> {
  return apiFetch('/v1/detection/rules', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export interface DryRunResult {
  ruleId: string;
  wouldMatchCount: number;
  sampleMatches: Array<{ entityId: string; entityType: string; matchedAt: string; preview: string }>;
}

export async function dryRunDetectionRule(
  input: { ruleBody: Record<string, unknown>; sinceDays?: number },
): Promise<DryRunResult> {
  return apiFetch('/v1/detection/rules/dry-run', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function publishDetectionRule(
  id: string,
  approverNote?: string,
  idempotencyKey?: string,
): Promise<DetectionRule> {
  return apiFetch(`/v1/detection/rules/${encodeURIComponent(id)}/publish`, {
    method: 'POST',
    body: JSON.stringify({ approverNote }),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function patchDetectionRuleStatus(
  id: string,
  status: 'active' | 'paused' | 'deprecated',
): Promise<DetectionRule> {
  return apiFetch(`/v1/detection/rules/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

/**
 * DDR playbook row. Wire shape matches Drizzle `$inferSelect` on
 * `ddr_playbooks` (schema/detection.ts:89). Previous client guess
 * used `twoPartyApproval` / `actions` / `category` - server has
 * `requiresTwoPersonRule` / `steps` / `builtIn`. Realigned for
 * Phase 4 wiring.
 */
export interface DetectionPlaybook {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  steps: Array<{ kind: string; description: string }>;
  requiresTwoPersonRule: boolean;
  builtIn: boolean;
  industryPackId: string | null;
  workflowId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listDetectionPlaybooks(): Promise<{ playbooks: DetectionPlaybook[] }> {
  return apiFetch('/v1/detection/playbooks');
}

export async function runDetectionPlaybook(
  name: string,
  input: { context: Record<string, unknown> },
  idempotencyKey?: string,
): Promise<{
  playbookName: string;
  runId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  actionsExecuted: Array<{ kind: string; outcome: string }>;
  requestId: string;
}> {
  return apiFetch(`/v1/detection/playbooks/${encodeURIComponent(name)}/run`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * DDR break-glass session row. Wire shape matches Drizzle
 * `$inferSelect` on `ddr_break_glass_sessions` (schema/detection.ts:133).
 * The server does NOT carry a literal `status` column - the listing
 * service derives status from (approved_at, expires_at, revoked_at)
 * in the page logic. We keep an optional `status` here so handlers
 * that compute it server-side still typecheck.
 */
export interface BreakGlassSession {
  id: string;
  tenantId: string;
  userId: string;
  justification: string;
  approvedByUserId: string | null;
  approvedAt: string | null;
  expiresAt: string;
  revokedAt: string | null;
  followUpSummary: string | null;
  followUpFiledAt: string | null;
  createdAt: string;
  // Derived in the UI (or, in future, by the listing service): one of
  // 'pending_approval' | 'approved' | 'active' | 'expired' | 'closed'.
  status?: 'pending_approval' | 'approved' | 'active' | 'expired' | 'closed';
}

export async function listBreakGlassSessions(): Promise<{ sessions: BreakGlassSession[] }> {
  return apiFetch('/v1/detection/break-glass');
}

export async function activateBreakGlass(
  input: { justification: string; requestedPermissions: string[]; durationMinutes: number },
  idempotencyKey?: string,
): Promise<BreakGlassSession> {
  return apiFetch('/v1/detection/break-glass/activate', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function approveBreakGlass(
  id: string,
  input: { approverNote: string },
  idempotencyKey?: string,
): Promise<BreakGlassSession> {
  return apiFetch(`/v1/detection/break-glass/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function fileBreakGlassSummary(
  id: string,
  input: { summary: string; actionsTaken: string[] },
): Promise<BreakGlassSession> {
  return apiFetch(`/v1/detection/break-glass/${encodeURIComponent(id)}/file-summary`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * DDR SIEM adapter row. Wire shape matches `$inferSelect` on
 * `ddr_siem_adapters` (schema/detection.ts:158). Previous client
 * guess used `vendorKey` / `displayName` / `authMethod` /
 * `configKmsRef` - none of which exist on the server table.
 */
export interface SiemAdapter {
  id: string;
  tenantId: string;
  vendor: 'splunk' | 'sentinel' | 'xsoar' | 'servicenow' | 'unieye' | 'qradar' | 'elastic' | 'generic_webhook' | string;
  configuration: Record<string, unknown>;
  status: 'active' | 'paused' | 'failed' | string;
  lastEventAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  healthScore: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiemAdapterDescriptor {
  id: string;
  displayName: string;
  vendor: string;
  protocol: string;
  authMethod: string;
  maturity: string;
}

export async function listSiemAdapterRegistry(): Promise<{
  adapters: SiemAdapterDescriptor[];
}> {
  return apiFetch('/v1/detection/siem-adapters/registry');
}

export async function listSiemAdapters(): Promise<{ adapters: SiemAdapter[] }> {
  return apiFetch('/v1/detection/siem-adapters');
}

export async function createSiemAdapter(
  input: { vendor: string; configuration: Record<string, unknown> },
  idempotencyKey?: string,
): Promise<SiemAdapter> {
  return apiFetch('/v1/detection/siem-adapters', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export interface DecisionDetail extends Decision {
  rule_trace: Array<{ rule_name: string; matched: boolean; explanation: string }>;
  consent_state?: { granted: boolean; purpose?: string; expiresAt?: string };
  cross_border_state?: { allowed: boolean; reason?: string };
  section9_state?: { applicable: boolean; guardianConsentVerified?: boolean };
  ueba_state?: { zScore: number; baseline: string };
}

export async function getDecisionDetail(reqId: string): Promise<DecisionDetail> {
  const wrapped = await apiFetch<{ decision: DecisionDetail }>(
    `/v1/detection/decisions/${encodeURIComponent(reqId)}`,
  );
  // Server wraps as { decision: ... } per handlers.ts:getDecisionByReqId.
  return wrapped.decision;
}

/**
 * Phase 4 - Why-Blocked composer. Returns the full reason chain that
 * the Decisions detail drawer renders: rule, redundancy votes, plus
 * per-source verdicts (consent, cross-border, section-9, UEBA).
 */
export interface WhyBlockedResponse {
  requestId: string;
  decision: string;
  decidedAt: string;
  ruleName: string | null;
  ruleSeverity: string | null;
  explanation: string;
  redundancy: Array<{ engine: string; vote: string; confidence: number }>;
  reasons: Array<{ source: string; verdict: string; detail: string }>;
}

export async function getWhyBlocked(reqId: string): Promise<WhyBlockedResponse> {
  return apiFetch(`/v1/detection/why-blocked/${encodeURIComponent(reqId)}`);
}

export async function acknowledgeDecision(
  reqId: string,
  idempotencyKey?: string,
): Promise<{ ok: boolean; decisionId: string; acknowledgedBy: string }> {
  return apiFetch(`/v1/detection/decisions/${encodeURIComponent(reqId)}/acknowledge`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function suppressDecision(
  reqId: string,
  input: { reason: string },
  idempotencyKey?: string,
): Promise<{ ok: boolean; decisionId: string; suppressedBy: string }> {
  return apiFetch(`/v1/detection/decisions/${encodeURIComponent(reqId)}/suppress`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function killSessionForDecision(
  reqId: string,
  idempotencyKey?: string,
): Promise<{ ok: boolean; decisionId: string; killedBy: string; note: string; at: string }> {
  return apiFetch(`/v1/detection/decisions/${encodeURIComponent(reqId)}/kill-session`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function testSiemAdapter(
  adapterId: string,
  idempotencyKey?: string,
): Promise<{
  ok: boolean;
  adapterId: string;
  vendor: string;
  sentAt: string;
  payload: Record<string, unknown>;
}> {
  return apiFetch(`/v1/detection/siem-adapters/${encodeURIComponent(adapterId)}/test`, {
    method: 'POST',
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * P1-6 (2026-05-26) - GET /v1/detection/siem-adapters/:id/dispatches
 * returns in-memory ring buffer of recent test dispatches.
 */
export interface SiemDispatchHistoryEntry {
  ok: boolean;
  adapterId: string;
  tenantId: string;
  vendor: string;
  sentAt: string;
  errorMessage?: string;
}
export async function getSiemDispatchHistory(
  adapterId: string,
  limit: number = 10,
): Promise<{ entries: SiemDispatchHistoryEntry[]; cap: number }> {
  return apiFetch(
    `/v1/detection/siem-adapters/${encodeURIComponent(adapterId)}/dispatches?limit=${limit}`,
  );
}

// ── DDR enforcement contracts (T1.2, 2026-05-29) ─────────────────
//
// These four clients were drifted from the live Zod schemas in
// apps/api-controller/src/modules/detection/schemas.ts - they sent the
// wrong request fields (strict schemas → 400) and typed the wrong
// response. Re-aligned to the deployed handlers, which are the source
// of truth.

export type DbVendor = 'postgres' | 'mysql' | 'mssql' | 'oracle' | 'mongodb';

// POST /v1/detection/enforcement/synthetic-error
// schema: SyntheticErrorRequestSchema → generateSyntheticError() : SyntheticError
export interface SyntheticErrorInput {
  /** Request ID from a block decision - must match /^BLK-[A-Z0-9]{8,}$/. */
  requestId: string;
  vendor: DbVendor;
  tableName?: string;
  subtype?: 'permission' | 'not_found' | 'auth';
}
export interface SyntheticErrorResult {
  code: string;
  errorNumber: number;
  message: string;
  severity: string;
}
export async function postSyntheticError(
  input: SyntheticErrorInput,
): Promise<SyntheticErrorResult> {
  return apiFetch<SyntheticErrorResult>('/v1/detection/enforcement/synthetic-error', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// POST /v1/detection/enforcement/breach-pre-fill
// schema: BreachPreFillSchema → buildBreachPreFill() : BreachPreFillResult
// NOTE: server-side this builder is also auto-invoked by the
// `breach_console_pre_fill` auto-fire playbook on every block/alert
// decision (see detection/service.ts). This client is the manual
// equivalent - the full breach-console surface is a Tier-3 item.
export interface BreachPreFillInput {
  decisionId: string;
  requestId: string;
  breachWindowStart: string;
  breachWindowEnd: string;
  affectedRecordCount: number;
  dataCategories: string[];
  affectedPrincipalsCount: number;
  affectedPrincipalIds?: string[];
  timelineEvents: Array<{
    occurredAt: string;
    eventType: string;
    actorUserId: string;
    statementHash: string;
  }>;
  tenantName: string;
  tenantContactDpoEmail: string;
  recommendedActions: string[];
  industryAnchor: string;
  recommendedSeverity: 'critical' | 'high' | 'medium' | 'low';
}
export interface BreachPreFillResult {
  breachIncident: {
    tenant_id: string;
    detected_at: string;
    detection_source: string;
    affected_record_count: number;
    data_categories: ReadonlyArray<string>;
    affected_principals_count: number;
    affected_principal_ids: ReadonlyArray<string>;
    breach_window_start: string;
    breach_window_end: string;
    timeline_events: ReadonlyArray<unknown>;
    recommended_severity: string;
    recommended_actions: ReadonlyArray<string>;
    auto_filled_from_dam_event_id: string;
    request_id: string;
  };
  draftDpbNotification: string;
  draftPrincipalNotification: string;
  clocks: {
    rbi6Hour: { hoursElapsed: number; hoursRemaining: number };
    dpdpa72Hour: { hoursElapsed: number; hoursRemaining: number };
    gdpr72Hour: { hoursElapsed: number; hoursRemaining: number };
    hipaa60Day: { daysElapsed: number; daysRemaining: number };
  };
}
export async function pushBreachPreFill(
  input: BreachPreFillInput,
  idempotencyKey?: string,
): Promise<BreachPreFillResult> {
  return apiFetch<BreachPreFillResult>('/v1/detection/enforcement/breach-pre-fill', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Activity (ingest, for synthetic demo data + Phase 3 seed) ─────────

export async function postActivityEvent(
  input: Omit<ActivityEvent, 'id'>,
  idempotencyKey?: string,
): Promise<{ id: string; received: true }> {
  return apiFetch('/v1/activity/events', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Activity Capture Configs (DAM Sources - Tier A1, 2026-05-26) ─────
//
// Wire shape mirrors apps/api-controller/src/modules/activity/capture-configs.ts:CaptureConfigDto
// and CaptureSourcesSummary. Backend ships 7 routes (list / summary /
// get / upsert / patch / soft-delete / self-register). The /sources
// page builds on this surface.

export type ActivityCaptureMode =
  | 'native_audit'
  | 'cloud_stream'
  | 'proxy'
  | 'ebpf'
  | 'network';

export type ActivityCaptureStatus = 'active' | 'paused' | 'deleted' | 'error';

export interface ActivityCaptureConfig {
  id: string;
  tenantId: string;
  dataSourceConnectorInstanceId: string;
  captureMode: ActivityCaptureMode;
  configuration: Record<string, unknown>;
  status: ActivityCaptureStatus | string;
  healthScore: number | null;
  lastEventAt: string | null;
  eventsPerMinuteRecent: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityCaptureSourcesSummary {
  totalActive: number;
  totalPaused: number;
  totalError: number;
  byCaptureMode: Array<{ captureMode: string; count: number }>;
  avgHealthScore: number | null;
}

export async function listActivityCaptureConfigs(
  params: { status?: ActivityCaptureStatus | 'any' } = {},
): Promise<{ configs: ActivityCaptureConfig[]; count: number }> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/activity/capture-configs${suffix}`);
}

export async function getActivityCaptureSourcesSummary(): Promise<{
  summary: ActivityCaptureSourcesSummary;
}> {
  return apiFetch('/v1/activity/capture-configs/summary');
}

export async function getActivityCaptureConfig(
  id: string,
): Promise<{ config: ActivityCaptureConfig }> {
  return apiFetch(`/v1/activity/capture-configs/${encodeURIComponent(id)}`);
}

export interface UpsertActivityCaptureConfigInput {
  dataSourceConnectorInstanceId: string;
  captureMode: ActivityCaptureMode;
  configuration?: Record<string, unknown>;
  status?: ActivityCaptureStatus;
  healthScore?: number;
}

export async function upsertActivityCaptureConfig(
  input: UpsertActivityCaptureConfigInput,
  idempotencyKey?: string,
): Promise<{ config: ActivityCaptureConfig }> {
  return apiFetch('/v1/activity/capture-configs', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export interface PatchActivityCaptureConfigInput {
  status?: ActivityCaptureStatus;
  healthScore?: number;
  configuration?: Record<string, unknown>;
  eventsPerMinuteRecent?: number;
  lastEventAt?: string;
}

export async function patchActivityCaptureConfig(
  id: string,
  patch: PatchActivityCaptureConfigInput,
): Promise<{ config: ActivityCaptureConfig }> {
  return apiFetch(`/v1/activity/capture-configs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteActivityCaptureConfig(id: string): Promise<void> {
  return apiFetch(`/v1/activity/capture-configs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * P1-4 (2026-05-26) - run the 3-check verdict for a capture config.
 * Backend at apps/api-controller/src/modules/activity/capture-configs.ts:
 * testCaptureConfig. Returns ok + per-check status + summary line.
 */
export interface ActivityCaptureConfigTestCheck {
  id: 'status' | 'health' | 'freshness';
  ok: boolean;
  detail: string;
}
export interface ActivityCaptureConfigTestResult {
  ok: boolean;
  checks: ActivityCaptureConfigTestCheck[];
  summary: string;
  testedAt: string;
}
export async function testActivityCaptureConfig(
  id: string,
): Promise<ActivityCaptureConfigTestResult> {
  return apiFetch(`/v1/activity/capture-configs/${encodeURIComponent(id)}/test`, {
    method: 'POST',
  });
}

// ── Phase 1B - shared Data Source registry ────────────────────────────
// A data source = one connector_instance consumed by every licensed module
// (Discovery scans it, DAM captures it, DDR routes to it). Configure once.
export const DATA_SOURCE_CATEGORIES = [
  'dspm_database',
  'dspm_warehouse',
  'dspm_storage',
  'dam_native_audit',
  'dam_cloud_stream',
  'dam_proxy',
  'ddr_target',
] as const;
export type DataSourceCategory = (typeof DATA_SOURCE_CATEGORIES)[number];

export interface DataSource {
  id: string;
  displayName: string;
  category: DataSourceCategory | string;
  vendor: string | null;
  status: string;
  healthStatus: string | null;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export async function listDataSources(): Promise<{ sources: DataSource[]; count: number }> {
  return apiFetch('/v1/data-sources');
}

export interface CreateDataSourceInput {
  category: DataSourceCategory;
  displayName: string;
  config?: Record<string, unknown>;
}

export async function createDataSource(
  input: CreateDataSourceInput,
  idempotencyKey?: string,
): Promise<{ source: DataSource }> {
  return apiFetch('/v1/data-sources', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function deleteDataSource(id: string): Promise<void> {
  await apiFetch(`/v1/data-sources/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Phase 0 - config-key module-relevance matrix ──────────────────────
export type ConfigModuleKey = 'discovery' | 'activity' | 'detection' | 'compliance';
export type ConfigRelevance = 'required' | 'optional' | 'n/a';
export interface ConfigCatalogKey {
  key: string;
  label: string;
  href: string;
  group: string;
  relevance: Record<ConfigModuleKey, ConfigRelevance>;
  source: 'local' | 'federates' | 'adapter';
  inheritPolicy: 'silent' | 'accept-gate' | 'n/a';
  blurb: string;
}
export type ConfigReadiness = 'yes' | 'no' | 'unknown';
export async function getConfigCatalog(): Promise<{
  catalog: ConfigCatalogKey[];
  modules: ConfigModuleKey[];
  byModule: Record<ConfigModuleKey, { required: string[]; optional: string[] }>;
  readiness: Record<string, ConfigReadiness>;
}> {
  return apiFetch('/v1/config-catalog');
}

// ── Foundational / Industry Packs (Phase 6) ───────────────────────────

export type IndustryPackAnchor =
  | 'bfsi_banking'
  | 'bfsi_capital_markets'
  | 'bfsi_insurance'
  | 'bfsi_fintech'
  | 'healthcare'
  | 'pharma'
  | 'insurance'
  | 'edtech'
  | 'retail_ecommerce'
  | 'telecom'
  | 'government_psu'
  | 'manufacturing'
  | 'hospitality_travel'
  | 'saas_tech';

export type IndustryPackSourceKind = 'local' | 'foctta_core';

export interface IndustryPackActivationState {
  packId: IndustryPackAnchor;
  activated: boolean;
  activatedAt: string | null;
  deactivatedAt: string | null;
  source: IndustryPackSourceKind | null;
  externalRef: string | null;
  activatedByUserId: string | null;
  lastSyncedAt: string | null;
}

export interface IndustryPackSummary {
  anchor: IndustryPackAnchor;
  sourcePackId?: string;
  name: string;
  kind?: 'parent' | 'overlay' | 'bundle';
  geographies: string[];
  regulatoryClauses: string[];
  tenantActivation: IndustryPackActivationState;
  perBuActiveCount: number;
}

export interface IndustryPackDetail {
  summary: IndustryPackSummary;
  discoveryAdditions: {
    classifierOverlays: unknown[];
    expectedSystems: unknown[];
    ropaTemplates: unknown[];
    toxicCombinations: unknown[];
  };
  perBuActivations: Array<{
    businessUnitId: string;
    businessUnitName: string;
    businessUnitSlug: string;
    activatedAt: string;
    source: IndustryPackSourceKind;
    externalRef: string | null;
  }>;
}

export interface BusinessUnitPackActivationState {
  businessUnitId: string;
  packId: IndustryPackAnchor;
  activated: boolean;
  activatedAt: string | null;
  deactivatedAt: string | null;
  source: IndustryPackSourceKind | null;
  externalRef: string | null;
  lastSyncedAt: string | null;
}

export interface BuPackActivationsResponse {
  activations: BusinessUnitPackActivationState[];
  effectivePackIds: IndustryPackAnchor[];
  resolutionMode: 'override' | 'inherited' | 'tenant_default';
}

export async function listIndustryPacks(): Promise<{ packs: IndustryPackSummary[] }> {
  return apiFetch('/v1/foundational/industry-packs');
}

export async function getIndustryPackDetail(
  packId: IndustryPackAnchor,
): Promise<IndustryPackDetail> {
  return apiFetch(`/v1/foundational/industry-packs/${encodeURIComponent(packId)}`);
}

export async function activateIndustryPack(
  packId: IndustryPackAnchor,
  idempotencyKey?: string,
): Promise<{ activation: IndustryPackActivationState }> {
  return apiFetch(`/v1/foundational/industry-packs/${encodeURIComponent(packId)}/activate`, {
    method: 'POST',
    body: JSON.stringify({}),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function deactivateIndustryPack(
  packId: IndustryPackAnchor,
  idempotencyKey?: string,
): Promise<{ activation: IndustryPackActivationState }> {
  return apiFetch(`/v1/foundational/industry-packs/${encodeURIComponent(packId)}/deactivate`, {
    method: 'POST',
    body: JSON.stringify({}),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function listBuIndustryPacks(buId: string): Promise<BuPackActivationsResponse> {
  return apiFetch(`/v1/foundational/business-units/${encodeURIComponent(buId)}/industry-packs`);
}

export async function activateBuIndustryPack(
  buId: string,
  packId: IndustryPackAnchor,
  idempotencyKey?: string,
): Promise<{ activation: BusinessUnitPackActivationState }> {
  return apiFetch(
    `/v1/foundational/business-units/${encodeURIComponent(buId)}/industry-packs/${encodeURIComponent(packId)}/activate`,
    {
      method: 'POST',
      body: JSON.stringify({}),
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    },
  );
}

export async function deactivateBuIndustryPack(
  buId: string,
  packId: IndustryPackAnchor,
  idempotencyKey?: string,
): Promise<{ activation: BusinessUnitPackActivationState }> {
  return apiFetch(
    `/v1/foundational/business-units/${encodeURIComponent(buId)}/industry-packs/${encodeURIComponent(packId)}/deactivate`,
    {
      method: 'POST',
      body: JSON.stringify({}),
      idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
    },
  );
}

// ── Discovery / Classifier Vocab (Phase 6) ────────────────────────────

export type ClassifierOverlayPatternKind = 'regex' | 'keyword' | 'column_name';

export interface ClassifierOverlay {
  id: string;
  tenantId: string;
  packId: IndustryPackAnchor;
  patternKind: ClassifierOverlayPatternKind;
  pattern: string;
  label: string;
  confidenceBoost: number;
  regulations: string[];
  notes: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listClassifierVocab(
  packId: IndustryPackAnchor,
): Promise<{ overlays: ClassifierOverlay[] }> {
  return apiFetch(`/v1/discovery/classifier-vocab/${encodeURIComponent(packId)}`);
}

export type ClassifierChecksum = 'none' | 'luhn' | 'verhoeff' | 'gstin_mod36' | 'mod11';

export async function createClassifierVocab(
  packId: IndustryPackAnchor,
  input: {
    patternKind: ClassifierOverlayPatternKind;
    pattern: string;
    label: string;
    confidenceBoost?: number;
    regulations?: string[];
    notes?: string;
    // Modular Rule Engine: attach a checksum validator to a custom rule.
    checksum?: ClassifierChecksum;
    enabled?: boolean;
  },
  idempotencyKey?: string,
): Promise<{ overlay: ClassifierOverlay }> {
  return apiFetch(`/v1/discovery/classifier-vocab/${encodeURIComponent(packId)}`, {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

export async function deleteClassifierVocab(
  packId: IndustryPackAnchor,
  overlayId: string,
): Promise<{ overlay: ClassifierOverlay }> {
  return apiFetch(
    `/v1/discovery/classifier-vocab/${encodeURIComponent(packId)}/${encodeURIComponent(overlayId)}`,
    {
      method: 'DELETE',
      body: JSON.stringify({}),
    },
  );
}

// ── Discovery / India Moats (Phase 6) ─────────────────────────────────

export interface AadhaarVaultConfig {
  enabled: boolean;
  protectedFields?: string[];
  tokenizationProvider?: string;
  vaultKmsKeyRef?: string;
}

export interface AccountAggregatorConfig {
  enabled: boolean;
  fipEndpointUrl?: string;
  fiuEndpointUrl?: string;
  callbackUrl?: string;
  kmsKeyRef?: string;
  consentMaxDays?: number;
}

export interface DigilockerBreakGlassConfig {
  enabled: boolean;
  breakGlassApprovers?: string[];
  sessionMaxMinutes?: number;
  allowedReasonCodes?: string[];
}

export interface IndiaMoatsConfig {
  tenantId: string;
  aadhaarVault: AadhaarVaultConfig;
  accountAggregator: AccountAggregatorConfig;
  digilockerBreakGlass: DigilockerBreakGlassConfig;
  updatedAt: string;
}

export type IndiaMoatName = 'aadhaar-vault' | 'account-aggregator' | 'digilocker';

export async function getIndiaMoatsStatus(): Promise<IndiaMoatsConfig> {
  return apiFetch('/v1/discovery/india-moats/status');
}

export async function configureIndiaMoat(
  moat: IndiaMoatName,
  config: AadhaarVaultConfig | AccountAggregatorConfig | DigilockerBreakGlassConfig,
  idempotencyKey?: string,
): Promise<IndiaMoatsConfig> {
  return apiFetch(`/v1/discovery/india-moats/${moat}/configure`, {
    method: 'POST',
    body: JSON.stringify(config),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

// ── Platform outbound IPs (G6.4 - doc 22 §6.6.5) ─────────────────────

export interface OutboundEgressEntry {
  id: string;
  cidr: string;
  label: string;
  region: 'in-south-1' | 'ap-south-1' | 'us-east-1' | 'eu-west-1' | 'global';
  provider: 'self_hosted' | 'aws' | 'gcp' | 'azure' | 'cloudflare';
  addedAt: string;
  removingAt?: string;
}

export interface OutboundIpsResponse {
  revision: string;
  generatedAt: string;
  entries: OutboundEgressEntry[];
  notes: string;
}

export async function getOutboundIps(): Promise<OutboundIpsResponse> {
  return apiFetch('/v1/foundational/integrations/outbound-ips');
}

// ── Compliance evidence packs (G3 - doc 22 §4) ────────────────────────

export type EvidencePackFramework = 'DPDPA' | 'SEBI_CSCRF' | 'GDPR' | 'CCPA';
export type EvidencePackStatus = 'pending' | 'building' | 'ready' | 'failed';

export interface EvidencePack {
  id: string;
  framework: EvidencePackFramework;
  periodStart: string;
  periodEnd: string;
  status: EvidencePackStatus;
  generatedAt: string;
  generatedBy: string;
  fileCount: number | null;
  totalBytes: number | null;
  storageUrl: string | null;
  manifestHash: string | null;
  manifestSignature: string | null;
  expiresAt: string | null;
  errorMessage: string | null;
}

export async function listEvidencePacks(params: {
  framework?: EvidencePackFramework;
  status?: EvidencePackStatus;
  limit?: number;
} = {}): Promise<{ packs: EvidencePack[]; count: number }> {
  const q = new URLSearchParams();
  if (params.framework) q.set('framework', params.framework);
  if (params.status) q.set('status', params.status);
  if (params.limit) q.set('limit', String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiFetch(`/v1/compliance/evidence-packs${suffix}`);
}

export async function getEvidencePack(id: string): Promise<{ pack: EvidencePack }> {
  return apiFetch(`/v1/compliance/evidence-packs/${encodeURIComponent(id)}`);
}

export async function buildEvidencePack(input: {
  framework: EvidencePackFramework;
  periodStart: string;
  periodEnd: string;
}, idempotencyKey?: string): Promise<{ pack: EvidencePack }> {
  return apiFetch('/v1/compliance/evidence-packs/build', {
    method: 'POST',
    body: JSON.stringify(input),
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });
}

/**
 * OBS-13 (2026-05-26) - pull the ZIP bytes for a ready pack and
 * trigger a browser download. Tier A2 backend at
 * apps/api-controller/src/modules/compliance/evidence-packs/handlers.ts:
 * downloadEvidencePack returns 200 application/zip with
 * Content-Disposition attachment when status=ready, 404 if missing,
 * 409 if not ready, 410 if expired. apiFetch can't be used directly
 * because the response is binary, not JSON, so this helper mirrors
 * apiFetch's bearer-token plumbing and converts the body to a Blob
 * that the browser saves via a synthetic <a download> click.
 */
export async function downloadEvidencePack(id: string): Promise<void> {
  const token = getAuthToken();
  const url = `${API_BASE}/v1/compliance/evidence-packs/${encodeURIComponent(id)}/download`;
  const headers: Record<string, string> = { accept: 'application/zip' };
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail =
        (typeof body?.title === 'string' ? body.title : '') +
        (typeof body?.detail === 'string' ? ` - ${body.detail}` : '');
    } catch {
      detail = `${res.status} ${res.statusText}`;
    }
    throw new ApiError(res.status, detail || `Download failed (${res.status})`, null);
  }
  const blob = await res.blob();
  // Prefer the server-suggested filename from Content-Disposition; fall
  // back to a deterministic name if the header isn't readable.
  const cd = res.headers.get('content-disposition') ?? '';
  const filenameMatch = cd.match(/filename="?([^"]+)"?/i);
  const filename = filenameMatch?.[1] ?? `evidence-pack-${id}.zip`;
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Firefox finishes the download write before the URL
  // is invalidated (Chrome / Safari handle synchronous revoke fine).
  setTimeout(() => URL.revokeObjectURL(objUrl), 5_000);
}

// ── T1.5.B (2026-05-24) - Compliance Posture (DPO + grievance officer) ──

export interface CompliancePostureResponse {
  coreIntegrated: boolean;
  dpo: {
    appointed: boolean;
    name: string | null;
    email: string | null;
    designationStatus: 'unassigned' | 'partial' | 'complete';
  };
  organization: {
    legalName: string | null;
    industry: string | null;
    primaryRegion: string | null;
    dataResidency: string | null;
    crossBorderTransfer: boolean | null;
  };
  grievances: {
    open: number;
    countsByStatus: Record<string, number>;
    recent: Array<{
      id: string;
      referenceNumber: string;
      status: string;
      createdAt: string;
      slaDeadline: string | null;
      category: string | null;
    }>;
  };
}

export async function getCompliancePosture(): Promise<CompliancePostureResponse> {
  return apiFetch<CompliancePostureResponse>('/v1/foundational/compliance-posture');
}

// ── F (2026-05-25) - Compliance Posture Federation Dashboard ────────

export interface ComplianceFederationResponse {
  coreIntegrated: boolean;
  dataConnections: {
    total: number;
    connected: number;
    error: number;
    pending: number;
  };
  dsars: {
    total: number;
    open: number;
    pastSla: number;
    closed: number;
    rejected: number;
  };
  erasures: {
    total: number;
    active: number;
    blocked: number;
    completed: number;
    partial: number;
    certIssued: number;
  };
  breaches: {
    total: number;
    detected: number;
    contained: number;
    authorityNotified: number;
    pastAuthorityDeadline: number;
    involvingMinors: number;
  };
  vendors: {
    total: number;
    signed: number;
    pending: number;
    expired: number;
    expiringSoon: number;
  };
  risks: {
    total: number;
    open: number;
    mitigating: number;
    closed: number;
    highSeverity: number;
    criticalSeverity: number;
  };
  training: {
    total: number;
    assigned: number;
    completed: number;
    overdue: number;
    complianceRate: number;
  };
}

export async function getCompliancePostureFederation(): Promise<ComplianceFederationResponse> {
  return apiFetch<ComplianceFederationResponse>(
    '/v1/foundational/compliance-posture-federation',
  );
}

// ============================================================
// Wave 1 (2026-05-27) - Notifications module client. Wraps the
// previously-dark /v1/notifications/* surface.
// ============================================================

export type NotificationSeverity = 'critical' | 'high' | 'medium' | 'info';
export type NotificationSource =
  | 'discovery'
  | 'activity'
  | 'detection'
  | 'compliance'
  | 'foundational'
  | 'platform'
  | 'system';
export type NotificationType =
  | 'finding.critical'
  | 'finding.high'
  | 'decision.blocked'
  | 'breach.opened'
  | 'breach.clock.warning'
  | 'breach.clock.expired'
  | 'dsar.due_soon'
  | 'dsar.overdue'
  | 'consent.revoked'
  | 'evidence.ready'
  | 'connector.failing'
  | 'connector.unhealthy'
  | 'scan.completed'
  | 'rule.proposed'
  | 'playbook.executed'
  | 'system.maintenance'
  | 'system.update'
  | 'bridge.invitation';

export interface NotificationAction {
  key: string;
  label: string;
  icon?: string;
  confirm?: boolean;
}

export interface NotificationItem {
  id: string;
  tenantId: string;
  userId: string | null;
  type: NotificationType;
  severity: NotificationSeverity;
  source: NotificationSource;
  title: string;
  body: string;
  icon: string;
  url: string;
  actions: NotificationAction[];
  unread: boolean;
  unacknowledged: boolean;
  emittedAt: string;
  readAt: string | null;
  acknowledgedAt: string | null;
  metadata: Record<string, unknown>;
}

export interface ListNotificationsResponse {
  notifications: NotificationItem[];
  nextCursor: string | null;
  unreadCount: number;
}

export interface ChannelPrefs {
  in_app: boolean;
  sound: boolean;
  push: boolean;
  email: boolean;
}
export interface NotificationPreferences {
  userId?: string;
  tenantId?: string;
  channels?: Record<string, ChannelPrefs>;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  quietHoursTz?: string;
  pushSubscription?: Record<string, unknown> | null;
  soundEnabled?: boolean;
  pushEnabled?: boolean;
  updatedAt?: string;
}

export async function listNotifications(params?: {
  limit?: number;
  cursor?: string;
  includeRead?: boolean;
  includeDismissed?: boolean;
  type?: NotificationType;
  severity?: NotificationSeverity;
  source?: NotificationSource;
}): Promise<ListNotificationsResponse> {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.cursor) q.set('cursor', params.cursor);
  if (params?.includeRead !== undefined) q.set('includeRead', String(params.includeRead));
  if (params?.includeDismissed !== undefined) q.set('includeDismissed', String(params.includeDismissed));
  if (params?.type) q.set('type', params.type);
  if (params?.severity) q.set('severity', params.severity);
  if (params?.source) q.set('source', params.source);
  const qs = q.toString();
  return apiFetch<ListNotificationsResponse>(
    `/v1/notifications/${qs ? `?${qs}` : ''}`,
  );
}

export async function markNotificationRead(id: string): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(`/v1/notifications/${id}/mark-read`, { method: 'POST' });
}

export async function acknowledgeNotification(id: string): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(`/v1/notifications/${id}/acknowledge`, { method: 'POST' });
}

export async function dismissNotification(id: string): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(`/v1/notifications/${id}/dismiss`, { method: 'POST' });
}

export async function snoozeNotification(id: string, untilIso: string): Promise<NotificationItem> {
  return apiFetch<NotificationItem>(`/v1/notifications/${id}/snooze`, {
    method: 'POST',
    body: JSON.stringify({ until: untilIso }),
  });
}

export async function markAllNotificationsRead(): Promise<{ updated: number }> {
  return apiFetch<{ updated: number }>('/v1/notifications/mark-all-read', { method: 'POST' });
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  return apiFetch<NotificationPreferences>('/v1/notifications/preferences');
}

export async function updateNotificationPreferences(
  prefs: NotificationPreferences,
): Promise<NotificationPreferences> {
  return apiFetch<NotificationPreferences>('/v1/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

export async function getNotificationVapidPublicKey(): Promise<{ publicKey: string }> {
  return apiFetch<{ publicKey: string }>('/v1/notifications/vapid-public-key');
}

export async function emitTestNotification(): Promise<{ id: string }> {
  return apiFetch<{ id: string }>('/v1/notifications/test', { method: 'POST' });
}

// ============================================================
// Wave 1 (2026-05-27) - DPIA + SDF document/scaffold helpers.
// Backend has `/v1/compliance/dpia/scaffold` (inventory pre-fill)
// + `/dpia/:id/document?format=pdf|html` + `/sdf-obligations/document`
// - these have been ready since Tier A4 (2026-05-26) but never wired
// in the Dpia + Sdf pages. SEBI/DPDPA evidence shortcut.
// ============================================================

export interface DpiaScaffold {
  title: string | null;
  purpose: string | null;
  legalBasis: string | null;
  dataCategories: string[];
  affectedSubjectsEstimate: number | null;
  riskRegister: Array<{
    id: string;
    description: string;
    likelihood: 'low' | 'medium' | 'high';
    impact: 'low' | 'medium' | 'high';
    residualRisk: 'low' | 'medium' | 'high';
    mitigation: string;
  }>;
  sourceArtefacts: Array<{ kind: string; ref: string; label?: string }>;
}

export async function getDpiaScaffold(params?: {
  businessUnitId?: string;
  processingActivityId?: string;
}): Promise<DpiaScaffold> {
  const q = new URLSearchParams();
  if (params?.businessUnitId) q.set('businessUnitId', params.businessUnitId);
  if (params?.processingActivityId) q.set('processingActivityId', params.processingActivityId);
  return apiFetch<DpiaScaffold>(
    `/v1/compliance/dpia/scaffold${q.toString() ? `?${q}` : ''}`,
  );
}

/**
 * Triggers a browser download of the DPIA document. format='pdf' is
 * the regulator-grade artefact; 'html' is the human-review draft.
 */
export function downloadDpiaDocument(id: string, format: 'pdf' | 'html' = 'pdf'): void {
  const token = getAuthToken();
  const url = `${API_BASE}/v1/compliance/dpia/${encodeURIComponent(id)}/document?format=${format}${
    token ? `&access_token=${encodeURIComponent(token)}` : ''
  }`;
  // Direct download via temporary anchor - preserves Content-Disposition.
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  a.click();
}

export function downloadSdfDocument(): void {
  const token = getAuthToken();
  const url = `${API_BASE}/v1/compliance/sdf-obligations/document${
    token ? `?access_token=${encodeURIComponent(token)}` : ''
  }`;
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  a.click();
}

// ============================================================
// Wave 2 (2026-05-27) - on-demand UEBA / Sonar / P-Wave analyze.
// These POST endpoints have shipped since Phase 3 Day 7-10 but
// were only callable from a CronJob - no SOC operator had a way
// to trigger an analysis pass over the last N hours.
// ============================================================

export interface AnalyzeUebaInput {
  baselineDays?: number;
  recentHours?: number;
  actorUserId?: string;
}
export interface AnalyzeSonarInput {
  baselineDays?: number;
  recentHours?: number;
  application?: string;
  klThreshold?: number;
  minEventCount?: number;
}
export interface AnalyzeEarlyWarningInput {
  recentHours?: number;
  actorUserId?: string;
  reconBurstColdTableThreshold?: number;
  reconBurstWindowMinutes?: number;
}

export interface AnalyzeResult {
  findings: unknown[];
  meta?: Record<string, unknown>;
}

export async function analyzeUeba(input: AnalyzeUebaInput = {}): Promise<AnalyzeResult> {
  return apiFetch<AnalyzeResult>('/v1/activity/ueba/analyze', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function analyzeSonar(input: AnalyzeSonarInput = {}): Promise<AnalyzeResult> {
  return apiFetch<AnalyzeResult>('/v1/activity/sonar/analyze', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function analyzeEarlyWarning(
  input: AnalyzeEarlyWarningInput = {},
): Promise<AnalyzeResult> {
  return apiFetch<AnalyzeResult>('/v1/activity/early-warning/analyze', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============================================================
// Wave 3 (2026-05-27) - Industry-overview + BU-compliance status.
// These two endpoints surface the three-axis taxonomy (Industry
// Profile × Classification Family × Compliance Bundle) - the
// platform's differentiator narrative. They've been DB-backed
// since the foundational module shipped but no page consumed
// them: the unified rollup story was invisible to evaluators.
// ============================================================

export interface SeverityCounts {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface IndustryOverview {
  filter: {
    industryProfile?: string;
    classificationFamily?: string;
    complianceBundle?: string;
  };
  findings: SeverityCounts;
  decisions: { total: number; byDecisionType: Record<string, number> };
  snapshotsActive: number;
  generatedAt: string;
}

export interface BuComplianceStatus {
  businessUnitId: string;
  activeProfileIds: string[];
  activeFamilyIds: string[];
  activeBundleIds: string[];
  activeOverlayIds: string[];
  taxonomyVersion: string;
  findings: SeverityCounts;
  decisions: { total: number; byDecisionType: Record<string, number> };
  latestSnapshotAt: string | null;
  generatedAt: string;
}

export async function getIndustryOverview(filter?: {
  industryProfile?: string;
  classificationFamily?: string;
  complianceBundle?: string;
}): Promise<IndustryOverview> {
  const q = new URLSearchParams();
  if (filter?.industryProfile) q.set('industryProfile', filter.industryProfile);
  if (filter?.classificationFamily) q.set('classificationFamily', filter.classificationFamily);
  if (filter?.complianceBundle) q.set('complianceBundle', filter.complianceBundle);
  return apiFetch<IndustryOverview>(
    `/v1/foundational/industry-overview${q.toString() ? `?${q}` : ''}`,
  );
}

export async function getBuComplianceStatus(businessUnitId: string): Promise<BuComplianceStatus> {
  return apiFetch<BuComplianceStatus>(
    `/v1/foundational/bu-compliance-status?businessUnitId=${encodeURIComponent(businessUnitId)}`,
  );
}

// ============================================================
// Wave 4 (2026-05-27) - Frontier operator endpoints that finish
// the Federated / SMPC / PQ workflows. Existing pages already
// wire the "open"/"generate"/"recruit" endpoints but the
// follow-up "submit/aggregate/sign/verify" endpoints had no UI.
// ============================================================

export interface SubmitFederatedUpdateInput {
  roundId: string;
  delta: number[];
  sampleCount: number;
  differentialPrivacyEpsilon?: number;
  differentialPrivacyDelta?: number;
}
export interface SubmitFederatedUpdateResult {
  participantId: string;
  encryptedUpdateRef: string;
  accepted: boolean;
}
export async function submitFederatedUpdate(
  input: SubmitFederatedUpdateInput,
): Promise<SubmitFederatedUpdateResult> {
  return apiFetch<SubmitFederatedUpdateResult>('/v1/frontier/federated/updates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface AggregateFederatedRoundInput {
  roundId: string;
}
export interface AggregateFederatedRoundResult {
  roundId: string;
  status: string;
  participantCount: number;
  aggregatedUpdateRef: string | null;
}
export async function aggregateFederatedRound(
  input: AggregateFederatedRoundInput,
): Promise<AggregateFederatedRoundResult> {
  return apiFetch<AggregateFederatedRoundResult>('/v1/frontier/federated/aggregate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface SubmitSmpcShareInput {
  sessionId: string;
  shareIndex: number;
  encryptedShareRef: string;
}
export interface SubmitSmpcShareResult {
  shareId: string;
  sessionId: string;
  shareIndex: number;
}
export async function submitSmpcShare(input: SubmitSmpcShareInput): Promise<SubmitSmpcShareResult> {
  return apiFetch<SubmitSmpcShareResult>('/v1/frontier/smpc/shares', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface PqSignInput {
  keyId: string;
  targetType: 'audit_chain_block' | 'breach_notification' | 'compliance_replay';
  targetId: string;
  payload: string;
}
export interface PqSignResult {
  signatureId: string;
  targetType: string;
  targetId: string;
  payloadHash: string;
  signatureB64: string;
  algorithm: string;
}
export async function pqSign(input: PqSignInput): Promise<PqSignResult> {
  return apiFetch<PqSignResult>('/v1/frontier/pq/sign', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface PqVerifyInput {
  algorithm: string;
  publicKeyB64: string;
  privateKeyKmsRef: string;
  payload: string;
  signatureB64: string;
}
export interface PqVerifyResult {
  valid: boolean;
  payloadHash: string;
}
export async function pqVerify(input: PqVerifyInput): Promise<PqVerifyResult> {
  return apiFetch<PqVerifyResult>('/v1/frontier/pq/verify', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ============================================================
// Wave 5 (2026-05-27) - quick-win surfaces from the B+D bucket.
// ============================================================

export interface ReplayRegulator {
  key: string;
  breachClockHours: number;
  enforcesSection9Equivalent: boolean;
  activeForTenant: boolean;
}
export async function listComplianceReplayRegulators(): Promise<{
  regulators: ReplayRegulator[];
  coreIntegrated: boolean;
  tenantId: string;
}> {
  return apiFetch('/v1/frontier/replay/regulators');
}

export interface RegisterNetworkTapInput {
  tapName: string;
  sourceKind:
    | 'aws_vpc_traffic_mirror'
    | 'azure_vnet_tap'
    | 'gcp_packet_mirror'
    | 'on_prem_span_port'
    | 'erspan';
  sourceRef: string;
  targetSqlProtocols: string;
  bytesPerSecond?: number;
}
export interface RegisterNetworkTapResult {
  tapId: string;
  status: string;
}
export async function registerNetworkTap(
  input: RegisterNetworkTapInput,
): Promise<RegisterNetworkTapResult> {
  return apiFetch<RegisterNetworkTapResult>('/v1/frontier/sensors/network-tap', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function acknowledgeUebaFinding(id: string): Promise<{ finding: unknown }> {
  return apiFetch(`/v1/activity/ueba/findings/${encodeURIComponent(id)}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
export async function acknowledgeSonarFinding(id: string): Promise<{ finding: unknown }> {
  return apiFetch(`/v1/activity/sonar/findings/${encodeURIComponent(id)}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Fix D (2026-05-28) - dismiss or escalate a finding with a regulatory-
 * grade reason string. Reason is captured into the audit-chain payload;
 * the row carries the new status + the transitioning actor.
 *
 * Status values: 'dismissed' (false-positive, no action) or 'escalated'
 * (confirmed-threat, hand-off to higher tier).
 *
 * Reason must be 3..1000 chars per the server-side Zod schema
 * (PatchFindingStatusSchema in apps/api-controller/src/modules/activity/
 * schemas.ts). The UI enforces the min upfront.
 */
export async function patchUebaFindingStatus(
  id: string,
  status: 'dismissed' | 'escalated',
  reason: string,
): Promise<{ finding: unknown }> {
  return apiFetch(`/v1/activity/ueba/findings/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
}
export async function patchSonarFindingStatus(
  id: string,
  status: 'dismissed' | 'escalated',
  reason: string,
): Promise<{ finding: unknown }> {
  return apiFetch(`/v1/activity/sonar/findings/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
}

// ============================================================
// Wave 5 Group-A (2026-05-27) - synchronous rule + enforcement
// testers, on-demand classify. All backends shipped previously.
// ============================================================

// POST /v1/detection/evaluate
// schema: EvaluateInputSchema {finding?, event?, context?} (strict) →
// evaluateAndRecord() : { decision(row), engineResult, redundancy, requestId }
export interface EvaluateRuleInput {
  finding?: Record<string, unknown>;
  event?: Record<string, unknown>;
  context?: Record<string, unknown>;
}
export interface EvaluateRuleResult {
  decision: { id: string; decision: string; requestId: string };
  engineResult: {
    finalDecision: string;
    finalExplanation: string;
    matchedRules: Array<{ ruleName: string }>;
  };
  redundancy: {
    finalDecision: 'allow' | 'mask' | 'alert' | 'step_up' | 'break_glass' | 'quarantine' | 'block';
    explanation: string;
    degraded: boolean;
    votes: Array<{
      engine: string;
      decision: string;
      confidence: number;
      explanation: string;
    }>;
  };
  requestId: string;
}
export async function evaluateDetectionRule(input: EvaluateRuleInput): Promise<EvaluateRuleResult> {
  return apiFetch<EvaluateRuleResult>('/v1/detection/evaluate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// POST /v1/detection/enforcement/run
// schema: RunEnforcementSchema → runEnforcementPipeline() : EnforcementPipelineResult.
// This simulates a SINGLE database query through the full enforcement
// pipeline (geofence, mass-export pre-cog, consent firewall, provenance,
// section-9) and returns the strictest decision across engines.
export interface RunEnforcementInput {
  vendor: DbVendor;
  tablesTouched: Array<{ name: string; holdsMinorData?: boolean }>;
  piiCategoriesTouched?: string[];
  actorRole: string;
  actorPermissions?: string[];
  sourceIp?: string;
  /** ISO 3166-1 alpha-2, e.g. "IN". */
  sourceCountryCode: string;
  statementType: 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE' | 'EXPORT' | 'DDL' | 'OTHER';
  rowsReturned?: number;
  actorBaselineRowsPerQuery?: number;
  guardianConsentToken?: string;
  consentRecordId?: string;
  purposeDeclared?: string;
  principalIdentifierHashes?: string[];
  applicableRegulators?: Array<'DPDPA' | 'GDPR' | 'CCPA' | 'PIPL' | 'POPIA' | 'LGPD'>;
  transferSafeguardInPlace?: 'scc' | 'bcr' | 'explicit_consent' | 'none';
  failOpenOnUnavailable?: boolean;
  piiColumnsBeingWritten?: string[];
  testConsentLedger?: Array<{
    principalIdentifierHash: string;
    purpose: string;
    hasActiveConsent: boolean;
  }>;
  testValidGuardianTokens?: string[];
  testValidConsentRecordIds?: string[];
}
export interface RunEnforcementResult {
  decision: 'allow' | 'mask' | 'step_up' | 'block';
  requestId: string;
  reasons: Array<{ engine: string; decision: string; reason: string }>;
  syntheticError?: {
    code: string;
    errorNumber: number;
    message: string;
    severity: string;
  };
  massExportMultiplier?: number;
}
export async function runDetectionEnforcement(
  input: RunEnforcementInput,
): Promise<RunEnforcementResult> {
  return apiFetch<RunEnforcementResult>('/v1/detection/enforcement/run', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface ClassifySamplesInput {
  columnName: string;
  sampleValues: string[];
  /**
   * Declare that these samples came from OCR of a scanned document. Checksum-
   * backed identifiers then get one OCR-confusion repair attempt, and values
   * that match the format but cannot be confirmed surface as `ocr_unverified`
   * instead of being dropped. MUST stay false for digital text - relaxing the
   * checksum there would resurrect the 12-digit account-number false positives.
   */
  fromOcr?: boolean;
}
export interface ClassifySamplesResult {
  label: string;
  confidence: number;
  rationale: string;
  tier: number;
  /**
   * Set when the winning value was RECONSTRUCTED from OCR damage rather than
   * read cleanly, so the UI can say so instead of implying a confirmed read.
   */
  ocrUnverified?: boolean;
  /** The rescue's own explanation (which substitution, or why unconfirmable). */
  ocrRationale?: string;
  alternateLabels: Array<{ label: string; confidence: number }>;
  /**
   * Checksum prevalence for the winning label, present only for
   * checksum-backed labels (Aadhaar / credit card / GSTIN). Drives the
   * "1 of 6 sampled values passed the Verhoeff checksum" line.
   */
  validation?: { matched: number; validated: number; checksum: string };
}

/**
 * Raw shape the backend /v1/discovery/classify actually returns (the
 * edge-scanner contract): a ranked `votes` array + per-engine health. The
 * on-demand panel had drifted from this - it sent `columnName`/`sampleValues`
 * (the backend's `.strict()` schema wants `samples`/`columnHint`) and expected
 * a flat `{label,confidence,rationale}`, so every submit 400'd with
 * "Request did not match expected schema" before the classifier ran. We now
 * speak the real contract and adapt the response for display.
 */
interface ClassifyRawResponse {
  votes: Array<{
    label: string;
    confidence: number;
    agreement: 'exact' | 'strong' | 'medium' | 'single_engine' | 'disagreement';
    flagged: boolean;
    engineVotes: Array<{ engine: string; confidence: number }>;
    validation?: { matched: number; validated: number; checksum: string };
    /** Present on OCR-rescued votes: the value was reconstructed, not read cleanly. */
    tier?: string;
    /** Human-readable explanation for an OCR-rescued vote. */
    rationale?: string;
  }>;
  engines: Array<{ engine: string; ok: boolean; latencyMs: number; errorMessage?: string }>;
  edm?: { datasetsConsulted: number; matchedValues: number };
}

const AGREEMENT_TEXT: Record<string, string> = {
  strong: 'strong agreement',
  medium: 'moderate agreement',
  single_engine: 'a single engine',
  disagreement: 'partial agreement',
};

function tierFromEngine(engine: string): number {
  // EDM is Tier-0: exact membership in the tenant's own data, above every
  // pattern tier. It carries no digit, so the generic regex would mislabel it
  // Tier-1.
  if (engine === 'edm_exact') return 0;
  const m = /tier(\d)/.exec(engine);
  return m ? Number(m[1]) : 1;
}

export async function classifySamples(input: ClassifySamplesInput): Promise<ClassifySamplesResult> {
  const raw = await apiFetch<ClassifyRawResponse>('/v1/discovery/classify', {
    method: 'POST',
    // Correct field names for the strict backend schema.
    body: JSON.stringify({
      samples: input.sampleValues,
      columnHint: input.columnName,
      ...(input.fromOcr ? { fromOcr: true } : {}),
    }),
  });
  const top = raw.votes[0];
  if (!top) {
    return {
      label: 'no PII detected',
      confidence: 0,
      rationale:
        'No detector matched these values - expected for non-PII data, or when a value fails its checksum (e.g. a 12-digit number that is not a valid Aadhaar).',
      tier: 1,
      alternateLabels: [],
    };
  }
  const enginesUsed = [...new Set(top.engineVotes.map((e) => e.engine))];
  const winningTier = enginesUsed.length ? Math.min(...enginesUsed.map(tierFromEngine)) : 1;
  // An exact match is a different KIND of claim, so it gets its own sentence.
  // Saying it "won with strong agreement across N engines" would describe a
  // vote that never happened - the value was simply in the customer's list.
  const rationale =
    top.agreement === 'exact'
      ? `"${top.label}" is an EXACT match against a dataset you uploaded - no pattern matching involved, so this cannot be a false positive.`
      : `"${top.label}" won with ${AGREEMENT_TEXT[top.agreement] ?? top.agreement} across ${enginesUsed.length} engine(s): ${enginesUsed.join(', ')}.`;
  return {
    label: top.label,
    confidence: top.confidence,
    // An OCR-rescued value was RECONSTRUCTED. Saying it "won with agreement
    // across N engines" would overstate it, so the rescue's own explanation
    // replaces the generic voting sentence.
    rationale: top.tier === 'ocr_unverified' && top.rationale ? top.rationale : rationale,
    tier: winningTier,
    ...(top.tier === 'ocr_unverified'
      ? { ocrUnverified: true, ...(top.rationale ? { ocrRationale: top.rationale } : {}) }
      : {}),
    alternateLabels: raw.votes.slice(1, 4).map((v) => ({ label: v.label, confidence: v.confidence })),
    ...(top.validation ? { validation: top.validation } : {}),
  };
}

// ============================================================
// Wave 5 Group-C (2026-05-27) - effective-config audit resolver.
// Returns the union of every classifier overlay / expected system /
// playbook / toxic combo that applies under the current
// taxonomy snapshot for this BU (or tenant default).
// ============================================================

export interface EffectiveConfig {
  businessUnitId: string | null;
  resolution: string;
  activePackIds: string[];
  classifierOverlays: Array<{
    packId: string;
    label: string;
    regexPatterns: string[];
    keywords: string[];
    confidenceBoost: number;
    regulations: string[];
  }>;
  expectedSystems: Array<{
    packId: string;
    slug: string;
    name: string;
    connectorCategory: string | null;
  }>;
  ropaTemplates: Array<{
    packId: string;
    name: string;
    defaultLegalBasis: string;
    dataCategories: string[];
  }>;
  toxicCombinations: Array<{
    packId: string;
    labels: string[];
    severity: 'critical' | 'high' | 'medium';
    rationale: string;
  }>;
  regulatoryClauses: string[];
  ddrPlaybooks: Array<{
    packId: string;
    playbookId: string;
    name: string;
    regulations: string[];
  }>;
}

export async function getEffectiveConfig(businessUnitId?: string): Promise<EffectiveConfig> {
  const q = businessUnitId ? `?buId=${encodeURIComponent(businessUnitId)}` : '';
  return apiFetch<EffectiveConfig>(`/v1/foundational/effective-config${q}`);
}

// ============================================================
// Wave 7 (2026-05-27) - backend GET routes added for the last
// 5 dark surfaces. Each pairs with a viewer page or section.
// ============================================================

export interface NotifySendLogEntry {
  id: string;
  tenantId: string;
  channel: 'email' | 'sms';
  providerId: string | null;
  providerKind: string;
  idempotencyKey: string | null;
  toAddress: string;
  subject: string | null;
  templateKey: string | null;
  status: string;
  attemptNumber: number;
  providerMessageId: string | null;
  errorMessage: string | null;
  triggeredByKind: string | null;
  triggeredById: string | null;
  createdAt: string;
  updatedAt: string;
}
export async function listNotifySendLog(params?: {
  limit?: number;
  channel?: 'email' | 'sms';
  status?: string;
}): Promise<{ entries: NotifySendLogEntry[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.channel) q.set('channel', params.channel);
  if (params?.status) q.set('status', params.status);
  return apiFetch(`/v1/notify/send-log${q.toString() ? `?${q}` : ''}`);
}

export interface BootstrapLogEntry {
  id: string;
  tenantId: string;
  eventId: string;
  eventType: string;
  coreTenantId: string | null;
  status: string;
  payload: Record<string, unknown>;
  stepResults: unknown[];
  errorDetail: string | null;
  receivedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export async function listBootstrapLog(params?: {
  limit?: number;
  status?: string;
}): Promise<{ entries: BootstrapLogEntry[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.status) q.set('status', params.status);
  return apiFetch(`/v1/foundational/bootstrap-log${q.toString() ? `?${q}` : ''}`);
}

export interface InboundEventEntry {
  id: string;
  tenantId: string;
  connectorId: string;
  eventCategory: string;
  eventType: string;
  severity: string | null;
  externalRef: string | null;
  correlationId: string | null;
  rawPayload: Record<string, unknown>;
  normalized: Record<string, unknown> | null;
  processingStatus: string;
  processedAt: string | null;
  actionsTaken: unknown[];
  errorMessage: string | null;
  createdAt: string;
}
export async function listInboundEvents(params?: {
  limit?: number;
  eventType?: string;
}): Promise<{ entries: InboundEventEntry[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.eventType) q.set('eventType', params.eventType);
  return apiFetch(`/v1/foundational/inbound-events${q.toString() ? `?${q}` : ''}`);
}

export interface ScannerCapability {
  id: string;
  label: string;
  enabled: boolean;
}
export interface EdgeScannerCapabilities {
  capabilities: {
    discovery: {
      scanners: ScannerCapability[];
      classifierTiers: ScannerCapability[];
      features: ScannerCapability[];
    };
    activity: { readers: ScannerCapability[] };
    detection: { sidecarRewriters: ScannerCapability[] };
  };
}
export async function getEdgeScannerCapabilities(): Promise<EdgeScannerCapabilities> {
  return apiFetch<EdgeScannerCapabilities>('/v1/edge/scanner-capabilities');
}

export interface Tier4ExemplarSummary {
  label: string;
  sampleCount: number;
  examples: string[];
}
export async function listTier4Exemplars(): Promise<{
  exemplars: Tier4ExemplarSummary[];
  total: number;
}> {
  return apiFetch('/v1/discovery/tier4-exemplars');
}

// ── Admin → Users (T1.1, 2026-05-28) ─────────────────────────────
//
// Frontend for the /v1/admin/users routes shipped in commit c409666
// (backend ready; this finishes the end-to-end loop). Per Discovery
// Independence Principle (CLAUDE.md §14), every standalone customer
// has this surface - they don't need a Core deployment to manage
// their own users.

export type DiscoveryUserStatus = 'invited' | 'active' | 'suspended';

export interface DiscoveryUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  status: DiscoveryUserStatus;
  externalRef: string | null;
  invitedAt: string;
  activatedAt: string | null;
  lastLoginAt: string | null;
  mfaEnrolledAt: string | null;
  invitedByActor: string | null;
}

export async function listAdminUsers(params?: {
  status?: DiscoveryUserStatus;
  role?: string;
  limit?: number;
  offset?: number;
}): Promise<{ users: DiscoveryUser[]; count: number }> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.role) q.set('role', params.role);
  if (params?.limit !== undefined) q.set('limit', String(params.limit));
  if (params?.offset !== undefined) q.set('offset', String(params.offset));
  return apiFetch<{ users: DiscoveryUser[]; count: number }>(
    `/v1/admin/users${q.toString() ? `?${q}` : ''}`,
  );
}

export async function inviteAdminUser(args: {
  email: string;
  fullName: string;
  role: string;
}): Promise<{ user: DiscoveryUser }> {
  return apiFetch<{ user: DiscoveryUser }>('/v1/admin/users', {
    method: 'POST',
    body: JSON.stringify(args),
    idempotencyKey: newIdempotencyKey(),
  });
}

export async function patchAdminUser(
  id: string,
  patch: { role?: string; status?: DiscoveryUserStatus },
): Promise<{ user: DiscoveryUser }> {
  return apiFetch<{ user: DiscoveryUser }>(`/v1/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteAdminUser(id: string): Promise<{ user: DiscoveryUser }> {
  return apiFetch<{ user: DiscoveryUser }>(`/v1/admin/users/${id}`, {
    method: 'DELETE',
  });
}

// ── Admin → Health (T1.2, 2026-05-28) ────────────────────────────
//
// Tenant-facing aggregate health snapshot. Backend = /v1/admin/health
// (modules/admin/health/routes.ts).

export interface TenantHealthAuditChain {
  totalBlocks: number;
  headBlockIndex: number | null;
  lastWriteAt: string | null;
  chainIntact: boolean;
}
export interface TenantHealthScannerSeries {
  lastFindingAt: string | null;
  recent24h: number;
}
export interface TenantHealthScanners {
  ueba: TenantHealthScannerSeries;
  sonar: TenantHealthScannerSeries;
  earlyWarning: TenantHealthScannerSeries;
}
export interface TenantHealthEdgeFleet {
  total: number;
  healthy: number;
  degraded: number;
  stale: number;
  down: number;
  pending: number;
  decommissioned: number;
}
export interface TenantHealthAuditVault {
  tenantId: string;
  totalBlocks: number;
  mirroredBlocks: number;
  unmirroredBlocks: number;
  oldestUnmirroredAt: string | null;
  newestMirroredAt: string | null;
  retentionYears: number;
  bucket: string | null;
}
export interface TenantHealthErrorRate {
  windowSeconds: number;
  total: number;
  status4xx: number;
  status5xx: number;
}
export interface TenantHealthCompound {
  openCompoundFindings: number;
  lastSignalAt: string | null;
}
export type SubsystemOrError<T> = T | { error: string };
export interface TenantHealthSnapshot {
  tenantId: string;
  generatedAt: string;
  auditChain: SubsystemOrError<TenantHealthAuditChain>;
  scanners: SubsystemOrError<TenantHealthScanners>;
  edgeFleet: SubsystemOrError<TenantHealthEdgeFleet>;
  auditVault: SubsystemOrError<TenantHealthAuditVault>;
  errorRate: SubsystemOrError<TenantHealthErrorRate>;
  compound: SubsystemOrError<TenantHealthCompound>;
  overall: {
    status: 'healthy' | 'degraded' | 'unknown';
    weakSpots: ReadonlyArray<string>;
  };
}

export async function getTenantHealth(): Promise<TenantHealthSnapshot> {
  return apiFetch<TenantHealthSnapshot>('/v1/admin/health');
}

// ── Audit-vault on-demand actions (T1.3, 2026-05-29) ─────────────
//
// Manual triggers for the S3 WORM mirror + tamper-verify runs that
// otherwise fire on a schedule. Surfaced as buttons on the admin
// Health page so a DPO/IT-Admin can force a mirror before an audit
// and prove chain integrity on demand.

export interface AuditVaultMirrorResult {
  tenantId: string;
  blocksScanned: number;
  blocksMirrored: number;
  blocksFailed: number;
  durationMs: number;
  vaultEnabled: boolean;
}

export interface AuditVaultVerifyResult {
  tenantId: string;
  blocksChecked: number;
  blocksVerified: number;
  blocksTampered: number;
  blocksMissing: number;
  details: ReadonlyArray<{
    blockId: string;
    blockIndex: number;
    s3Key: string;
    status: 'verified' | 'tampered' | 'missing';
    reason?: string;
  }>;
}

export async function mirrorAuditVault(maxBlocks?: number): Promise<AuditVaultMirrorResult> {
  return apiFetch<AuditVaultMirrorResult>('/v1/audit/vault/mirror', {
    method: 'POST',
    body: JSON.stringify(maxBlocks !== undefined ? { maxBlocks } : {}),
  });
}

export async function verifyAuditVault(limit?: number): Promise<AuditVaultVerifyResult> {
  return apiFetch<AuditVaultVerifyResult>('/v1/audit/vault/verify', {
    method: 'POST',
    body: JSON.stringify(limit !== undefined ? { limit } : {}),
  });
}

// ── Admin → Business-unit source (CSV import) ────────────────────
// Bulk, non-core path for loading a BU tree. Dry run executes the same
// validation as the real import and only skips the writes.

export interface BuSourceCatalogueEntry {
  kind: string;
  label: string;
  description: string;
  requiresPairing: boolean;
}

export interface BuSourceState {
  source: { kind: string; enabled: boolean; updatedAt?: string | null } | null;
  catalogue: BuSourceCatalogueEntry[];
  effective: string;
  note?: string;
}

export interface BuImportResult {
  dryRun: boolean;
  created: number;
  updated: number;
  rejected: number;
  parentsLinked: number;
  rejections: Array<{ line: number; reason: string }>;
  drift: Array<{ externalRef: string; name: string }>;
}

export async function getBuSource(): Promise<BuSourceState> {
  return apiFetch<BuSourceState>('/v1/admin/bu-source');
}

export async function setBuSource(args: {
  kind: string;
  enabled: boolean;
}): Promise<{ source: { kind: string; enabled: boolean } }> {
  return apiFetch<{ source: { kind: string; enabled: boolean } }>('/v1/admin/bu-source', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export async function importBuCsv(csv: string, dryRun: boolean): Promise<BuImportResult> {
  return apiFetch<BuImportResult>('/v1/admin/bu-source/import-csv', {
    method: 'POST',
    body: JSON.stringify({ csv, dryRun }),
  });
}

// ── Admin → Consent Source (alt-CMP) ─────────────────────────────
// Lets a standalone (no privacy-core) tenant point the consent-aware
// query firewall at their own CMP instead of needing a seeded DB row.

export interface ConsentSourceCatalogueEntry {
  kind: string;
  label: string;
  fields: string[];
  secretField: string | null;
}

export interface ConsentSourceConfig {
  kind: string;
  enabled: boolean;
  config: Record<string, unknown>;
  /** True when a credential is stored. The secret itself is never returned. */
  hasSecret: boolean;
  lastUsedAt?: string | null;
  updatedAt?: string | null;
}

export interface ConsentSourceAdminState {
  config: ConsentSourceConfig | null;
  catalogue: ConsentSourceCatalogueEntry[];
  effective?: string;
}

export interface ConsentSourceTestResult {
  ok: boolean;
  elapsedMs: number;
  reachable: boolean;
  answered?: { hasActiveConsent: boolean };
  note?: string;
  error?: string;
}

export async function getConsentSourceConfig(): Promise<ConsentSourceAdminState> {
  return apiFetch<ConsentSourceAdminState>('/v1/admin/consent-source');
}

export async function upsertConsentSourceConfig(args: {
  kind: string;
  config: Record<string, unknown>;
  enabled: boolean;
}): Promise<{ config: ConsentSourceConfig }> {
  return apiFetch<{ config: ConsentSourceConfig }>('/v1/admin/consent-source', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export async function testConsentSource(purpose = 'marketing'): Promise<ConsentSourceTestResult> {
  return apiFetch<ConsentSourceTestResult>('/v1/admin/consent-source/test', {
    method: 'POST',
    body: JSON.stringify({ purpose }),
  });
}

export async function clearConsentSourceConfig(): Promise<{ cleared: boolean }> {
  return apiFetch<{ cleared: boolean }>('/v1/admin/consent-source', { method: 'DELETE' });
}

// ── Admin → PAN verifier (customer-credentialed Protean OPV, 2026-07-22) ──
// Same admin shape as the consent source: the credential is encrypted at rest
// and never returned; `hasSecret` is the only trace it exists.

export interface PanVerifierAdminState {
  config: ConsentSourceConfig | null;
  catalogue: ConsentSourceCatalogueEntry[];
  effective?: string;
}

export interface PanRegistryVerdict {
  configured: boolean;
  via?: string;
  ok?: boolean;
  /** Protean's published taxonomy: existing_valid / existing_valid_marked /
   *  not_found / deleted / deactivated / fake. */
  status?: string;
  aadhaarSeeding?: string;
  nameMatch?: boolean;
  dobMatch?: boolean;
  elapsedMs?: number;
  error?: string;
  note?: string;
}

export interface VerifyPanResult {
  /** Masked (AB*******F) - the raw PAN never comes back. */
  pan: string;
  format: { valid: boolean; explanation: string };
  registry: PanRegistryVerdict;
}

export async function getPanVerifierConfig(): Promise<PanVerifierAdminState> {
  return apiFetch<PanVerifierAdminState>('/v1/admin/pan-verifier');
}

export async function upsertPanVerifierConfig(args: {
  kind: string;
  config: Record<string, unknown>;
  enabled: boolean;
}): Promise<{ config: ConsentSourceConfig }> {
  return apiFetch<{ config: ConsentSourceConfig }>('/v1/admin/pan-verifier', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export async function testPanVerifier(pan: string): Promise<PanRegistryVerdict> {
  return apiFetch<PanRegistryVerdict>('/v1/admin/pan-verifier/test', {
    method: 'POST',
    body: JSON.stringify({ pan }),
  });
}

export async function clearPanVerifierConfig(): Promise<{ cleared: boolean }> {
  return apiFetch<{ cleared: boolean }>('/v1/admin/pan-verifier', { method: 'DELETE' });
}

export async function verifyPan(pan: string): Promise<VerifyPanResult> {
  return apiFetch<VerifyPanResult>('/v1/discovery/verify/pan', {
    method: 'POST',
    body: JSON.stringify({ pan }),
  });
}

// ── Admin → QR trust anchor (signed-identity-QR verification, 2026-07-22) ──
// The trusted PUBLIC certificate used to verify signed identity QR codes.
// UIDAI Aadhaar Secure QR today; DigiLocker etc. are future provider kinds.
// The cert is public, so it is returned (as a preview) rather than hidden.

export interface QrProviderCatalogueEntry {
  kind: string;
  label: string;
  certBased: boolean;
  help: string;
}

export interface QrTrustAnchor {
  kind: string;
  enabled: boolean;
  hasCert: boolean;
  certPreview: string | null;
  updatedAt?: string | null;
  lastUsedAt?: string | null;
}

export interface QrTrustAnchorState {
  anchors: QrTrustAnchor[];
  catalogue: QrProviderCatalogueEntry[];
}

export interface AadhaarQrVerifyResult {
  parsed: boolean;
  signature: 'valid' | 'invalid' | 'no_cert' | 'unparseable';
  fields?: {
    lastFourDigits?: string;
    referenceId?: string;
    name?: string;
    dob?: string;
    gender?: string;
  };
  error?: string;
}

export async function getQrTrustAnchors(): Promise<QrTrustAnchorState> {
  return apiFetch<QrTrustAnchorState>('/v1/admin/qr-trust-anchor');
}

export async function upsertQrTrustAnchor(args: {
  kind: string;
  certPem: string;
  enabled: boolean;
}): Promise<{ ok: boolean; kind: string; enabled: boolean }> {
  return apiFetch('/v1/admin/qr-trust-anchor', { method: 'PUT', body: JSON.stringify(args) });
}

export async function testQrTrustAnchor(qr: string): Promise<AadhaarQrVerifyResult> {
  return apiFetch<AadhaarQrVerifyResult>('/v1/admin/qr-trust-anchor/test', {
    method: 'POST',
    body: JSON.stringify({ qr }),
  });
}

export async function clearQrTrustAnchor(kind: string): Promise<{ cleared: boolean }> {
  return apiFetch(`/v1/admin/qr-trust-anchor?kind=${encodeURIComponent(kind)}`, { method: 'DELETE' });
}

// ── Admin → AuditSink (T1.4, 2026-05-28) ─────────────────────────
//
// Tenant config for the secondary SIEM/webhook destination that
// audit events ship to alongside the DB chain.

export interface TenantAuditSinkConfig {
  tenantId: string;
  kind: string;
  config: Record<string, unknown>;
  enabled: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface AuditSinkAdminState {
  config: TenantAuditSinkConfig | null;
  registeredKinds: ReadonlyArray<string>;
}
export interface AuditSinkTestResult {
  ok: boolean;
  kind: string;
  elapsedMs?: number;
  error?: string;
  message?: string;
  health: {
    kind: string;
    reachable: boolean;
    lastSuccessAt: string | null;
    lastError?: string;
  };
}

export async function getAuditSinkConfig(): Promise<AuditSinkAdminState> {
  return apiFetch<AuditSinkAdminState>('/v1/admin/audit-sink');
}

export async function upsertAuditSinkConfig(args: {
  kind: string;
  config: Record<string, unknown>;
  enabled: boolean;
}): Promise<{ config: TenantAuditSinkConfig }> {
  return apiFetch<{ config: TenantAuditSinkConfig }>('/v1/admin/audit-sink', {
    method: 'PUT',
    body: JSON.stringify(args),
  });
}

export async function testAuditSink(args?: {
  kind?: string;
  config?: Record<string, unknown>;
}): Promise<AuditSinkTestResult> {
  return apiFetch<AuditSinkTestResult>('/v1/admin/audit-sink/test', {
    method: 'POST',
    body: JSON.stringify(args ?? {}),
  });
}

// ── DAM → Compound findings / kill-chain (2026-05-29) ────────────
//
// Frontend for the compound-severity correlator shipped backend-only in
// commit 8a3ad1f. The cross-engine kill-chain (≥2 of UEBA/Sonar/Early-
// Warning firing on the same actor in a rolling window, severity boosted
// one rung) is the most differentiated DAM capability - this surfaces it.
// Endpoints: GET /v1/activity/compound-findings, GET /:id,
// POST /:id/acknowledge, PATCH /:id/status, POST /correlate.

export type CompoundStatus = 'open' | 'acknowledged' | 'dismissed' | 'escalated';

export interface CompoundContributingFinding {
  engine: string;
  findingId: string;
  severity: string;
  detectedAt: string;
  summary: string;
}

export interface CompoundFinding {
  id: string;
  actorUserId: string;
  enginesFired: string;
  engineCount: number;
  severity: string;
  baseSeverity: string;
  boostApplied: boolean;
  windowSince: string;
  windowUntil: string;
  firstSignalAt: string;
  lastSignalAt: string;
  contributingFindings: CompoundContributingFinding[];
  status: CompoundStatus;
  acknowledgedByUserId: string | null;
  acknowledgedAt: string | null;
  resolutionReason: string | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  detectedAt: string;
}

export interface CompoundCorrelateResult {
  tenantId: string;
  contributingFetched: { ueba: number; sonar: number; earlyWarning: number };
  draftsProduced: number;
  newCompoundFindings: number;
  durationMs: number;
}

export async function listCompoundFindings(params?: {
  status?: CompoundStatus;
  actorUserId?: string;
  since?: string;
  until?: string;
  limit?: number;
}): Promise<{ findings: CompoundFinding[]; count: number }> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.actorUserId) q.set('actorUserId', params.actorUserId);
  if (params?.since) q.set('since', params.since);
  if (params?.until) q.set('until', params.until);
  if (params?.limit !== undefined) q.set('limit', String(params.limit));
  return apiFetch<{ findings: CompoundFinding[]; count: number }>(
    `/v1/activity/compound-findings${q.toString() ? `?${q}` : ''}`,
  );
}

export async function getCompoundFinding(id: string): Promise<{ finding: CompoundFinding }> {
  return apiFetch<{ finding: CompoundFinding }>(`/v1/activity/compound-findings/${id}`);
}

export async function acknowledgeCompoundFinding(
  id: string,
): Promise<{ finding: CompoundFinding }> {
  return apiFetch<{ finding: CompoundFinding }>(
    `/v1/activity/compound-findings/${id}/acknowledge`,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

export async function patchCompoundFindingStatus(
  id: string,
  status: 'dismissed' | 'escalated',
  reason: string,
): Promise<{ finding: CompoundFinding }> {
  return apiFetch<{ finding: CompoundFinding }>(`/v1/activity/compound-findings/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, reason }),
  });
}

export async function runCompoundCorrelator(args?: {
  lookbackHours?: number;
  windowHours?: number;
  boostThreshold?: number;
}): Promise<CompoundCorrelateResult> {
  return apiFetch<CompoundCorrelateResult>('/v1/activity/compound-findings/correlate', {
    method: 'POST',
    body: JSON.stringify(args ?? {}),
  });
}

// ─── Agent entitlement: ceilings + increase requests ────────────────────────
//
// These five wrap the commercial control surface. NOTE the response shape
// differs from the older edge routes: they return `{ ok, data }`, so each
// helper unwraps `.data` and callers get the payload directly, matching how
// every other function in this file behaves.

export interface AgentIncreaseRequest {
  id: string;
  tenantId: string;
  agentClass: 'endpoint' | 'vpcPod' | 'gateway';
  currentLimit: number | null;
  liveAtRequest: number;
  requestedLimit: number;
  justification: string;
  requestedBy: string;
  state: 'requested' | 'approved' | 'rejected' | 'withdrawn';
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

/** Ceilings plus the live verdict per class, and who manages the ceiling. */
export async function getAgentLimits(): Promise<{
  limits: Record<string, unknown>;
  classes: { agentClass: string; verdict: AgentEntitlementVerdict }[];
  approvalSource: string;
}> {
  const r = await apiFetch<{ ok: boolean; data: {
    limits: Record<string, unknown>;
    classes: { agentClass: string; verdict: AgentEntitlementVerdict }[];
    approvalSource: string;
  } }>('/v1/entitlements/agent-limits');
  return r.data;
}

export async function listAgentIncreaseRequests(openOnly = false): Promise<AgentIncreaseRequest[]> {
  const r = await apiFetch<{ ok: boolean; data: AgentIncreaseRequest[] }>(
    `/v1/entitlements/increase-requests${openOnly ? '?open=true' : ''}`,
  );
  return r.data;
}

/**
 * Raise a request. currentLimit and liveAtRequest are deliberately NOT sent -
 * the server reads them itself, so a caller cannot understate their own overage
 * in the record the approver later reads.
 */
export async function createAgentIncreaseRequest(args: {
  agentClass: 'endpoint' | 'vpcPod' | 'gateway';
  requestedLimit: number;
  justification: string;
}): Promise<AgentIncreaseRequest & { upstream: { delivered: boolean; kind: string } }> {
  const r = await apiFetch<{ ok: boolean; data: AgentIncreaseRequest & {
    upstream: { delivered: boolean; kind: string };
  } }>('/v1/entitlements/increase-requests', {
    method: 'POST',
    body: JSON.stringify(args),
    idempotencyKey: newIdempotencyKey(),
  });
  return r.data;
}

/**
 * Decide. `applyLimit` is separate from `decision` on purpose: approving records
 * agreement, applying moves the ceiling, and they are different acts requiring
 * different authority.
 */
export async function decideAgentIncreaseRequest(
  id: string,
  args: { decision: 'approved' | 'rejected'; note?: string; applyLimit?: boolean },
): Promise<AgentIncreaseRequest & { ceilingApplied: number | null }> {
  const r = await apiFetch<{ ok: boolean; data: AgentIncreaseRequest & {
    ceilingApplied: number | null;
  } }>(`/v1/entitlements/increase-requests/${id}/decide`, {
    method: 'POST',
    body: JSON.stringify(args),
    idempotencyKey: newIdempotencyKey(),
  });
  return r.data;
}
